import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_BASE } from "../config";
import { useSearchParams } from "react-router-dom";

import {
  RiCalendarLine,
  RiMapPinLine,
  RiImageAddLine,
  RiArrowUpLine,
  RiHistoryLine,
  RiFilePdfLine,
  RiPhoneLine,
  RiHotelLine,
  RiCarLine,
  RiBuildingLine,
  RiPriceTag3Line,
  RiInformationLine,
  RiTimeLine,
  RiAwardLine,
  RiTeamLine,
  RiArrowRightLine,
  RiSaveLine,
  RiEditLine,
  RiDeleteBinLine,
  RiEyeLine,
  RiEyeOffLine,
  RiAddLine,
  RiCloseLine,
  RiUploadLine,
  RiImageLine,
  RiSettings4Line,
  RiListCheck,
  RiUserStarLine,
  RiFileTextLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiMegaphoneLine,
  RiPaletteLine,
  RiLinkLine,
  RiFolderChartLine,
  RiUserVoiceLine,
  RiCalendarEventLine,
  RiSortAsc,
  RiSortDesc,
  RiDownloadLine,
  RiHashtag,
  RiMoneyDollarCircleLine,
  RiStarLine,
  RiStarFill,
  RiLinkedinBoxLine,
  RiTwitterXLine,
  RiBookOpenLine,
  RiGlobalLine,
  RiBriefcaseLine,
  RiGraduationCapLine,
  RiPriceTagLine,
  RiPresentationLine,
  RiErrorWarningLine,
  RiSearchLine,
  RiCheckLine,
  RiRefreshLine,
  RiExternalLinkLine,
  RiArrowDownLine,
  RiFlaskLine,
  RiHeartPulseLine,
  RiVirusLine,
  RiSyringeLine,
  RiBrainLine,
  RiCpuLine,
  RiUserHeartLine,
  RiTestTubeLine,
  RiMedicineBottleLine,
  RiHealthBookLine,
  RiMicroscopeLine,
  RiShieldStarLine,
  RiMentalHealthLine,
  RiFocusLine,
  RiCloudLine,
} from "react-icons/ri";
import toast from "react-hot-toast";

/* ─────────────────────────────────────────────────────────────
   COLOR PALETTE
───────────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F7FF",
  softBlue: "#EBF1FF",
  primary: "#1A56DB",
  primaryDark: "#1240A8",
  primaryLight: "#3B82F6",
  navy: "#0F2557",
  navyDeep: "#091A3E",
  accent: "#60A5FA",
  accentGold: "#F59E0B",
  text: "#0F1F3D",
  textMid: "#374966",
  textMuted: "#6B7FA3",
  border: "#D6E4FF",
  borderLight: "#EBF1FF",
  shadow: "0 4px 24px rgba(26,86,219,0.08)",
  shadowMd: "0 8px 40px rgba(26,86,219,0.12)",
  shadowLg: "0 20px 60px rgba(26,86,219,0.16)",
  gradPrimary: "linear-gradient(135deg, #1A56DB 0%, #3B82F6 100%)",
  gradNavy: "linear-gradient(135deg, #0F2557 0%, #1A56DB 100%)",
  gradSoft: "linear-gradient(135deg, #EBF1FF 0%, #F4F7FF 100%)",
  gradGold: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
  gradAccent: "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
};

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });
const withEventId = (obj, eventId) => ({ ...obj, eventId });
const eventQuery = (eventId) => (eventId ? `?eventId=${eventId}` : "");

/* ══════════════════════════════════════════════════════════════════════
   EVENT SELECTOR
   ══════════════════════════════════════════════════════════════════════ */
const EventSelector = ({ token, selectedEvent, onSelect }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/events`, {
        headers: authHeaders(token),
      });
      setEvents(data.success ? data.events || [] : []);
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const filtered = events.filter(
    (e) =>
      e.title?.toLowerCase().includes(search.toLowerCase()) ||
      e.slug?.toLowerCase().includes(search.toLowerCase()),
  );

  const getSlug = (ev) =>
    ev.slug ||
    ev.title
      ?.toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/--+/g, "-")
      .trim();

  return (
    <div style={{ position: "relative", zIndex: 100 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 20px",
          background: selectedEvent ? T.gradPrimary : T.offWhite,
          border: `1px solid ${selectedEvent ? "transparent" : T.border}`,
          borderRadius: 12,
          cursor: "pointer",
          color: selectedEvent ? T.white : T.text,
          fontWeight: 600,
          fontSize: 14,
          minWidth: 260,
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <RiCalendarEventLine size={16} />
          {selectedEvent ? selectedEvent.title : "Select an Event"}
        </span>
        <RiArrowDownLine
          size={14}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 0.2s",
          }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              minWidth: 340,
              background: T.white,
              border: `1px solid ${T.borderLight}`,
              borderRadius: 16,
              boxShadow: T.shadowLg,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                borderBottom: `1px solid ${T.borderLight}`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <RiSearchLine size={14} style={{ color: T.textMuted }} />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search events…"
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: 13,
                  color: T.text,
                  background: "transparent",
                  width: "100%",
                }}
              />
            </div>

            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              {loading ? (
                <div
                  style={{
                    padding: 24,
                    textAlign: "center",
                    color: T.textMuted,
                    fontSize: 13,
                  }}
                >
                  Loading…
                </div>
              ) : filtered.length === 0 ? (
                <div
                  style={{
                    padding: 24,
                    textAlign: "center",
                    color: T.textMuted,
                    fontSize: 13,
                  }}
                >
                  No events found
                </div>
              ) : (
                filtered.map((ev) => {
                  const slug = getSlug(ev);
                  const isActive = selectedEvent?._id === ev._id;
                  return (
                    <button
                      key={ev._id}
                      onClick={() => {
                        onSelect({ ...ev, slug });
                        setOpen(false);
                        setSearch("");
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        width: "100%",
                        padding: "12px 16px",
                        background: isActive ? T.softBlue : "transparent",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                        borderBottom: `1px solid ${T.borderLight}`,
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          background: isActive ? T.gradPrimary : T.offWhite,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <RiCalendarEventLine
                          size={16}
                          style={{ color: isActive ? T.white : T.primary }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: T.text,
                          }}
                        >
                          {ev.title}
                        </div>
                        <div style={{ fontSize: 11, color: T.textMuted }}>
                          /event/{slug}
                          {ev.date && ` · ${ev.date}`}
                        </div>
                      </div>
                      {isActive && (
                        <RiCheckLine size={14} style={{ color: T.primary }} />
                      )}
                    </button>
                  );
                })
              )}
            </div>

            <div
              style={{
                padding: "10px 16px",
                borderTop: `1px solid ${T.borderLight}`,
                display: "flex",
                gap: 8,
              }}
            >
              <button
                onClick={fetchEvents}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  background: T.offWhite,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  fontSize: 12,
                  color: T.textMid,
                  cursor: "pointer",
                }}
              >
                <RiRefreshLine size={12} /> Refresh
              </button>
              {selectedEvent && (
                <a
                  href={`/event/${selectedEvent.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    background: T.softBlue,
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    fontSize: 12,
                    color: T.primary,
                    textDecoration: "none",
                  }}
                >
                  <RiExternalLinkLine size={12} /> View Live
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {open && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: -1 }}
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  );
};
/* ══════════════════════════════════════════════════════════════════════
   NO EVENT SELECTED placeholder
   ══════════════════════════════════════════════════════════════════════ */
const NoEventSelected = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 400,
      gap: 16,
      color: T.textMuted,
    }}
  >
    <div
      style={{
        width: 80,
        height: 80,
        borderRadius: 24,
        background: T.softBlue,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <RiCalendarEventLine size={36} style={{ color: T.primary }} />
    </div>
    <h3 style={{ fontSize: 20, fontWeight: 700, color: T.navy, margin: 0 }}>
      Select an Event
    </h3>
    <p style={{ fontSize: 14, textAlign: "center", maxWidth: 340, margin: 0 }}>
      Use the <strong>event picker</strong> above to choose which event you want
      to manage. All tab data is isolated per event.
    </p>
  </motion.div>
);

/* ══════════════════════════════════════════════════════════════════════
   GENERIC helpers
   ══════════════════════════════════════════════════════════════════════ */
const Field = ({ label, children, span }) => (
  <div style={{ gridColumn: span ? `span ${span}` : undefined }}>
    <label
      style={{
        display: "block",
        fontSize: 13,
        fontWeight: 600,
        color: T.textMid,
        marginBottom: 6,
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    style={{
      width: "100%",
      background: T.offWhite,
      border: `1px solid ${T.border}`,
      borderRadius: 10,
      padding: "10px 12px",
      color: T.text,
      fontSize: 13,
      outline: "none",
      ...props.style,
    }}
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    style={{
      width: "100%",
      background: T.offWhite,
      border: `1px solid ${T.border}`,
      borderRadius: 10,
      padding: "10px 12px",
      color: T.text,
      fontSize: 13,
      outline: "none",
      resize: "vertical",
      ...props.style,
    }}
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    style={{
      width: "100%",
      background: T.offWhite,
      border: `1px solid ${T.border}`,
      borderRadius: 10,
      padding: "10px 12px",
      color: T.text,
      fontSize: 13,
      outline: "none",
      ...props.style,
    }}
  >
    {children}
  </select>
);

const Btn = ({ children, variant = "primary", ...props }) => {
  const styles = {
    primary: { background: T.gradPrimary, color: T.white, border: "none" },
    secondary: { background: "#E5E7EB", color: T.textMid, border: "none" },
    danger: { background: "#FEE2E2", color: "#EF4444", border: "none" },
    ghost: {
      background: T.softBlue,
      color: T.primary,
      border: `1px solid ${T.border}`,
    },
  };
  return (
    <button
      {...props}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "9px 20px",
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        ...styles[variant],
        ...(props.disabled ? { opacity: 0.6, cursor: "not-allowed" } : {}),
        ...props.style,
      }}
    >
      {children}
    </button>
  );
};

const Card = ({ children, style }) => (
  <div
    style={{
      background: T.white,
      border: `1px solid ${T.borderLight}`,
      borderRadius: 16,
      padding: 24,
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3
    style={{
      fontSize: 16,
      fontWeight: 700,
      color: T.navy,
      marginBottom: 16,
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}
  >
    {children}
  </h3>
);

/* ══════════════════════════════════════════════════════════════════════
   SUBMIT ANNOUNCEMENTS TAB
   ══════════════════════════════════════════════════════════════════════ */
const SubmitAnnouncementsTab = ({ token, eventId }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    message: "",
    isActive: false,
    priority: "medium",
    backgroundColor: "#d4a853",
    textColor: "#ffffff",
    link: "",
    expiresAt: "",
  });
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (eventId) fetchAnnouncements();
  }, [eventId]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/submit-announcements/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (data.success) setAnnouncements(data.submitAnnouncements || []);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      toast.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      message: "",
      isActive: false,
      priority: "medium",
      backgroundColor: "#d4a853",
      textColor: "#ffffff",
      link: "",
      expiresAt: "",
    });
    setEditing(null);
  };

  const saveAnnouncement = async () => {
    if (!form.message.trim()) {
      toast.error("Announcement message is required");
      return;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = withEventId(form, eventId);

      if (editing) {
        await axios.put(
          `${API_BASE}/api/submit-announcements/${editing._id}`,
          payload,
          config,
        );
        toast.success("Announcement updated successfully");
      } else {
        await axios.post(
          `${API_BASE}/api/submit-announcements`,
          payload,
          config,
        );
        toast.success("Announcement created successfully");
      }

      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error("Error saving announcement:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await axios.delete(`${API_BASE}/api/submit-announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleActive = async (announcement) => {
    try {
      await axios.put(
        `${API_BASE}/api/submit-announcements/${announcement._id}`,
        { ...announcement, isActive: !announcement.isActive, eventId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Announcement ${!announcement.isActive ? "activated" : "deactivated"}`,
      );
      fetchAnnouncements();
    } catch {
      toast.error("Failed to update");
    }
  };

  const activeAnnouncements = announcements.filter((a) => a.isActive);
  const inactiveAnnouncements = announcements.filter((a) => !a.isActive);
  const activePreview =
    activeAnnouncements.length > 0 ? activeAnnouncements[0] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <div
          className="px-6 py-4 border-b"
          style={{ borderBottomColor: T.borderLight }}
        >
          <h3
            className="text-lg font-semibold flex items-center gap-2"
            style={{ color: T.navy }}
          >
            <RiEyeLine className="text-green-400" /> Live Banner Preview
          </h3>
          <p className="text-sm mt-1" style={{ color: T.textMuted }}>
            This is how the active announcement will appear on the website
          </p>
        </div>
        <div className="p-6">
          {activePreview ? (
            <div
              style={{
                backgroundColor: activePreview.backgroundColor || "#d4a853",
                color: activePreview.textColor || "#ffffff",
                padding: "14px 24px",
                textAlign: "center",
                fontWeight: 600,
                fontSize: "15.5px",
                letterSpacing: "0.3px",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              {activePreview.link ? (
                <a
                  href={activePreview.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "inherit", textDecoration: "underline" }}
                >
                  {activePreview.message}
                </a>
              ) : (
                activePreview.message
              )}
            </div>
          ) : (
            <div
              className="text-center py-8 rounded-lg"
              style={{ background: T.offWhite }}
            >
              <RiMegaphoneLine
                size={48}
                className="mx-auto mb-2"
                style={{ color: T.textMuted }}
              />
              <p style={{ color: T.textMuted }}>No active announcement</p>
              <p className="text-sm" style={{ color: T.textMuted }}>
                Create an announcement and set it as active to display here
              </p>
            </div>
          )}
        </div>
      </div>

      <div
        className="rounded-xl p-6"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <h3
          className="text-lg font-semibold mb-4 flex items-center gap-2"
          style={{ color: T.navy }}
        >
          <RiMegaphoneLine className="text-yellow-400" />
          {editing ? "Edit Announcement" : "Create New Announcement"}
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label
                className="block mb-2 text-sm"
                style={{ color: T.textMid }}
              >
                Message *
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full rounded-lg p-3"
                style={{
                  background: T.offWhite,
                  border: `1px solid ${T.border}`,
                  color: T.text,
                }}
                rows="3"
                placeholder="Enter announcement message (max 500 characters)"
                maxLength="500"
              />
              <p className="text-xs mt-1" style={{ color: T.textMuted }}>
                {form.message.length}/500 characters
              </p>
            </div>
            <div>
              <label
                className="block mb-2 text-sm"
                style={{ color: T.textMid }}
              >
                Priority
              </label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full rounded-lg p-3"
                style={{
                  background: T.offWhite,
                  border: `1px solid ${T.border}`,
                  color: T.text,
                }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label
                className="block mb-2 text-sm"
                style={{ color: T.textMid }}
              >
                Link (Optional)
              </label>
              <input
                type="url"
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                className="w-full rounded-lg p-3"
                style={{
                  background: T.offWhite,
                  border: `1px solid ${T.border}`,
                  color: T.text,
                }}
                placeholder="https://example.com"
              />
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label
                className="block mb-2 text-sm flex items-center gap-2"
                style={{ color: T.textMid }}
              >
                <RiPaletteLine /> Background Color
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={form.backgroundColor}
                  onChange={(e) =>
                    setForm({ ...form, backgroundColor: e.target.value })
                  }
                  className="w-12 h-12 rounded-lg cursor-pointer"
                  style={{ border: `1px solid ${T.border}` }}
                />
                <input
                  type="text"
                  value={form.backgroundColor}
                  onChange={(e) =>
                    setForm({ ...form, backgroundColor: e.target.value })
                  }
                  className="flex-1 rounded-lg p-3"
                  style={{
                    background: T.offWhite,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                  }}
                  placeholder="#d4a853"
                />
              </div>
            </div>
            <div>
              <label
                className="block mb-2 text-sm flex items-center gap-2"
                style={{ color: T.textMid }}
              >
                <RiPaletteLine /> Text Color
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={form.textColor}
                  onChange={(e) =>
                    setForm({ ...form, textColor: e.target.value })
                  }
                  className="w-12 h-12 rounded-lg cursor-pointer"
                  style={{ border: `1px solid ${T.border}` }}
                />
                <input
                  type="text"
                  value={form.textColor}
                  onChange={(e) =>
                    setForm({ ...form, textColor: e.target.value })
                  }
                  className="flex-1 rounded-lg p-3"
                  style={{
                    background: T.offWhite,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                  }}
                  placeholder="#ffffff"
                />
              </div>
            </div>
            <div>
              <label
                className="block mb-2 text-sm"
                style={{ color: T.textMid }}
              >
                Expires At (Optional)
              </label>
              <input
                type="datetime-local"
                value={form.expiresAt}
                onChange={(e) =>
                  setForm({ ...form, expiresAt: e.target.value })
                }
                className="w-full rounded-lg p-3"
                style={{
                  background: T.offWhite,
                  border: `1px solid ${T.border}`,
                  color: T.text,
                }}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            className="w-5 h-5 accent-green-500"
          />
          <label style={{ color: T.textMid }}>
            Set as active announcement (only one can be active at a time)
          </label>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={saveAnnouncement}
            disabled={loading}
            className="px-6 py-2 rounded-lg flex items-center gap-2"
            style={{ background: T.gradPrimary, color: T.white }}
          >
            <RiSaveLine size={18} />
            {loading
              ? "Saving..."
              : editing
                ? "Update Announcement"
                : "Create Announcement"}
          </button>
          {editing && (
            <button
              onClick={resetForm}
              className="px-6 py-2 rounded-lg"
              style={{ background: "#E5E7EB", color: T.textMid }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div
        className="rounded-xl p-6"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: T.navy }}>
          ✅ Active Announcement
        </h3>
        <div className="space-y-3">
          {activeAnnouncements.map((announcement) => (
            <div
              key={announcement._id}
              className="flex items-center gap-4 p-3 rounded-lg"
              style={{ background: T.offWhite }}
            >
              <div
                className="w-24 h-12 rounded flex items-center justify-center text-xs overflow-hidden"
                style={{
                  backgroundColor: announcement.backgroundColor,
                  color: announcement.textColor,
                }}
              >
                {announcement.message.substring(0, 30)}...
              </div>
              <div className="flex-1">
                <p className="font-semibold" style={{ color: T.text }}>
                  {announcement.message.substring(0, 80)}
                </p>
                <p className="text-sm" style={{ color: T.textMuted }}>
                  Priority: {announcement.priority} |{" "}
                  {announcement.link ? "Has link" : "No link"}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleActive(announcement)}
                  className="p-2 rounded"
                  style={{
                    color: T.accentGold,
                    background: `${T.accentGold}20`,
                  }}
                >
                  <RiEyeOffLine size={16} />
                </button>
                <button
                  onClick={() => {
                    setEditing(announcement);
                    setForm(announcement);
                  }}
                  className="p-2 rounded"
                  style={{ color: T.primary, background: `${T.primary}20` }}
                >
                  <RiEditLine size={16} />
                </button>
                <button
                  onClick={() => deleteAnnouncement(announcement._id)}
                  className="p-2 rounded"
                  style={{ color: "#EF4444", background: "#FEE2E2" }}
                >
                  <RiDeleteBinLine size={16} />
                </button>
              </div>
            </div>
          ))}
          {activeAnnouncements.length === 0 && (
            <p className="text-center py-4" style={{ color: T.textMuted }}>
              No active announcement. Create one above and set it as active.
            </p>
          )}
        </div>
      </div>

      {inactiveAnnouncements.length > 0 && (
        <div
          className="rounded-xl p-6 opacity-60"
          style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: T.navy }}>
            🚫 Inactive Announcements ({inactiveAnnouncements.length})
          </h3>
          <div className="space-y-3">
            {inactiveAnnouncements.map((announcement) => (
              <div
                key={announcement._id}
                className="flex items-center gap-4 p-3 rounded-lg"
                style={{ background: T.offWhite }}
              >
                <div
                  className="w-24 h-12 rounded flex items-center justify-center text-xs opacity-50 overflow-hidden"
                  style={{
                    backgroundColor: announcement.backgroundColor,
                    color: announcement.textColor,
                  }}
                >
                  {announcement.message.substring(0, 30)}...
                </div>
                <div className="flex-1">
                  <p className="font-semibold" style={{ color: T.text }}>
                    {announcement.message.substring(0, 80)}
                  </p>
                  <p className="text-sm" style={{ color: T.textMuted }}>
                    Priority: {announcement.priority}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(announcement)}
                    className="p-2 rounded"
                    style={{ color: "#4CAF50", background: "#E8F5E9" }}
                  >
                    <RiEyeLine size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(announcement);
                      setForm(announcement);
                    }}
                    className="p-2 rounded"
                    style={{ color: T.primary, background: `${T.primary}20` }}
                  >
                    <RiEditLine size={16} />
                  </button>
                  <button
                    onClick={() => deleteAnnouncement(announcement._id)}
                    className="p-2 rounded"
                    style={{ color: "#EF4444", background: "#FEE2E2" }}
                  >
                    <RiDeleteBinLine size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

   
    </motion.div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   SUMMIT HERO SLIDES TAB
   ══════════════════════════════════════════════════════════════════════ */
const SummitHeroSlidesTab = ({ token, eventId }) => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState({});
  const autoplayRef = useRef(null);
  const slideInterval = 5000;

  // ── Fetch slides ──
  const fetchSlides = useCallback(async () => {
    if (!eventId) {
      setSlides([]);
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/summit-hero-slides/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const validSlides = (data.success ? data.data : []).filter(
        (slide) => slide.image && slide.image.trim() !== "",
      );
      setSlides(validSlides);
    } catch (error) {
      console.error("Error fetching slides:", error);
      toast.error("Failed to load slides");
      setSlides([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, token]);

  useEffect(() => {
    setSlides([]);
    setCurrentSlide(0);
    setImagesLoaded({});
    if (eventId) fetchSlides();
  }, [eventId, fetchSlides]);

  const activeSlides = useMemo(() => {
    return slides
      .filter((s) => s.isActive !== false)
      .sort((a, b) => a.order - b.order);
  }, [slides]);

  const inactiveSlides = useMemo(() => {
    return slides.filter((s) => s.isActive === false);
  }, [slides]);

  // ── Autoplay ──
  useEffect(() => {
    if (autoplay && activeSlides.length > 1) {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
      autoplayRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % activeSlides.length);
      }, slideInterval);
    }
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [autoplay, activeSlides.length]);

 const handleImageChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  if (!file.type.startsWith("image/")) {
    toast.error("Please select an image file");
    return;
  }
  // ✅ Size check removed – allow any file size
  setImageFile(file);
  setImagePreview(URL.createObjectURL(file));
};
  const resetForm = () => {
    setImageFile(null);
    setImagePreview(null);
    setEditing(null);
  };

  // ── Save slide with overlay opacity set to 5% ──
  const saveSlide = async () => {
    if (!eventId) {
      toast.error("No event selected. Please select an event first.");
      return;
    }
    if (!imageFile && !editing?.image) {
      toast.error("Image is required");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("eventId", eventId);
      formData.append("order", 0);
      formData.append("isActive", true);
      formData.append("title", "");
      // ✅ Set background overlay opacity to 5% (0.05)
      formData.append("overlayOpacity", "0.05");
      if (imageFile) formData.append("image", imageFile);

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      };

      if (editing) {
        await axios.put(
          `${API_BASE}/api/summit-hero-slides/${editing._id}`,
          formData,
          config,
        );
        toast.success("Slide updated");
      } else {
        await axios.post(
          `${API_BASE}/api/summit-hero-slides`,
          formData,
          config,
        );
        toast.success("Slide added");
      }

      resetForm();
      fetchSlides();
    } catch (error) {
      console.error("Error saving slide:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteSlide = async (id) => {
    if (!window.confirm("Delete this slide?")) return;
    try {
      await axios.delete(`${API_BASE}/api/summit-hero-slides/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Slide deleted");
      fetchSlides();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleActive = async (slide) => {
    try {
      await axios.patch(
        `${API_BASE}/api/summit-hero-slides/${slide._id}`,
        { isActive: !slide.isActive },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Slide ${!slide.isActive ? "activated" : "deactivated"}`);
      fetchSlides();
    } catch {
      toast.error("Failed to update");
    }
  };

  const nextSlide = () => {
    if (activeSlides.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % activeSlides.length);
      resetAutoplay();
    }
  };

  const prevSlide = () => {
    if (activeSlides.length > 0) {
      setCurrentSlide(
        (prev) => (prev - 1 + activeSlides.length) % activeSlides.length,
      );
      resetAutoplay();
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    resetAutoplay();
  };

  const resetAutoplay = () => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    if (autoplay && activeSlides.length > 1) {
      autoplayRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % activeSlides.length);
      }, slideInterval);
    }
  };

  const toggleAutoplay = () => {
    setAutoplay(!autoplay);
    if (!autoplay) resetAutoplay();
    else if (autoplayRef.current) clearInterval(autoplayRef.current);
  };

  if (loading && slides.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto" />
          <p className="mt-4 text-gray-500">Loading slides...</p>
        </div>
      </div>
    );
  }

  if (!eventId) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div
          className="text-center p-8 rounded-xl"
          style={{ background: "#fff", border: "1px solid #e2e8f0" }}
        >
          <div
            className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ background: "rgba(42,157,143,0.1)" }}
          >
            <RiImageLine size={40} style={{ color: "#2A9D8F" }} />
          </div>
          <h3
            className="text-xl font-semibold mb-2"
            style={{ color: "#1C3461" }}
          >
            No Event Selected
          </h3>
          <p className="text-sm" style={{ color: "#6B8090" }}>
            Please select an event from the dropdown above to manage its hero
            slides.
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Event Info Banner */}
      <div
        className="mb-4 p-3 rounded-lg"
        style={{
          background: "rgba(42,157,143,0.1)",
          border: "1px solid rgba(42,157,143,0.2)",
        }}
      >
        <p className="text-sm" style={{ color: "#2A9D8F" }}>
          <strong>📌 Current Event ID:</strong> {eventId} |
          <strong> Slides for this event:</strong> {slides.length} total (
          {activeSlides.length} active, {inactiveSlides.length} inactive)
        </p>
        <p className="text-xs mt-1" style={{ color: "#6B8090" }}>
          💡 Upload images to create a hero carousel for this event.
        </p>
      </div>

      {/* Live Carousel Preview with 5% overlay opacity */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: "#fff", border: "1px solid #E2F2F0" }}
      >
        <div
          className="px-6 py-4 border-b flex justify-between items-center"
          style={{ borderBottomColor: "#E2F2F0" }}
        >
          <h3
            className="text-lg font-semibold flex items-center gap-2"
            style={{ color: "#1A3A4A" }}
          >
            <RiEyeLine className="text-blue-400" /> Live Carousel Preview
          </h3>
          {activeSlides.length > 0 && (
            <span className="text-xs" style={{ color: "#6B8090" }}>
              {currentSlide + 1} / {activeSlides.length} slides
            </span>
          )}
        </div>
        <div className="relative min-h-[550px]">
          {activeSlides.length > 0 ? (
            <>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="relative min-h-[550px] w-full"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-opacity duration-700"
                    style={{
                      backgroundImage: `url(${activeSlides[currentSlide]?.image})`,
                      opacity: imagesLoaded[currentSlide] ? 1 : 0,
                    }}
                  />
                  {!imagesLoaded[currentSlide] && (
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 animate-pulse" />
                  )}
                  {/* ✅ Overlay opacity set to 5% (0.05) */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ backgroundColor: `rgba(0, 0, 0, 0.05)` }}
                  />
                </motion.div>
              </AnimatePresence>
              {activeSlides.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white p-2 rounded-full transition z-20 hover:bg-black/50"
                    style={{
                      background: "rgba(0,0,0,0.4)",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <RiArrowLeftSLine size={28} />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white p-2 rounded-full transition z-20 hover:bg-black/50"
                    style={{
                      background: "rgba(0,0,0,0.4)",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <RiArrowRightSLine size={28} />
                  </button>
                </>
              )}
              {activeSlides.length > 1 && (
                <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-3 z-20">
                  {activeSlides.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => goToSlide(idx)}
                      className={`h-2 rounded-full transition-all duration-300 ${currentSlide === idx ? "w-8 bg-yellow-400" : "w-2 bg-white/50 hover:bg-white/80"}`}
                    />
                  ))}
                </div>
              )}
              {activeSlides.length > 1 && (
                <button
                  onClick={toggleAutoplay}
                  className="absolute bottom-6 right-6 z-20 text-white px-3 py-1 rounded-lg text-xs"
                  style={{
                    background: "rgba(0,0,0,0.5)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  {autoplay ? "⏸ Pause" : "▶ Play"}
                </button>
              )}
            </>
          ) : (
            <div
              className="min-h-[550px] flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
              }}
            >
              <div className="text-center">
                <RiImageLine
                  size={64}
                  className="mx-auto mb-4"
                  style={{ color: "#6B8090" }}
                />
                <p style={{ color: "#6B8090" }}>
                  No slides added yet for this event
                </p>
                <p className="text-sm mt-1" style={{ color: "#6B8090" }}>
                  Upload your first image below
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Slide Form */}
      <div
        className="rounded-xl p-6"
        style={{ background: "#fff", border: `1px solid #E2F2F0` }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: "#1A3A4A" }}>
          {editing ? "✏️ Edit Slide Image" : "📸 Upload New Slide Image"}
        </h3>

        <div>
          <label className="block mb-2 text-sm" style={{ color: "#2D4A5A" }}>
            Background Image *
          </label>
          <div className="flex items-start gap-4 flex-wrap">
            <div
              className="w-48 h-32 border-2 border-dashed rounded-xl flex items-center justify-center overflow-hidden"
              style={{ background: "#F4F9F8", borderColor: "#C8E8E4" }}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
              ) : editing?.image ? (
                <img
                  src={editing.image}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <RiImageLine size={48} style={{ color: "#6B8090" }} />
              )}
            </div>
            <div className="flex-1">
              <input
                id="slide-image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
              <label
                htmlFor="slide-image"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer transition"
                style={{ background: "#2A9D8F", color: "#fff" }}
              >
                <RiUploadLine size={18} />
                {imageFile
                  ? imageFile.name
                  : editing?.image
                    ? "Change Image"
                    : "Choose Image"}
              </label>
              <p className="text-xs mt-2" style={{ color: "#6B8090" }}>
                Recommended: 1920x1080px, JPG/PNG
              </p>
              {imageFile && (
                <button
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                  }}
                  className="text-xs mt-2 hover:underline"
                  style={{ color: "#EF4444" }}
                >
                  Remove selected file
                </button>
              )}
            </div>
          </div>
          {/* ✅ Show overlay opacity info */}
          <p className="text-xs mt-2" style={{ color: "#2A9D8F" }}>
            ℹ️ Background overlay opacity is set to <strong>5%</strong> for
            optimal visibility.
          </p>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={saveSlide}
            disabled={loading}
            className="px-6 py-2 rounded-lg flex items-center gap-2"
            style={{
              background: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
              color: "#fff",
            }}
          >
            <RiSaveLine size={18} />
            {loading ? "Saving..." : editing ? "Update Slide" : "Upload Image"}
          </button>
          {editing && (
            <button
              onClick={resetForm}
              className="px-6 py-2 rounded-lg"
              style={{ background: "#E5E7EB", color: "#374966" }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Active Slides List */}
      <div
        className="rounded-xl p-6"
        style={{ background: "#fff", border: `1px solid #E2F2F0` }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: "#1A3A4A" }}>
          ✅ Active Slides for this Event ({activeSlides.length})
        </h3>
        <div className="space-y-3">
          {activeSlides.map((slide, idx) => (
            <div
              key={slide._id}
              className="flex items-center gap-4 p-3 rounded-lg"
              style={{ background: "#F4F9F8" }}
            >
              <img
                src={slide.image}
                alt={`Slide ${idx + 1}`}
                className="w-20 h-16 object-cover rounded-lg"
              />
              <div className="flex-1">
                <p className="text-sm" style={{ color: "#6B8090" }}>
                  Order: {slide.order || 0}
                </p>
                <p className="text-xs" style={{ color: "#2A9D8F" }}>
                  Event ID: {slide.eventId}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleActive(slide)}
                  className="p-2 rounded"
                  style={{
                    color: "#D4A017",
                    background: "rgba(212,160,23,0.2)",
                  }}
                >
                  <RiEyeOffLine size={16} />
                </button>
                <button
                  onClick={() => {
                    setEditing(slide);
                    setImagePreview(slide.image);
                  }}
                  className="p-2 rounded"
                  style={{
                    color: "#2A9D8F",
                    background: "rgba(42,157,143,0.2)",
                  }}
                >
                  <RiEditLine size={16} />
                </button>
                <button
                  onClick={() => deleteSlide(slide._id)}
                  className="p-2 rounded"
                  style={{ color: "#EF4444", background: "#FEE2E2" }}
                >
                  <RiDeleteBinLine size={16} />
                </button>
              </div>
            </div>
          ))}
          {activeSlides.length === 0 && (
            <p className="text-center py-4" style={{ color: "#6B8090" }}>
              No active slides for this event. Upload images above.
            </p>
          )}
        </div>
      </div>

      {/* Inactive Slides List */}
      {inactiveSlides.length > 0 && (
        <div
          className="rounded-xl p-6 opacity-60"
          style={{ background: "#fff", border: `1px solid #E2F2F0` }}
        >
          <h3
            className="text-lg font-semibold mb-4"
            style={{ color: "#1A3A4A" }}
          >
            🚫 Hidden Slides for this Event ({inactiveSlides.length})
          </h3>
          <div className="space-y-3">
            {inactiveSlides.map((slide, idx) => (
              <div
                key={slide._id}
                className="flex items-center gap-4 p-3 rounded-lg"
                style={{ background: "#F4F9F8" }}
              >
                <img
                  src={slide.image}
                  alt={`Slide ${idx + 1}`}
                  className="w-20 h-16 object-cover rounded-lg opacity-50"
                />
                <div className="flex-1">
                  <p className="text-sm" style={{ color: "#6B8090" }}>
                    Order: {slide.order || 0}
                  </p>
                  <p className="text-xs" style={{ color: "#2A9D8F" }}>
                    Event ID: {slide.eventId}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(slide)}
                    className="p-2 rounded"
                    style={{ color: "#4CAF50", background: "#E8F5E9" }}
                  >
                    <RiEyeLine size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(slide);
                      setImagePreview(slide.image);
                    }}
                    className="p-2 rounded"
                    style={{
                      color: "#2A9D8F",
                      background: "rgba(42,157,143,0.2)",
                    }}
                  >
                    <RiEditLine size={16} />
                  </button>
                  <button
                    onClick={() => deleteSlide(slide._id)}
                    className="p-2 rounded"
                    style={{ color: "#EF4444", background: "#FEE2E2" }}
                  >
                    <RiDeleteBinLine size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    
    </motion.div>
  );
};
/* ══════════════════════════════════════════════════════════════════════
   PROGRAMME OVERVIEW TAB
   ══════════════════════════════════════════════════════════════════════ */
/* ─── PROGRAMME OVERVIEW TAB (WITH NULL CHECKS) ─────────────────────────────────────────────────── */

const ProgrammeOverviewTab = ({ token, eventId, onProgrammeUpdate }) => {
  const [programme, setProgramme] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    edition: "2024",
    isActive: true,
    keyDates: [],
    sessions: [],
  });
  const [newSession, setNewSession] = useState({
    order: 0,
    title: "",
    presentations: [],
  });
  const [newPresentation, setNewPresentation] = useState({
    order: 0,
    speakerName: "",
    speakerAffiliation: "",
    presentationTitle: "",
    description: "",
  });
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    if (eventId) fetchProgramme();
  }, [eventId]);

  const fetchProgramme = async () => {
    if (!eventId) return;
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/programme/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (data.success && data.data) {
        let programmeData = data.data;
        if (Array.isArray(programmeData) && programmeData.length > 0) {
          programmeData = programmeData[0];
        }

        if (programmeData) {
          setProgramme(programmeData);
          setFormData({
            edition: programmeData.edition || "2024",
            isActive:
              programmeData.isActive !== undefined
                ? programmeData.isActive
                : true,
            keyDates: programmeData.keyDates || [],
            sessions: programmeData.sessions || [],
          });
          if (onProgrammeUpdate) onProgrammeUpdate(programmeData);
        } else {
          setProgramme(null);
          setFormData({
            edition: new Date().getFullYear().toString(),
            isActive: true,
            keyDates: [],
            sessions: [],
          });
          if (onProgrammeUpdate) onProgrammeUpdate(null);
        }
      } else {
        setProgramme(null);
        setFormData({
          edition: new Date().getFullYear().toString(),
          isActive: true,
          keyDates: [],
          sessions: [],
        });
        if (onProgrammeUpdate) onProgrammeUpdate(null);
      }
    } catch (error) {
      console.error("Error fetching programme:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to load programme data");
      }
      setProgramme(null);
      setFormData({
        edition: new Date().getFullYear().toString(),
        isActive: true,
        keyDates: [],
        sessions: [],
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Helper: find existing programme by edition ──
  const findProgrammeByEdition = async (edition) => {
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/programme/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (data.success && data.data) {
        return data.data.find((p) => p.edition === edition) || null;
      }
      return null;
    } catch {
      return null;
    }
  };

  // ── Handle "Create New Programme" – always edits existing if any ──
  const handleCreateNew = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/programme/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (data.success && data.data && data.data.length > 0) {
        // There is already a programme – edit it
        const existing = data.data[0];
        setProgramme(existing);
        setFormData({
          edition: existing.edition || new Date().getFullYear().toString(),
          isActive: existing.isActive !== undefined ? existing.isActive : true,
          keyDates: existing.keyDates || [],
          sessions: existing.sessions || [],
        });
        setEditing(true);
        toast.info("Editing existing programme – you can update it.");
      } else {
        // No programme found – create fresh
        setProgramme(null);
        setFormData({
          edition: new Date().getFullYear().toString(),
          isActive: true,
          keyDates: [],
          sessions: [],
        });
        setEditing(true);
      }
    } catch (error) {
      console.error("Error checking existing programme:", error);
      // If error (e.g., 404), assume no programme exists
      setProgramme(null);
      setFormData({
        edition: new Date().getFullYear().toString(),
        isActive: true,
        keyDates: [],
        sessions: [],
      });
      setEditing(true);
    } finally {
      setLoading(false);
    }
  };

  // ── Save: if duplicate edition found, update that existing one ──
  const saveProgramme = async () => {
    setSaving(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { ...formData, eventId };

      // Check if a programme with this edition already exists (for the same event)
      const existing = await findProgrammeByEdition(formData.edition);

      if (existing) {
        // If we are creating new or editing a different programme, switch to the existing one
        if (!programme || programme._id !== existing._id) {
          // Update the existing programme with the current form data
          await axios.put(
            `${API_BASE}/api/programme/${existing._id}`,
            payload,
            config,
          );
          toast.success(
            `Programme updated (edition "${formData.edition}" already existed)`,
          );
          await fetchProgramme();
          setEditing(false);
          setSaving(false);
          return;
        }
      }

      // No duplicate found, proceed with normal create/update
      if (programme?._id) {
        await axios.put(
          `${API_BASE}/api/programme/${programme._id}`,
          payload,
          config,
        );
        toast.success("Programme updated successfully");
      } else {
        await axios.post(`${API_BASE}/api/programme`, payload, config);
        toast.success("Programme created successfully");
      }

      await fetchProgramme();
      setEditing(false);
    } catch (error) {
      console.error("Error saving programme:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // ── All helper functions (unchanged) ──
  const addKeyDate = () => {
    setFormData({
      ...formData,
      keyDates: [
        ...(formData.keyDates || []),
        { title: "", date: "", isEnabled: true },
      ],
    });
  };

  const updateKeyDate = (index, field, value) => {
    const updated = [...(formData.keyDates || [])];
    if (updated[index]) {
      updated[index][field] = value;
      setFormData({ ...formData, keyDates: updated });
    }
  };

  const removeKeyDate = (index) => {
    const updated = (formData.keyDates || []).filter((_, i) => i !== index);
    setFormData({ ...formData, keyDates: updated });
  };

  const addSession = () => {
    if (!newSession.title.trim()) {
      toast.error("Session title is required");
      return;
    }
    setFormData({
      ...formData,
      sessions: [
        ...(formData.sessions || []),
        { ...newSession, presentations: [] },
      ],
    });
    setNewSession({
      order: (formData.sessions || []).length,
      title: "",
      presentations: [],
    });
    toast.success("Session added");
  };

  const updateSession = (index, field, value) => {
    const updated = [...(formData.sessions || [])];
    if (updated[index]) {
      updated[index][field] = value;
      setFormData({ ...formData, sessions: updated });
    }
  };

  const removeSession = (index) => {
    if (window.confirm("Delete this session and all its presentations?")) {
      const updated = (formData.sessions || []).filter((_, i) => i !== index);
      setFormData({ ...formData, sessions: updated });
      toast.success("Session removed");
    }
  };

  const addPresentation = (sessionIndex) => {
    if (
      !newPresentation.presentationTitle.trim() ||
      !newPresentation.speakerName.trim()
    ) {
      toast.error("Presentation title and speaker name are required");
      return;
    }
    const updated = [...(formData.sessions || [])];
    if (updated[sessionIndex]) {
      updated[sessionIndex].presentations = [
        ...(updated[sessionIndex].presentations || []),
        { ...newPresentation },
      ];
      setFormData({ ...formData, sessions: updated });
      setNewPresentation({
        order: 0,
        speakerName: "",
        speakerAffiliation: "",
        presentationTitle: "",
        description: "",
      });
      toast.success("Presentation added");
    }
  };

  const updatePresentation = (sessionIndex, presIndex, field, value) => {
    const updated = [...(formData.sessions || [])];
    if (
      updated[sessionIndex] &&
      updated[sessionIndex].presentations[presIndex]
    ) {
      updated[sessionIndex].presentations[presIndex][field] = value;
      setFormData({ ...formData, sessions: updated });
    }
  };

  const removePresentation = (sessionIndex, presIndex) => {
    if (window.confirm("Remove this presentation?")) {
      const updated = [...(formData.sessions || [])];
      if (updated[sessionIndex]) {
        updated[sessionIndex].presentations = updated[
          sessionIndex
        ].presentations.filter((_, i) => i !== presIndex);
        setFormData({ ...formData, sessions: updated });
        toast.success("Presentation removed");
      }
    }
  };

  const toggleSort = () => {
    const sorted = [...(formData.sessions || [])].sort((a, b) => {
      return sortOrder === "asc"
        ? (a.order || 0) - (b.order || 0)
        : (b.order || 0) - (a.order || 0);
    });
    setFormData({ ...formData, sessions: sorted });
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const sortedSessions = [...(formData.sessions || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );
  const getSortedPresentations = (presentations) => {
    return [...(presentations || [])].sort(
      (a, b) => (a.order || 0) - (b.order || 0),
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        <p style={{ color: "#6B8090" }} className="mt-2">
          Loading programme...
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Programme Display Section */}
      {!editing && (
        <>
          <div
            className="rounded-xl overflow-hidden"
            style={{ background: "#FFFFFF", border: "1px solid #E2F2F0" }}
          >
            <div
              className="px-6 py-4 border-b flex justify-between items-center"
              style={{ borderBottomColor: "#E2F2F0" }}
            >
              <h3
                className="text-lg font-semibold flex items-center gap-2"
                style={{ color: "#1A3A4A" }}
              >
                <RiEyeLine className="text-green-400" /> Live Programme Preview
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={fetchProgramme}
                  className="p-2 rounded-lg"
                  style={{ background: "#F4F9F8", color: "#2A9D8F" }}
                  title="Refresh"
                >
                  <RiRefreshLine size={16} />
                </button>
                <span className="text-xs" style={{ color: "#6B8090" }}>
                  Edition {formData.edition}
                </span>
              </div>
            </div>
            <div className="p-6 max-h-[600px] overflow-y-auto">
              {sortedSessions.length > 0 ? (
                <div className="space-y-8">
                  {(formData.keyDates || []).length > 0 && (
                    <div
                      className="rounded-lg p-4"
                      style={{
                        background: "#E8F5F330",
                        border: "1px solid #2A9D8F20",
                      }}
                    >
                      <h4
                        className="font-semibold mb-3"
                        style={{ color: "#2A9D8F" }}
                      >
                        📅 Important Dates
                      </h4>
                      <div className="space-y-2">
                        {(formData.keyDates || []).map(
                          (date, idx) =>
                            date.isEnabled && (
                              <div
                                key={idx}
                                className="flex justify-between items-center"
                              >
                                <span style={{ color: "#6B8090" }}>
                                  {date.title}:
                                </span>
                                <span
                                  className="font-semibold"
                                  style={{ color: "#1C3461" }}
                                >
                                  {date.date
                                    ? new Date(date.date).toLocaleDateString()
                                    : "Date not set"}
                                </span>
                              </div>
                            ),
                        )}
                      </div>
                    </div>
                  )}
                  {sortedSessions.map((session, sessionIdx) => (
                    <div
                      key={sessionIdx}
                      className="border-b pb-6 last:border-0"
                      style={{ borderBottomColor: "#E2F2F0" }}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h2
                          className="text-2xl font-bold"
                          style={{ color: "#1C3461" }}
                        >
                          {session.order || 0}. {session.title}
                        </h2>
                      </div>
                      <div className="space-y-4 ml-4">
                        {getSortedPresentations(session.presentations).map(
                          (pres, presIdx) => (
                            <div
                              key={presIdx}
                              className="border-l-2 pl-4 py-2"
                              style={{ borderLeftColor: "#3BBCAE" }}
                            >
                              <h3
                                className="text-lg font-semibold"
                                style={{ color: "#3BBCAE" }}
                              >
                                {pres.presentationTitle}
                              </h3>
                              <p className="mt-1" style={{ color: "#2D4A5A" }}>
                                <strong>{pres.speakerName}</strong>
                                {pres.speakerAffiliation &&
                                  `, ${pres.speakerAffiliation}`}
                              </p>
                              {pres.description && (
                                <p
                                  className="text-sm mt-2"
                                  style={{ color: "#6B8090" }}
                                >
                                  {pres.description}
                                </p>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <RiFolderChartLine
                    size={48}
                    className="mx-auto mb-3"
                    style={{ color: "#6B8090" }}
                  />
                  <p style={{ color: "#6B8090" }}>
                    No programme sessions added yet
                  </p>
                  <p className="text-sm" style={{ color: "#6B8090" }}>
                    Click "Create / Edit Programme" to add sessions and
                    presentations
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            {programme && (
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 rounded-lg flex items-center gap-2 transition"
                style={{ background: "#2A9D8F20", color: "#2A9D8F" }}
              >
                <RiEditLine size={16} />
                Edit Programme
              </button>
            )}

            <button
              onClick={handleCreateNew}
              className="px-4 py-2 rounded-lg flex items-center gap-2 transition"
              style={{ background: "#2A9D8F", color: "#FFFFFF" }}
            >
              <RiAddLine size={16} />
              {programme
                ? "Edit Existing Programme"
                : "Create / Edit Programme"}
            </button>
          </div>
        </>
      )}

      {/* Edit/Create Form */}
      {editing && (
        <div className="space-y-6">
          <div
            className="rounded-xl p-6"
            style={{ background: "#FFFFFF", border: "1px solid #E2F2F0" }}
          >
            <h3
              className="text-lg font-semibold mb-4"
              style={{ color: "#1A3A4A" }}
            >
              {programme ? "✏️ Edit Programme" : "✨ Create / Edit Programme"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className="block mb-2 text-sm"
                  style={{ color: "#2D4A5A" }}
                >
                  Edition
                </label>
                <input
                  type="text"
                  value={formData.edition}
                  onChange={(e) =>
                    setFormData({ ...formData, edition: e.target.value })
                  }
                  className="w-full rounded-lg p-3"
                  style={{
                    background: "#F4F9F8",
                    border: "1px solid #C8E8E4",
                    color: "#1C3461",
                  }}
                />
              </div>
              <div className="flex items-center gap-3 mt-6">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="w-5 h-5 accent-green-500"
                />
                <label style={{ color: "#2D4A5A" }}>
                  Active (show on website)
                </label>
              </div>
            </div>
          </div>

          <div
            className="rounded-xl p-6"
            style={{ background: "#FFFFFF", border: "1px solid #E2F2F0" }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3
                className="text-lg font-semibold"
                style={{ color: "#1A3A4A" }}
              >
                📅 Key Dates
              </h3>
              <button
                onClick={addKeyDate}
                className="px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm"
                style={{ background: "#2A9D8F20", color: "#2A9D8F" }}
              >
                <RiAddLine size={14} /> Add Date
              </button>
            </div>
            <div className="space-y-3">
              {(formData.keyDates || []).map((date, idx) => (
                <div
                  key={idx}
                  className="flex gap-3 items-start p-3 rounded-lg"
                  style={{ background: "#F4F9F8" }}
                >
                  <input
                    type="text"
                    value={date.title}
                    onChange={(e) =>
                      updateKeyDate(idx, "title", e.target.value)
                    }
                    placeholder="Title"
                    className="flex-1 rounded-lg p-2 text-sm"
                    style={{
                      background: "#FFFFFF",
                      border: "1px solid #C8E8E4",
                      color: "#1C3461",
                    }}
                  />
                  <input
                    type="date"
                    value={date.date?.split("T")[0] || ""}
                    onChange={(e) => updateKeyDate(idx, "date", e.target.value)}
                    className="w-40 rounded-lg p-2 text-sm"
                    style={{
                      background: "#FFFFFF",
                      border: "1px solid #C8E8E4",
                      color: "#1C3461",
                    }}
                  />
                  <input
                    type="checkbox"
                    checked={date.isEnabled}
                    onChange={(e) =>
                      updateKeyDate(idx, "isEnabled", e.target.checked)
                    }
                    className="w-5 h-5"
                  />
                  <button
                    onClick={() => removeKeyDate(idx)}
                    className="p-2 rounded"
                    style={{ color: "#EF4444", background: "#FEE2E2" }}
                  >
                    <RiDeleteBinLine size={16} />
                  </button>
                </div>
              ))}
              {(formData.keyDates || []).length === 0 && (
                <p className="text-center py-4" style={{ color: "#6B8090" }}>
                  No key dates added
                </p>
              )}
            </div>
          </div>

          <div
            className="rounded-xl p-6"
            style={{ background: "#FFFFFF", border: "1px solid #E2F2F0" }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3
                className="text-lg font-semibold"
                style={{ color: "#1A3A4A" }}
              >
                🎯 Sessions & Presentations
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={toggleSort}
                  className="px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm"
                  style={{ background: "#F4F9F8", color: "#2D4A5A" }}
                >
                  {sortOrder === "asc" ? <RiSortAsc /> : <RiSortDesc />} Sort by
                  Order
                </button>
              </div>
            </div>

            {sortedSessions.map((session, sessionIdx) => (
              <div
                key={sessionIdx}
                className="mb-6 rounded-lg p-4"
                style={{ background: "#F4F9F8" }}
              >
                <div className="flex gap-3 mb-4">
                  <input
                    type="number"
                    value={session.order}
                    onChange={(e) =>
                      updateSession(
                        sessionIdx,
                        "order",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    className="w-20 rounded-lg p-2 text-sm"
                    style={{
                      background: "#FFFFFF",
                      border: "1px solid #C8E8E4",
                      color: "#1C3461",
                    }}
                    placeholder="Order"
                  />
                  <input
                    type="text"
                    value={session.title}
                    onChange={(e) =>
                      updateSession(sessionIdx, "title", e.target.value)
                    }
                    placeholder="Session Title"
                    className="flex-1 rounded-lg p-2"
                    style={{
                      background: "#FFFFFF",
                      border: "1px solid #C8E8E4",
                      color: "#1C3461",
                    }}
                  />
                  <button
                    onClick={() => removeSession(sessionIdx)}
                    className="p-2 rounded"
                    style={{ color: "#EF4444", background: "#FEE2E2" }}
                  >
                    <RiDeleteBinLine size={18} />
                  </button>
                </div>
                <div className="ml-6 space-y-3">
                  <h4
                    className="text-sm font-semibold"
                    style={{ color: "#2D4A5A" }}
                  >
                    Presentations:
                  </h4>
                  {getSortedPresentations(session.presentations).map(
                    (pres, presIdx) => (
                      <div
                        key={presIdx}
                        className="rounded-lg p-3"
                        style={{ background: "#FFFFFF" }}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input
                            type="number"
                            value={pres.order}
                            onChange={(e) =>
                              updatePresentation(
                                sessionIdx,
                                presIdx,
                                "order",
                                parseInt(e.target.value) || 0,
                              )
                            }
                            className="w-20 rounded-lg p-2 text-sm"
                            style={{
                              background: "#F4F9F8",
                              border: "1px solid #C8E8E4",
                              color: "#1C3461",
                            }}
                            placeholder="Order"
                          />
                          <input
                            type="text"
                            value={pres.presentationTitle}
                            onChange={(e) =>
                              updatePresentation(
                                sessionIdx,
                                presIdx,
                                "presentationTitle",
                                e.target.value,
                              )
                            }
                            placeholder="Presentation Title"
                            className="rounded-lg p-2"
                            style={{
                              background: "#F4F9F8",
                              border: "1px solid #C8E8E4",
                              color: "#1C3461",
                            }}
                          />
                          <input
                            type="text"
                            value={pres.speakerName}
                            onChange={(e) =>
                              updatePresentation(
                                sessionIdx,
                                presIdx,
                                "speakerName",
                                e.target.value,
                              )
                            }
                            placeholder="Speaker Name"
                            className="rounded-lg p-2"
                            style={{
                              background: "#F4F9F8",
                              border: "1px solid #C8E8E4",
                              color: "#1C3461",
                            }}
                          />
                          <input
                            type="text"
                            value={pres.speakerAffiliation}
                            onChange={(e) =>
                              updatePresentation(
                                sessionIdx,
                                presIdx,
                                "speakerAffiliation",
                                e.target.value,
                              )
                            }
                            placeholder="Affiliation"
                            className="rounded-lg p-2"
                            style={{
                              background: "#F4F9F8",
                              border: "1px solid #C8E8E4",
                              color: "#1C3461",
                            }}
                          />
                        </div>
                        <textarea
                          value={pres.description || ""}
                          onChange={(e) =>
                            updatePresentation(
                              sessionIdx,
                              presIdx,
                              "description",
                              e.target.value,
                            )
                          }
                          placeholder="Description (optional)"
                          className="mt-2 w-full rounded-lg p-2 text-sm"
                          rows="2"
                          style={{
                            background: "#F4F9F8",
                            border: "1px solid #C8E8E4",
                            color: "#1C3461",
                          }}
                        />
                        <button
                          onClick={() =>
                            removePresentation(sessionIdx, presIdx)
                          }
                          className="mt-2 text-sm hover:underline"
                          style={{ color: "#EF4444" }}
                        >
                          Remove Presentation
                        </button>
                      </div>
                    ),
                  )}
                  <div
                    className="border-t pt-4 mt-4"
                    style={{ borderTopColor: "#C8E8E4" }}
                  >
                    <h5 className="text-sm mb-3" style={{ color: "#6B8090" }}>
                      Add New Presentation:
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="number"
                        value={newPresentation.order}
                        onChange={(e) =>
                          setNewPresentation({
                            ...newPresentation,
                            order: parseInt(e.target.value) || 0,
                          })
                        }
                        placeholder="Order"
                        className="rounded-lg p-2 text-sm"
                        style={{
                          background: "#F4F9F8",
                          border: "1px solid #C8E8E4",
                          color: "#1C3461",
                        }}
                      />
                      <input
                        type="text"
                        value={newPresentation.presentationTitle}
                        onChange={(e) =>
                          setNewPresentation({
                            ...newPresentation,
                            presentationTitle: e.target.value,
                          })
                        }
                        placeholder="Presentation Title *"
                        className="rounded-lg p-2"
                        style={{
                          background: "#F4F9F8",
                          border: "1px solid #C8E8E4",
                          color: "#1C3461",
                        }}
                      />
                      <input
                        type="text"
                        value={newPresentation.speakerName}
                        onChange={(e) =>
                          setNewPresentation({
                            ...newPresentation,
                            speakerName: e.target.value,
                          })
                        }
                        placeholder="Speaker Name *"
                        className="rounded-lg p-2"
                        style={{
                          background: "#F4F9F8",
                          border: "1px solid #C8E8E4",
                          color: "#1C3461",
                        }}
                      />
                      <input
                        type="text"
                        value={newPresentation.speakerAffiliation}
                        onChange={(e) =>
                          setNewPresentation({
                            ...newPresentation,
                            speakerAffiliation: e.target.value,
                          })
                        }
                        placeholder="Affiliation"
                        className="rounded-lg p-2"
                        style={{
                          background: "#F4F9F8",
                          border: "1px solid #C8E8E4",
                          color: "#1C3461",
                        }}
                      />
                    </div>
                    <textarea
                      value={newPresentation.description}
                      onChange={(e) =>
                        setNewPresentation({
                          ...newPresentation,
                          description: e.target.value,
                        })
                      }
                      placeholder="Description (optional)"
                      className="mt-2 w-full rounded-lg p-2 text-sm"
                      rows="2"
                      style={{
                        background: "#F4F9F8",
                        border: "1px solid #C8E8E4",
                        color: "#1C3461",
                      }}
                    />
                    <button
                      onClick={() => addPresentation(sessionIdx)}
                      className="mt-3 px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
                      style={{ background: "#2A9D8F20", color: "#2A9D8F" }}
                    >
                      <RiAddLine size={14} /> Add Presentation
                    </button>
                  </div>
                </div>
              </div>
            ))}

            <div
              className="mt-6 pt-4 border-t"
              style={{ borderTopColor: "#C8E8E4" }}
            >
              <h4 className="text-sm mb-3" style={{ color: "#6B8090" }}>
                Add New Session:
              </h4>
              <div className="flex gap-3">
                <input
                  type="number"
                  value={newSession.order}
                  onChange={(e) =>
                    setNewSession({
                      ...newSession,
                      order: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="Order"
                  className="w-24 rounded-lg p-2 text-sm"
                  style={{
                    background: "#F4F9F8",
                    border: "1px solid #C8E8E4",
                    color: "#1C3461",
                  }}
                />
                <input
                  type="text"
                  value={newSession.title}
                  onChange={(e) =>
                    setNewSession({ ...newSession, title: e.target.value })
                  }
                  placeholder="Session Title *"
                  className="flex-1 rounded-lg p-2"
                  style={{
                    background: "#F4F9F8",
                    border: "1px solid #C8E8E4",
                    color: "#1C3461",
                  }}
                />
                <button
                  onClick={addSession}
                  className="px-4 py-2 rounded-lg flex items-center gap-2"
                  style={{ background: "#2A9D8F20", color: "#2A9D8F" }}
                >
                  <RiAddLine size={16} /> Add Session
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setEditing(false);
                fetchProgramme();
              }}
              className="px-6 py-2 rounded-lg"
              style={{ background: "#E5E7EB", color: "#2D4A5A" }}
            >
              Cancel
            </button>
            <button
              onClick={saveProgramme}
              disabled={saving}
              className="px-6 py-2 rounded-lg flex items-center gap-2"
              style={{ background: T.gradPrimary, color: "#FFFFFF" }}
            >
              <RiSaveLine size={18} />
              {saving ? "Saving..." : "Save All Changes"}
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};
const AccommodationsTab = ({ token, eventId }) => {
  const [accommodations, setAccommodations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    price: 0,
    priceDisplay: "",
    featured: false,
    color: "#06B6D4",
    roomSize: "",
    roomType: "single",
    imageUrl: "",
    order: 0,
    isActive: true,
  });
  const [editing, setEditing] = useState(null);

  const roomTypes = [
    { value: "single", label: "Single Room" },
    { value: "double", label: "Double Room" },
    { value: "twin", label: "Twin Room" },
    { value: "suite", label: "Suite" },
    { value: "family", label: "Family Room" },
  ];

  // Debug: Log when component mounts and when eventId changes
  useEffect(() => {
    console.log("🔍 AccommodationsTab mounted/updated");
    console.log("📌 eventId:", eventId);
    console.log("🔑 Token present:", !!token);
    console.log("📝 EventId type:", typeof eventId);
    console.log("📝 EventId value:", eventId);
  }, [eventId, token]);

  // ✅ Fetch accommodations when eventId changes
  useEffect(() => {
    console.log("📋 useEffect triggered with eventId:", eventId);
    if (eventId) {
      fetchAccommodations();
    } else {
      console.warn("⚠️ No eventId provided to AccommodationsTab");
      setLoading(false);
    }
  }, [eventId]);

  // ✅ Updated fetchAccommodations with eventId
  const fetchAccommodations = async () => {
    console.log("🔄 fetchAccommodations called");
    console.log("📌 Current eventId:", eventId);

    if (!eventId) {
      console.warn("❌ No eventId provided for fetchAccommodations");
      setAccommodations([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // ✅ Use eventId in the API call
      const url = `/api/accommodation/event/${eventId}`;
      console.log(`📡 Fetching from: ${url}`);

      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const { data } = await axios.get(url, config);
      console.log("📦 Response data:", data);

      if (data.success) {
        setAccommodations(data.data || []);
        console.log(`✅ Loaded ${data.data?.length || 0} accommodations`);
      } else {
        console.warn("⚠️ API returned success: false", data.message);
        setAccommodations([]);
      }
    } catch (error) {
      console.error("❌ Error fetching accommodations:", error);
      console.error("❌ Error response:", error.response);

      if (error.response?.status === 404) {
        // 404 means no accommodations found, which is fine
        console.log("ℹ️ No accommodations found for this event (404)");
        setAccommodations([]);
      } else {
        toast.error(
          error.response?.data?.message || "Failed to load accommodations",
        );
        setAccommodations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: "",
      price: 0,
      priceDisplay: "",
      featured: false,
      color: "#06B6D4",
      roomSize: "",
      roomType: "single",
      imageUrl: "",
      order: accommodations.length,
      isActive: true,
    });
    setEditing(null);
  };

  // ✅ Updated save function with eventId
  const save = async () => {
    console.log("💾 save called");
    console.log("📌 eventId:", eventId);
    console.log("📝 form data:", form);

    if (!eventId) {
      toast.error("No event specified. Please select an event first.");
      return;
    }

    if (!form.title || !form.price) {
      toast.error("Room title and price are required");
      return;
    }

    const payload = {
      ...form,
      eventId,
      priceDisplay: form.priceDisplay || `$${form.price}`,
    };

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Check if we are editing an existing one or creating new
      if (editing) {
        // Update existing
        console.log(`📡 Updating accommodation: ${editing._id}`);
        await axios.put(`/api/accommodation/${editing._id}`, payload, config);
        toast.success("Accommodation updated successfully!");
        resetForm();
        fetchAccommodations();
        setLoading(false);
        return;
      }

      // Creating new – check for duplicate title in the current event
      const existing = accommodations.find(
        (acc) =>
          acc.title.toLowerCase() === form.title.toLowerCase() &&
          acc.eventId === eventId,
      );

      if (existing) {
        // Duplicate found → silently update it
        console.log(
          `🔄 Duplicate found – updating existing accommodation: ${existing._id}`,
        );
        await axios.put(`/api/accommodation/${existing._id}`, payload, config);
        toast.success(`Accommodation "${form.title}" updated successfully!`);
        resetForm();
        fetchAccommodations();
        setLoading(false);
        return;
      }

      // No duplicate – create new
      console.log("📡 Creating new accommodation");
      await axios.post("/api/accommodation", payload, config);
      toast.success("Accommodation created successfully!");
      resetForm();
      fetchAccommodations();
    } catch (err) {
      console.error("❌ Error saving accommodation:", err);
      console.error("❌ Error response:", err.response);
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteAccommodation = async (id) => {
    if (!window.confirm("Delete this accommodation?")) return;
    try {
      await axios.delete(`/api/accommodation/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Accommodation deleted");
      fetchAccommodations();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleStatus = async (id, isActive) => {
    try {
      await axios.patch(
        `/api/accommodation/${id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Accommodation ${isActive ? "deactivated" : "activated"}`);
      fetchAccommodations();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const startEdit = (acc) => {
    setEditing(acc);
    setForm({
      title: acc.title,
      price: acc.price,
      priceDisplay: acc.priceDisplay,
      featured: acc.featured || false,
      color: acc.color || "#06B6D4",
      roomSize: acc.roomSize || "",
      roomType: acc.roomType || "single",
      imageUrl: acc.imageUrl || "",
      order: acc.order,
      isActive: acc.isActive,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeAccommodations = accommodations
    .filter((a) => a.isActive)
    .sort((a, b) => a.order - b.order);
  const inactiveAccommodations = accommodations.filter((a) => !a.isActive);

  // ✅ Check if eventId is null, undefined, or empty string
  const hasValidEventId =
    eventId && eventId !== "" && eventId !== "undefined" && eventId !== "null";

  // Loading state
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: T.white,
          padding: "32px",
          borderRadius: "16px",
          boxShadow: T.shadowMd,
          border: `1px solid ${T.borderLight}`,
        }}
      >
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <div
            style={{
              width: 48,
              height: 48,
              border: `3px solid ${T.border}`,
              borderTopColor: T.primary,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p style={{ color: T.textMuted }}>Loading accommodations...</p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </motion.div>
    );
  }

  // No event ID error state
  if (!hasValidEventId) {
    console.warn("⚠️ Rendering No Event Selected state");
    console.log("📌 eventId value:", eventId);
    console.log("📌 eventId type:", typeof eventId);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: T.white,
          padding: "32px",
          borderRadius: "16px",
          boxShadow: T.shadowMd,
          border: `1px solid ${T.borderLight}`,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 16,
            background: T.softTeal,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <RiHotelLine size={28} color={T.primary} />
        </div>
        <h3
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: T.text,
            marginBottom: 8,
          }}
        >
          No Event Selected
        </h3>
        <p style={{ color: T.textMuted, marginBottom: 16 }}>
          Please select an event to manage accommodations.
        </p>
        <button
          onClick={() => {
            console.log("🔄 Manual refresh button clicked");
            console.log("📌 Current eventId:", eventId);
            if (eventId) {
              fetchAccommodations();
            } else {
              toast.error("No event selected. Please select an event first.");
            }
          }}
          style={{
            padding: "8px 20px",
            background: T.primary,
            color: T.white,
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Refresh
        </button>
      </motion.div>
    );
  }

  // Show empty state if no accommodations
  if (accommodations.length === 0 && !loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: T.white,
          padding: "32px",
          borderRadius: "16px",
          boxShadow: T.shadowMd,
          border: `1px solid ${T.borderLight}`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy }}>
            Manage Accommodations
          </h2>
          <span
            style={{
              fontSize: "12px",
              color: T.textMuted,
              background: T.softBlue,
              padding: "4px 12px",
              borderRadius: "999px",
            }}
          >
            0 active · 0 hidden
          </span>
        </div>
        <p
          style={{ color: T.textMuted, marginBottom: "32px", fontSize: "14px" }}
        >
          Manage hotel rooms and accommodation options for conference attendees.
        </p>

        {/* Form Section */}
        <div
          style={{
            background: T.offWhite,
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "32px",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "semibold",
              color: T.navy,
              marginBottom: "16px",
            }}
          >
            {editing ? `Editing: ${editing.title}` : "Add New Accommodation"}
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "16px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  color: T.textMid,
                  marginBottom: "8px",
                  fontSize: "13px",
                }}
              >
                Room Title *
              </label>
              <input
                type="text"
                placeholder="e.g., Standard Room"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  color: T.text,
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  color: T.textMid,
                  marginBottom: "8px",
                  fontSize: "13px",
                }}
              >
                Price (USD) *
              </label>
              <input
                type="number"
                placeholder="e.g., 199"
                value={form.price}
                onChange={(e) =>
                  setForm({
                    ...form,
                    price: parseInt(e.target.value) || 0,
                    priceDisplay: `$${parseInt(e.target.value) || 0}`,
                  })
                }
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  color: T.text,
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  color: T.textMid,
                  marginBottom: "8px",
                  fontSize: "13px",
                }}
              >
                Room Type
              </label>
              <select
                value={form.roomType}
                onChange={(e) => setForm({ ...form, roomType: e.target.value })}
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  color: T.text,
                }}
              >
                {roomTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  color: T.textMid,
                  marginBottom: "8px",
                  fontSize: "13px",
                }}
              >
                Room Size / Occupancy
              </label>
              <input
                type="text"
                placeholder="e.g., Single, Double, Triple"
                value={form.roomSize}
                onChange={(e) => setForm({ ...form, roomSize: e.target.value })}
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  color: T.text,
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  color: T.textMid,
                  marginBottom: "8px",
                  fontSize: "13px",
                }}
              >
                Display Order
              </label>
              <input
                type="number"
                value={form.order}
                onChange={(e) =>
                  setForm({ ...form, order: parseInt(e.target.value) || 0 })
                }
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  color: T.text,
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  color: T.textMid,
                  marginBottom: "8px",
                  fontSize: "13px",
                }}
              >
                Card Color
              </label>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {[
                  "#06B6D4",
                  "#3B82F6",
                  "#F59E0B",
                  "#10B981",
                  "#8B5CF6",
                  "#EC4899",
                ].map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setForm({ ...form, color })}
                    style={{
                      width: 40,
                      height: 40,
                      backgroundColor: color,
                      borderRadius: 8,
                      border:
                        form.color === color
                          ? "2px solid white"
                          : "2px solid transparent",
                      outline:
                        form.color === color ? `2px solid ${T.navy}` : "none",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) =>
                    setForm({ ...form, featured: e.target.checked })
                  }
                  style={{
                    width: "18px",
                    height: "18px",
                    accentColor: T.accentGold,
                  }}
                />
                <span style={{ color: T.textMid }}>⭐ Featured</span>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.checked })
                  }
                  style={{
                    width: "18px",
                    height: "18px",
                    accentColor: T.primary,
                  }}
                />
                <span style={{ color: T.textMid }}>✅ Active</span>
              </label>
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
            <button
              onClick={save}
              disabled={loading}
              style={{
                padding: "10px 24px",
                background: T.gradPrimary,
                color: T.white,
                border: "none",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              <RiSaveLine size={18} />
              {loading
                ? "Saving..."
                : editing
                  ? "Update Accommodation"
                  : "Create Accommodation"}
            </button>
            {editing && (
              <button
                onClick={resetForm}
                style={{
                  padding: "10px 24px",
                  background: "#E5E7EB",
                  color: T.textMid,
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <div
          style={{ textAlign: "center", padding: "40px", color: T.textMuted }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: 16,
              background: T.softTeal,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <RiHotelLine size={28} color={T.primary} />
          </div>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: 600,
              color: T.text,
              marginBottom: "8px",
            }}
          >
            No Accommodations Yet
          </h4>
          <p style={{ fontSize: "14px" }}>
            Add your first accommodation using the form above.
          </p>
        </div>
      </motion.div>
    );
  }

  // Main render with data
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: T.white,
        padding: "32px",
        borderRadius: "16px",
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy }}>
          Manage Accommodations
        </h2>
        <span
          style={{
            fontSize: "12px",
            color: T.textMuted,
            background: T.softBlue,
            padding: "4px 12px",
            borderRadius: "999px",
          }}
        >
          {activeAccommodations.length} active · {inactiveAccommodations.length}{" "}
          hidden
        </span>
      </div>
      <p style={{ color: T.textMuted, marginBottom: "32px", fontSize: "14px" }}>
        Manage hotel rooms and accommodation options for conference attendees.
      </p>

      <div
        style={{
          background: T.offWhite,
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "32px",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "semibold",
            color: T.navy,
            marginBottom: "16px",
          }}
        >
          {editing ? `Editing: ${editing.title}` : "Add New Accommodation"}
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "16px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              Room Title *
            </label>
            <input
              type="text"
              placeholder="e.g., Standard Room"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              Price (USD) *
            </label>
            <input
              type="number"
              placeholder="e.g., 199"
              value={form.price}
              onChange={(e) =>
                setForm({
                  ...form,
                  price: parseInt(e.target.value) || 0,
                  priceDisplay: `$${parseInt(e.target.value) || 0}`,
                })
              }
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              Room Type
            </label>
            <select
              value={form.roomType}
              onChange={(e) => setForm({ ...form, roomType: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            >
              {roomTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              Room Size / Occupancy
            </label>
            <input
              type="text"
              placeholder="e.g., Single, Double, Triple"
              value={form.roomSize}
              onChange={(e) => setForm({ ...form, roomSize: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              Display Order
            </label>
            <input
              type="number"
              value={form.order}
              onChange={(e) =>
                setForm({ ...form, order: parseInt(e.target.value) || 0 })
              }
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              Card Color
            </label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {[
                "#06B6D4",
                "#3B82F6",
                "#F59E0B",
                "#10B981",
                "#8B5CF6",
                "#EC4899",
              ].map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setForm({ ...form, color })}
                  style={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    borderRadius: 8,
                    border:
                      form.color === color
                        ? "2px solid white"
                        : "2px solid transparent",
                    outline:
                      form.color === color ? `2px solid ${T.navy}` : "none",
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "16px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) =>
                  setForm({ ...form, featured: e.target.checked })
                }
                style={{
                  width: "18px",
                  height: "18px",
                  accentColor: T.accentGold,
                }}
              />
              <span style={{ color: T.textMid }}>⭐ Featured</span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) =>
                  setForm({ ...form, isActive: e.target.checked })
                }
                style={{
                  width: "18px",
                  height: "18px",
                  accentColor: T.primary,
                }}
              />
              <span style={{ color: T.textMid }}>✅ Active</span>
            </label>
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
          <button
            onClick={save}
            disabled={loading}
            style={{
              padding: "10px 24px",
              background: T.gradPrimary,
              color: T.white,
              border: "none",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            <RiSaveLine size={18} />
            {loading
              ? "Saving..."
              : editing
                ? "Update Accommodation"
                : "Create Accommodation"}
          </button>
          {editing && (
            <button
              onClick={resetForm}
              style={{
                padding: "10px 24px",
                background: "#E5E7EB",
                color: T.textMid,
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3
        style={{
          fontSize: "18px",
          fontWeight: "semibold",
          color: T.navy,
          marginBottom: "16px",
        }}
      >
        ✅ Active Accommodations ({activeAccommodations.length})
      </h3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
          gap: "16px",
          marginBottom: "40px",
        }}
      >
        {activeAccommodations.map((acc) => (
          <div
            key={acc._id}
            style={{
              background: T.offWhite,
              padding: "16px",
              borderRadius: "12px",
              borderLeft: `4px solid ${acc.featured ? T.accentGold : acc.color}`,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <h4
                    style={{
                      color: T.text,
                      fontWeight: "semibold",
                      fontSize: "16px",
                    }}
                  >
                    {acc.title}
                  </h4>
                  {acc.featured && (
                    <span
                      style={{
                        fontSize: "11px",
                        background: "#FEF3C7",
                        color: T.accentGold,
                        padding: "2px 8px",
                        borderRadius: "999px",
                      }}
                    >
                      ⭐ Featured
                    </span>
                  )}
                  <span style={{ fontSize: "11px", color: T.textMuted }}>
                    Order: {acc.order}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "22px",
                    fontWeight: "bold",
                    color: acc.color,
                    marginBottom: "8px",
                  }}
                >
                  {acc.priceDisplay || `$${acc.price}`}
                </div>
                {acc.roomType && (
                  <div
                    style={{
                      fontSize: "13px",
                      color: T.textMid,
                      marginBottom: "4px",
                    }}
                  >
                    <RiHotelLine
                      size={14}
                      style={{ display: "inline", marginRight: "4px" }}
                    />
                    {roomTypes.find((t) => t.value === acc.roomType)?.label ||
                      acc.roomType}
                  </div>
                )}
                {acc.roomSize && (
                  <div style={{ fontSize: "13px", color: T.textMid }}>
                    <RiBuildingLine
                      size={14}
                      style={{ display: "inline", marginRight: "4px" }}
                    />
                    Occupancy: {acc.roomSize}
                  </div>
                )}
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <button
                  onClick={() => startEdit(acc)}
                  style={{
                    padding: "8px",
                    background: T.softBlue,
                    color: T.primary,
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                  title="Edit"
                >
                  <RiEditLine size={16} />
                </button>
                <button
                  onClick={() => toggleStatus(acc._id, acc.isActive)}
                  style={{
                    padding: "8px",
                    background: acc.isActive ? "#FEF3C7" : "#E8F5E9",
                    color: acc.isActive ? T.accentGold : "#4CAF50",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                  title={acc.isActive ? "Deactivate" : "Activate"}
                >
                  {acc.isActive ? (
                    <RiEyeOffLine size={16} />
                  ) : (
                    <RiEyeLine size={16} />
                  )}
                </button>
                <button
                  onClick={() => deleteAccommodation(acc._id)}
                  style={{
                    padding: "8px",
                    background: "#FEE2E2",
                    color: "#EF4444",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                  title="Delete"
                >
                  <RiDeleteBinLine size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {inactiveAccommodations.length > 0 && (
        <>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "semibold",
              color: T.navy,
              marginBottom: "16px",
            }}
          >
            🚫 Hidden Accommodations ({inactiveAccommodations.length})
          </h3>
          <div style={{ opacity: 0.6 }}>
            {inactiveAccommodations.map((acc) => (
              <div
                key={acc._id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: T.offWhite,
                  padding: "16px",
                  borderRadius: "12px",
                  marginBottom: "12px",
                }}
              >
                <div>
                  <h4 style={{ color: T.text, fontWeight: "semibold" }}>
                    {acc.title}
                  </h4>
                  <p style={{ color: T.textMuted, fontSize: "12px" }}>
                    {acc.priceDisplay || `$${acc.price}`} · Order: {acc.order}
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => toggleStatus(acc._id, acc.isActive)}
                    style={{
                      padding: "6px 12px",
                      background: "#E8F5E9",
                      color: "#4CAF50",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    Activate
                  </button>
                  <button
                    onClick={() => startEdit(acc)}
                    style={{
                      padding: "8px",
                      background: T.softBlue,
                      color: T.primary,
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <RiEditLine size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
};

const SponsorsTab = ({ token, eventId }) => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    order: 0,
    active: true,
    sponsorType: "platinum",
    url: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [editing, setEditing] = useState(null);

  const sponsorTypes = [
    { value: "platinum", label: "Platinum Sponsor", color: "#E5E4E2" },
    { value: "gold", label: "Gold Sponsor", color: "#FFD700" },
    { value: "silver", label: "Silver Sponsor", color: "#C0C0C0" },
    { value: "bronze", label: "Bronze Sponsor", color: "#CD7F32" },
    { value: "partner", label: "Partner", color: "#1A56DB" },
  ];

  // Debug: Log when component mounts
  useEffect(() => {
    console.log("🔍 SponsorsTab mounted");
    console.log("📌 eventId:", eventId);
    console.log("🔑 Token present:", !!token);
  }, [eventId, token]);

  // ✅ Fetch sponsors when eventId changes
  useEffect(() => {
    if (eventId) {
      fetchAll();
    } else {
      console.warn("⚠️ No eventId provided to SponsorsTab");
      setLoading(false);
    }
  }, [eventId]);

  // ✅ Updated fetchAll with eventId
  const fetchAll = async () => {
    if (!eventId) {
      console.warn("❌ No eventId provided for fetchAll");
      return;
    }

    try {
      setLoading(true);
      console.log(`📡 Fetching sponsors for event: ${eventId}`);

      // Try the event-specific endpoint first
      const url = `${API_BASE}/api/sponsors/event/${eventId}/all`;
      console.log(`📡 URL: ${url}`);

      const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
      const { data } = await axios.get(url, config);

      console.log("📦 Response:", data);
      setSponsors(data.success ? data.sponsors : []);
    } catch (error) {
      console.error("❌ Error fetching sponsors:", error);

      // If event endpoint fails, try the fallback
      try {
        console.log("📡 Trying fallback endpoint...");
        const fallbackUrl = `${API_BASE}/api/sponsors/all?eventId=${eventId}`;
        const config = token
          ? { headers: { Authorization: `Bearer ${token}` } }
          : {};
        const { data } = await axios.get(fallbackUrl, config);
        setSponsors(data.success ? data.sponsors : []);
      } catch (fallbackError) {
        console.error("❌ Fallback also failed:", fallbackError);
        toast.error("Failed to load sponsors");
        setSponsors([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setForm({
      name: "",
      order: 0,
      active: true,
      sponsorType: "platinum",
      url: "",
    });
    setLogoFile(null);
    setLogoPreview(null);
    setEditing(null);
  };

  // ✅ Updated save with eventId
  const save = async () => {
    console.log("💾 save called");
    console.log("📌 eventId:", eventId);

    if (!eventId) {
      toast.error("No event specified. Please select an event first.");
      return;
    }

    if (!form.name.trim()) {
      toast.error("Sponsor name is required");
      return;
    }

    const fd = new FormData();
    fd.append("eventId", eventId);
    fd.append("name", form.name.trim());
    fd.append("order", form.order);
    fd.append("active", form.active);
    fd.append("sponsorType", form.sponsorType);
    if (form.url) fd.append("url", form.url);
    if (logoFile) fd.append("logo", logoFile);

    try {
      setLoading(true);
      const cfg = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};

      if (editing) {
        console.log(`📡 Updating sponsor: ${editing._id}`);
        await axios.patch(`${API_BASE}/api/sponsors/${editing._id}`, fd, cfg);
        toast.success("Sponsor updated successfully!");
      } else {
        console.log("📡 Creating new sponsor");
        await axios.post(`${API_BASE}/api/sponsors`, fd, cfg);
        toast.success("Sponsor added successfully!");
      }

      resetForm();
      fetchAll();
    } catch (err) {
      console.error("❌ Error saving sponsor:", err);
      toast.error(err.response?.data?.message || "Failed to save sponsor");
    } finally {
      setLoading(false);
    }
  };

  const removeLogo = async (id) => {
    if (!window.confirm("Remove logo? The sponsor name will still show."))
      return;

    try {
      const cfg = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
      await axios.delete(`${API_BASE}/api/sponsors/${id}/logo`, cfg);
      toast.success("Logo removed successfully!");
      fetchAll();
    } catch (error) {
      console.error("Error removing logo:", error);
      toast.error("Failed to remove logo");
    }
  };

  const toggleActive = async (sponsor) => {
    try {
      const fd = new FormData();
      fd.append("active", String(!sponsor.active));
      const cfg = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};

      await axios.patch(`${API_BASE}/api/sponsors/${sponsor._id}`, fd, cfg);
      toast.success(`Sponsor ${!sponsor.active ? "activated" : "deactivated"}`);
      fetchAll();
    } catch (error) {
      console.error("Error toggling status:", error);
      toast.error("Failed to update status");
    }
  };

  const deleteSponsor = async (id) => {
    if (!window.confirm("Permanently delete this sponsor?")) return;

    try {
      const cfg = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
      await axios.delete(`${API_BASE}/api/sponsors/${id}`, cfg);
      toast.success("Sponsor deleted successfully!");
      fetchAll();
    } catch (error) {
      console.error("Error deleting sponsor:", error);
      toast.error("Failed to delete");
    }
  };

  const startEdit = (sp) => {
    setEditing(sp);
    setForm({
      name: sp.name,
      order: sp.order ?? 0,
      active: sp.active,
      sponsorType: sp.sponsorType || "platinum",
      url: sp.url || "",
    });
    setLogoPreview(sp.logoUrl || null);
    setLogoFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const active = sponsors
    .filter((s) => s.active)
    .sort((a, b) => a.order - b.order);
  const inactive = sponsors.filter((s) => !s.active);

  const getSponsorTypeLabel = (type) => {
    const found = sponsorTypes.find((t) => t.value === type);
    return found ? found.label : type;
  };

  const getSponsorTypeColor = (type) => {
    const found = sponsorTypes.find((t) => t.value === type);
    return found ? found.color : "#6B8090";
  };

  // No event ID error state
  if (!eventId) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: T.white,
          padding: "32px",
          borderRadius: "16px",
          boxShadow: T.shadowMd,
          border: `1px solid ${T.borderLight}`,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 16,
            background: T.softTeal,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <RiImageLine size={28} color={T.primary} />
        </div>
        <h3
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: T.text,
            marginBottom: 8,
          }}
        >
          No Event Selected
        </h3>
        <p style={{ color: T.textMuted }}>
          Please select an event to manage sponsors.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: T.white,
        padding: "32px",
        borderRadius: "16px",
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "8px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy }}>
          Manage Sponsors
        </h2>
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              color: T.textMuted,
              background: T.softBlue,
              padding: "4px 12px",
              borderRadius: "999px",
            }}
          >
            {active.length} active · {inactive.length} hidden
          </span>
          <button
            onClick={fetchAll}
            style={{
              fontSize: "12px",
              color: T.primary,
              background: T.offWhite,
              padding: "4px 12px",
              borderRadius: "999px",
              border: `1px solid ${T.border}`,
              cursor: "pointer",
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>
      <p style={{ color: T.textMuted, marginBottom: "32px", fontSize: "14px" }}>
        Sponsors are shown in the scrolling marquee on the homepage. Upload a
        logo (PNG/SVG recommended) or leave blank to show text only. Changes go
        live instantly.
      </p>

      <div
        style={{
          background: T.offWhite,
          border: `1px solid ${T.border}`,
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "40px",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "semibold",
            color: T.navy,
            marginBottom: "20px",
          }}
        >
          {editing ? `Editing: ${editing.name}` : "Add New Sponsor"}
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "16px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                fontSize: "13px",
                marginBottom: "4px",
              }}
            >
              Sponsor Name *
            </label>
            <input
              type="text"
              placeholder="e.g. GLOBALINK"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                fontSize: "13px",
                marginBottom: "4px",
              }}
            >
              Sponsor Type
            </label>
            <select
              value={form.sponsorType}
              onChange={(e) =>
                setForm({ ...form, sponsorType: e.target.value })
              }
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            >
              {sponsorTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                fontSize: "13px",
                marginBottom: "4px",
              }}
            >
              Website URL
            </label>
            <input
              type="url"
              placeholder="https://example.com"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                fontSize: "13px",
                marginBottom: "4px",
              }}
            >
              Display Order
            </label>
            <input
              type="number"
              value={form.order}
              onChange={(e) =>
                setForm({ ...form, order: parseInt(e.target.value) || 0 })
              }
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
            <p
              style={{ color: T.textMuted, fontSize: "11px", marginTop: "4px" }}
            >
              Lower number = appears first
            </p>
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label
              style={{
                display: "block",
                color: T.textMid,
                fontSize: "13px",
                marginBottom: "4px",
              }}
            >
              Logo Image (PNG, JPG, SVG, WEBP — max 2 MB)
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  width: 120,
                  height: 64,
                  background: T.white,
                  border: `1px dashed ${T.border}`,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  overflow: "hidden",
                }}
              >
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <RiImageLine size={28} style={{ color: T.textMuted }} />
                )}
              </div>

              <div style={{ flex: 1 }}>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={handleLogoChange}
                  style={{ display: "none" }}
                />
                <label
                  htmlFor="logo-upload"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 16px",
                    background: T.softBlue,
                    borderRadius: "8px",
                    fontSize: "13px",
                    color: T.primary,
                    cursor: "pointer",
                  }}
                >
                  <RiUploadLine size={16} />
                  {logoFile ? logoFile.name : "Choose file…"}
                </label>
                {editing && editing.logoUrl && (
                  <button
                    onClick={() => removeLogo(editing._id)}
                    style={{
                      marginLeft: "12px",
                      fontSize: "12px",
                      color: "#EF4444",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Remove existing logo
                  </button>
                )}
                {editing && (
                  <p
                    style={{
                      color: T.textMuted,
                      fontSize: "11px",
                      marginTop: "8px",
                    }}
                  >
                    Leave empty to keep the current logo
                  </p>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <input
              type="checkbox"
              id="sp-active"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              style={{ width: "18px", height: "18px", accentColor: T.primary }}
            />
            <label
              htmlFor="sp-active"
              style={{ color: T.textMid, fontSize: "13px", cursor: "pointer" }}
            >
              Show on website (active)
            </label>
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
          <button
            onClick={save}
            disabled={loading}
            style={{
              padding: "10px 24px",
              background: T.gradPrimary,
              color: T.white,
              border: "none",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: 600,
              opacity: loading ? 0.7 : 1,
            }}
          >
            <RiSaveLine size={18} />
            {loading ? "Saving…" : editing ? "Update Sponsor" : "Add Sponsor"}
          </button>
          {editing && (
            <button
              onClick={resetForm}
              style={{
                padding: "10px 24px",
                background: "#E5E7EB",
                color: T.textMid,
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {active.length > 0 && (
        <div style={{ marginBottom: "40px" }}>
          <p
            style={{
              color: T.textMuted,
              fontSize: "13px",
              marginBottom: "12px",
              fontWeight: 500,
            }}
          >
            📺 Live Marquee Preview
          </p>
          <div
            style={{
              display: "flex",
              gap: 16,
              overflowX: "auto",
              padding: "12px 0",
              scrollbarWidth: "thin",
            }}
          >
            {active.map((sp) => (
              <div
                key={sp._id}
                style={{
                  flexShrink: 0,
                  background: T.navyDeep,
                  border: `1px solid ${T.border}`,
                  borderBottom: `3px solid ${getSponsorTypeColor(sp.sponsorType)}`,
                  borderRadius: 8,
                  padding: sp.logoUrl ? "8px 24px" : "14px 32px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                  minWidth: 100,
                }}
              >
                {sp.logoUrl && (
                  <img
                    src={sp.logoUrl}
                    alt={sp.name}
                    style={{
                      height: 32,
                      maxWidth: 90,
                      objectFit: "contain",
                      filter: "brightness(0) invert(1)",
                    }}
                  />
                )}
                <span
                  style={{
                    color: T.white,
                    fontSize: sp.logoUrl ? 11 : 13,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    whiteSpace: "nowrap",
                    opacity: sp.logoUrl ? 0.65 : 1,
                  }}
                >
                  {sp.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <h3
        style={{
          fontSize: "18px",
          fontWeight: "semibold",
          color: T.navy,
          marginBottom: "16px",
        }}
      >
        ✅ Active Sponsors ({active.length})
      </h3>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          marginBottom: "40px",
        }}
      >
        {loading && active.length === 0 && (
          <div style={{ textAlign: "center", padding: "24px" }}>
            <div
              style={{
                display: "inline-block",
                width: "24px",
                height: "24px",
                border: `2px solid ${T.border}`,
                borderTopColor: T.primary,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          </div>
        )}
        {!loading && active.length === 0 && (
          <p style={{ color: T.textMuted, fontSize: "13px" }}>
            No active sponsors yet.
          </p>
        )}
        {active.map((sp) => (
          <div
            key={sp._id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              background: T.offWhite,
              padding: "12px",
              borderRadius: "12px",
              borderLeft: `4px solid ${getSponsorTypeColor(sp.sponsorType)}`,
            }}
          >
            <div
              style={{
                width: 56,
                height: 36,
                background: T.white,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {sp.logoUrl ? (
                <img
                  src={sp.logoUrl}
                  alt={sp.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <RiImageLine size={18} style={{ color: T.textMuted }} />
              )}
            </div>

            <div style={{ flex: 1 }}>
              <p
                style={{
                  color: T.text,
                  fontWeight: "semibold",
                  fontSize: "14px",
                }}
              >
                {sp.name}
              </p>
              <p style={{ color: T.textMuted, fontSize: "12px" }}>
                Type: {getSponsorTypeLabel(sp.sponsorType)} &nbsp;·&nbsp; Order:{" "}
                {sp.order} &nbsp;·&nbsp; {sp.logoUrl ? "Has logo" : "Text only"}
              </p>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => toggleActive(sp)}
                title={sp.active ? "Hide from site" : "Show on site"}
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  background: sp.active ? "#FEF3C7" : "#E8F5E9",
                  color: sp.active ? T.accentGold : "#4CAF50",
                  border: "none",
                }}
              >
                {sp.active ? (
                  <RiEyeOffLine size={16} />
                ) : (
                  <RiEyeLine size={16} />
                )}
              </button>
              <button
                onClick={() => startEdit(sp)}
                style={{
                  padding: "8px",
                  background: T.softBlue,
                  color: T.primary,
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                <RiEditLine size={16} />
              </button>
              <button
                onClick={() => deleteSponsor(sp._id)}
                style={{
                  padding: "8px",
                  background: "#FEE2E2",
                  color: "#EF4444",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                <RiDeleteBinLine size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {inactive.length > 0 && (
        <>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "semibold",
              color: T.navy,
              marginBottom: "16px",
            }}
          >
            🚫 Hidden Sponsors ({inactive.length})
          </h3>
          <div style={{ opacity: 0.6 }}>
            {inactive.map((sp) => (
              <div
                key={sp._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  background: T.offWhite,
                  padding: "12px",
                  borderRadius: "12px",
                  marginBottom: "12px",
                  borderLeft: `4px solid ${getSponsorTypeColor(sp.sponsorType)}`,
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 36,
                    background: T.white,
                    borderRadius: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                >
                  {sp.logoUrl ? (
                    <img
                      src={sp.logoUrl}
                      alt={sp.name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <RiImageLine size={18} style={{ color: T.textMuted }} />
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      color: T.text,
                      fontWeight: "semibold",
                      fontSize: "14px",
                    }}
                  >
                    {sp.name}
                  </p>
                  <p style={{ color: T.textMuted, fontSize: "12px" }}>
                    Type: {getSponsorTypeLabel(sp.sponsorType)} &nbsp;·&nbsp;
                    Order: {sp.order}
                  </p>
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => toggleActive(sp)}
                    title={sp.active ? "Hide from site" : "Show on site"}
                    style={{
                      padding: "8px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      background: sp.active ? "#FEF3C7" : "#E8F5E9",
                      color: sp.active ? T.accentGold : "#4CAF50",
                      border: "none",
                    }}
                  >
                    {sp.active ? (
                      <RiEyeOffLine size={16} />
                    ) : (
                      <RiEyeLine size={16} />
                    )}
                  </button>
                  <button
                    onClick={() => startEdit(sp)}
                    style={{
                      padding: "8px",
                      background: T.softBlue,
                      color: T.primary,
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <RiEditLine size={16} />
                  </button>
                  <button
                    onClick={() => deleteSponsor(sp._id)}
                    style={{
                      padding: "8px",
                      background: "#FEE2E2",
                      color: "#EF4444",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <RiDeleteBinLine size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div
        style={{
          marginTop: "32px",
          padding: "16px",
          background: `${T.primary}10`,
          borderRadius: "12px",
          border: `1px solid ${T.primary}20`,
        }}
      >
        <p style={{ color: T.primary, fontSize: "13px" }}>
          💡 The homepage fetches sponsors every 60 seconds automatically — no
          reload needed. For instant effect, the visitor can refresh the page.
        </p>
      </div>
    </motion.div>
  );
};
const BrochureTab = ({ token, eventId }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingBrochure, setUploadingBrochure] = useState(false);
  const [brochureFile, setBrochureFile] = useState(null);
  const [brochureFileName, setBrochureFileName] = useState("");
  const [brochurePdfUrl, setBrochurePdfUrl] = useState("");
  const [brochureInfo, setBrochureInfo] = useState({
    title: "Sponsorship Opportunities",
    subtitle: "Partner with Global Summit 2024",
    description:
      "Join the world's leading innovators and decision-makers at the most influential technology summit of the year. Our sponsorship packages offer unparalleled visibility and networking opportunities.",
    buttonText: "Download Brochure",
    brochureHighlights: [
      "5,000+ Attendees from 50+ Countries",
      "100+ Expert Speakers",
      "50+ Exhibition Booths",
      "Global Media Coverage",
      "Exclusive Networking Events",
      "Brand Visibility Opportunities",
    ],
    contactEmail: "sponsors@globalsummit.com",
    contactPhone: "+1 (555) 123-4567",
    isActive: true,
    template: "classic",
  });

  useEffect(() => {
    if (eventId) {
      fetchBrochureInfo();
    }
  }, [eventId]);

  const fetchBrochureInfo = async () => {
    if (!eventId) {
      console.warn("No eventId provided");
      return;
    }

    try {
      setLoading(true);
      const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
      const response = await axios.get(
        `${API_BASE}/api/brochure/info/${eventId}`,
        config,
      );

      console.log("📦 Brochure response:", response.data);

      if (response.data.success) {
        setBrochureInfo({
          title: response.data.title || "Sponsorship Opportunities",
          subtitle: response.data.subtitle || "Partner with Global Summit 2024",
          description: response.data.description || "",
          buttonText: response.data.buttonText || "Download Brochure",
          brochureHighlights: response.data.brochureHighlights || [],
          contactEmail:
            response.data.contactEmail || "sponsors@globalsummit.com",
          contactPhone: response.data.contactPhone || "+1 (555) 123-4567",
          isActive:
            response.data.isActive !== undefined
              ? response.data.isActive
              : true,
          template: response.data.template || "classic",
        });
        setBrochurePdfUrl(response.data.pdfUrl || "");
      }
    } catch (error) {
      console.error("Error fetching brochure info:", error);
      if (error.response?.status === 404) {
        console.log("No brochure found, using defaults");
      } else {
        toast.error("Failed to load brochure info");
      }
    } finally {
      setLoading(false);
    }
  };

  const saveBrochureInfo = async () => {
    if (!eventId) {
      toast.error("No event specified. Please select an event first.");
      return;
    }

    try {
      setLoading(true);
      const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};

      const payload = {
        title: brochureInfo.title,
        subtitle: brochureInfo.subtitle,
        description: brochureInfo.description,
        buttonText: brochureInfo.buttonText,
        brochureHighlights: brochureInfo.brochureHighlights,
        contactEmail: brochureInfo.contactEmail,
        contactPhone: brochureInfo.contactPhone,
        isActive: brochureInfo.isActive,
        template: brochureInfo.template,
      };

      console.log("📤 Saving brochure info:", payload);

      const response = await axios.post(
        `${API_BASE}/api/brochure/info/${eventId}`,
        payload,
        config,
      );

      if (response.data.success) {
        toast.success("Brochure info saved successfully!");
        await fetchBrochureInfo();
      } else {
        toast.error(response.data.message || "Failed to save brochure info");
      }
    } catch (error) {
      console.error("Error saving brochure info:", error);
      toast.error(
        error.response?.data?.message || "Failed to save brochure info",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBrochureFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Please select a PDF file");
      return;
    }
    // ✅ Removed the 10MB size limit check – users can upload any size
    setBrochureFile(file);
    setBrochureFileName(file.name);
  };

  const uploadBrochure = async () => {
    if (!eventId) {
      toast.error("No event specified. Please select an event first.");
      return;
    }

    if (!brochureFile) {
      toast.error("Please select a PDF file to upload");
      return;
    }

    try {
      setUploadingBrochure(true);
      const formData = new FormData();
      formData.append("brochure", brochureFile);

      const config = token
        ? {
            headers: { Authorization: `Bearer ${token}` },
            "Content-Type": "multipart/form-data",
          }
        : {};

      const response = await axios.post(
        `${API_BASE}/api/brochure/upload/${eventId}`,
        formData,
        config,
      );

      if (response.data.success) {
        toast.success("Brochure uploaded successfully!");
        setBrochureFile(null);
        setBrochureFileName("");
        setBrochurePdfUrl(response.data.data?.pdfUrl || "");
        await fetchBrochureInfo();
      } else {
        toast.error(response.data.message || "Failed to upload brochure");
      }
    } catch (error) {
      console.error("Error uploading brochure:", error);
      toast.error(error.response?.data?.message || "Failed to upload brochure");
    } finally {
      setUploadingBrochure(false);
    }
  };

  const deleteBrochure = async () => {
    if (!eventId) {
      toast.error("No event specified. Please select an event first.");
      return;
    }

    if (!window.confirm("Delete the uploaded brochure?")) return;

    try {
      const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
      await axios.delete(`${API_BASE}/api/brochure/delete/${eventId}`, config);

      toast.success("Brochure deleted successfully!");
      setBrochurePdfUrl("");
      setBrochureFile(null);
      setBrochureFileName("");
      await fetchBrochureInfo();
    } catch (error) {
      console.error("Error deleting brochure:", error);
      toast.error(error.response?.data?.message || "Failed to delete brochure");
    }
  };

  const downloadBrochure = async () => {
    if (!eventId) {
      toast.error("No event specified. Please select an event first.");
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE}/api/brochure/download/${eventId}`,
        {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Sponsorship-Brochure.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Brochure downloaded successfully!");
    } catch (error) {
      console.error("Error downloading brochure:", error);
      toast.error("Failed to download brochure");
    }
  };

  if (!eventId) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: T.white,
          padding: "32px",
          borderRadius: "16px",
          boxShadow: T.shadowMd,
          border: `1px solid ${T.borderLight}`,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 16,
            background: T.softTeal,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <RiFilePdfLine size={28} color={T.primary} />
        </div>
        <h3
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: "black",
            marginBottom: 8,
          }}
        >
          No Event Selected
        </h3>
        <p style={{ color: "black" }}>
          Please select an event to manage brochure.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: T.white,
        padding: "32px",
        borderRadius: "16px",
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "bold", color: "black" }}>
          Manage Brochure
        </h2>
        <button
          onClick={fetchBrochureInfo}
          style={{
            fontSize: "12px",
            color: T.primary,
            background: T.offWhite,
            padding: "4px 12px",
            borderRadius: "999px",
            border: `1px solid ${T.border}`,
            cursor: "pointer",
          }}
        >
          <RiRefreshLine
            size={14}
            style={{ display: "inline", marginRight: "4px" }}
          />{" "}
          Refresh
        </button>
      </div>
      <p style={{ color: "black", marginBottom: "32px", fontSize: "14px" }}>
        Upload and manage the sponsorship brochure that users can download.
      </p>

      {brochurePdfUrl && (
        <div
          style={{
            background: T.offWhite,
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "32px",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "semibold",
              color: "black",
              marginBottom: "16px",
            }}
          >
            ✅ Current Brochure
          </h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px",
              background: T.white,
              borderRadius: "12px",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "#FEE2E2",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span style={{ fontSize: "24px" }}>📄</span>
              </div>
              <div>
                <p style={{ color: "black", fontWeight: 500 }}>
                  {brochureInfo.title}
                </p>
                <p style={{ color: "black", fontSize: "13px" }}>PDF Document</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button
                onClick={downloadBrochure}
                style={{
                  padding: "8px 16px",
                  background: T.softBlue,
                  color: T.primary,
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontWeight: 600,
                  fontSize: "13px",
                }}
              >
                <RiDownloadLine size={16} /> Download
              </button>
              <button
                onClick={deleteBrochure}
                style={{
                  padding: "8px 16px",
                  background: "#FEE2E2",
                  color: "#EF4444",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontWeight: 600,
                  fontSize: "13px",
                }}
              >
                <RiDeleteBinLine size={16} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          background: T.offWhite,
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "32px",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "semibold",
            color: "black",
            marginBottom: "16px",
          }}
        >
          📋 Brochure Information
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label
              style={{
                display: "block",
                color: "black",
                marginBottom: "8px",
                fontWeight: 600,
              }}
            >
              Title *
            </label>
            <input
              type="text"
              value={brochureInfo.title}
              onChange={(e) =>
                setBrochureInfo({ ...brochureInfo, title: e.target.value })
              }
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                outline: "none",
                transition: "border-color 0.2s",
                color: "black",
              }}
              onFocus={(e) => (e.target.style.borderColor = T.primary)}
              onBlur={(e) => (e.target.style.borderColor = T.border)}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                color: "black",
                marginBottom: "8px",
                fontWeight: 600,
              }}
            >
              Subtitle
            </label>
            <input
              type="text"
              value={brochureInfo.subtitle}
              onChange={(e) =>
                setBrochureInfo({ ...brochureInfo, subtitle: e.target.value })
              }
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                outline: "none",
                transition: "border-color 0.2s",
                color: "black",
              }}
              onFocus={(e) => (e.target.style.borderColor = T.primary)}
              onBlur={(e) => (e.target.style.borderColor = T.border)}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                color: "black",
                marginBottom: "8px",
                fontWeight: 600,
              }}
            >
              Description
            </label>
            <textarea
              value={brochureInfo.description}
              onChange={(e) =>
                setBrochureInfo({
                  ...brochureInfo,
                  description: e.target.value,
                })
              }
              rows="3"
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                fontFamily: "'Inter', sans-serif",
                resize: "vertical",
                outline: "none",
                transition: "border-color 0.2s",
                color: "black",
              }}
              onFocus={(e) => (e.target.style.borderColor = T.primary)}
              onBlur={(e) => (e.target.style.borderColor = T.border)}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                color: "black",
                marginBottom: "8px",
                fontWeight: 600,
              }}
            >
              Button Text
            </label>
            <input
              type="text"
              value={brochureInfo.buttonText}
              onChange={(e) =>
                setBrochureInfo({ ...brochureInfo, buttonText: e.target.value })
              }
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                outline: "none",
                transition: "border-color 0.2s",
                color: "black",
              }}
              onFocus={(e) => (e.target.style.borderColor = T.primary)}
              onBlur={(e) => (e.target.style.borderColor = T.border)}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <input
              type="checkbox"
              checked={brochureInfo.isActive}
              onChange={(e) =>
                setBrochureInfo({ ...brochureInfo, isActive: e.target.checked })
              }
              style={{ width: "18px", height: "18px", accentColor: T.primary }}
            />
            <label style={{ cursor: "pointer", color: "black" }}>
              Show on sponsors page
            </label>
          </div>
        </div>
      </div>

      <div
        style={{
          background: T.offWhite,
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "32px",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "semibold",
            color: "black",
            marginBottom: "16px",
          }}
        >
          📤 Upload New Brochure (PDF)
        </h3>
        <div>
          <label
            style={{
              display: "block",
              color: "black",
              marginBottom: "8px",
              fontWeight: 600,
            }}
          >
            Select PDF File (No size limit)
          </label>
          <input
            type="file"
            accept=".pdf"
            onChange={handleBrochureFileChange}
            style={{ width: "100%" }}
          />
          {brochureFileName && (
            <p style={{ color: "#4CAF50", fontSize: "13px", marginTop: "8px" }}>
              Selected: {brochureFileName}
            </p>
          )}
          {brochureFile && (
            <button
              onClick={uploadBrochure}
              disabled={uploadingBrochure}
              style={{
                marginTop: "12px",
                padding: "10px 24px",
                background: T.gradPrimary,
                color: T.white,
                border: "none",
                borderRadius: "8px",
                cursor: uploadingBrochure ? "not-allowed" : "pointer",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                opacity: uploadingBrochure ? 0.7 : 1,
              }}
            >
              <RiUploadLine size={18} />
              {uploadingBrochure ? "Uploading..." : "Upload Brochure"}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
        <button
          onClick={saveBrochureInfo}
          disabled={loading}
          style={{
            padding: "12px 32px",
            background: T.gradPrimary,
            color: T.white,
            border: "none",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: loading ? "not-allowed" : "pointer",
            fontWeight: 600,
            opacity: loading ? 0.7 : 1,
          }}
        >
          <RiSaveLine size={18} />{" "}
          {loading ? "Saving..." : "Save Brochure Settings"}
        </button>
        {brochureFile && (
          <button
            onClick={() => {
              setBrochureFile(null);
              setBrochureFileName("");
            }}
            style={{
              padding: "12px 32px",
              background: "#E5E7EB",
              color: "black",
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
            }}
          >
            Cancel Upload
          </button>
        )}
      </div>
    </motion.div>
  );
};
/* ══════════════════════════════════════════════════════════════════════
   SUMMIT PUBLICATIONS TAB (SHORTENED VERSION - SAME PATTERN)
   ══════════════════════════════════════════════════════════════════════ */
const SummitPublicationsTab = ({ token, eventId }) => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    coverImage: "",
    isActive: true,
    order: 0,
  });

  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    if (eventId) fetchPublications();
  }, [eventId]);

  const fetchPublications = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/summit-publications/event/${eventId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (data.success) setPublications(data.data || []);
      else setPublications([]);
    } catch (error) {
      console.error("Error fetching publications:", error);
      toast.error("Failed to load publications");
      setPublications([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      coverImage: "",
      isActive: true,
      order: 0,
    });
    setCoverImageFile(null);
    setCoverImagePreview(null);
    setEditing(null);
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }
    setCoverImageFile(file);
    setCoverImagePreview(URL.createObjectURL(file));
  };

  const uploadCoverImage = async () => {
    if (!coverImageFile) return null;
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append("image", coverImageFile);
      const { data } = await axios.post(
        `${API_BASE}/api/summit-publications/upload-cover`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (data.success) return data.imageUrl;
      return null;
    } catch (error) {
      console.error("Error uploading cover image:", error);
      toast.error("Failed to upload cover image");
      return null;
    } finally {
      setUploadingCover(false);
    }
  };

  const savePublication = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    setLoading(true);
    try {
      let coverImageUrl = formData.coverImage;
      if (coverImageFile) {
        const uploaded = await uploadCoverImage();
        if (uploaded) coverImageUrl = uploaded;
        else {
          setLoading(false);
          return;
        }
      }

      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        coverImage: coverImageUrl,
        isActive: formData.isActive,
        order: Number(formData.order) || 0,
        eventId,
        // Fixed values for hidden fields
        category: "venue",
        abstract: "Publication venue information.",
        authors: [],
        keywords: [],
        journalName: "",
        volume: "",
        issue: "",
        pages: "",
        publicationDate: new Date(),
        doi: "",
        pdfUrl: "",
        isFeatured: false,
      };

      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editing) {
        await axios.put(
          `${API_BASE}/api/summit-publications/${editing._id}`,
          payload,
          config,
        );
        toast.success("Publication updated successfully!");
      } else {
        await axios.post(
          `${API_BASE}/api/summit-publications`,
          payload,
          config,
        );
        toast.success("Publication created successfully!");
      }

      resetForm();
      setShowForm(false);
      fetchPublications();
    } catch (error) {
      console.error("Error saving publication:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (pub) => {
    try {
      await axios.patch(
        `${API_BASE}/api/summit-publications/${pub._id}/toggle-active`,
        { eventId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Publication ${pub.isActive ? "deactivated" : "activated"}`,
      );
      fetchPublications();
    } catch {
      toast.error("Failed to update");
    }
  };

  const deletePublication = async (id) => {
    if (!window.confirm("Delete this publication?")) return;
    try {
      await axios.delete(`${API_BASE}/api/summit-publications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Publication deleted");
      fetchPublications();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const seedDefaultPublications = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/summit-publications/seed/event/${eventId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Default publications seeded!");
      fetchPublications();
    } catch (error) {
      console.error("Error seeding:", error);
      toast.error("Failed to seed");
    } finally {
      setLoading(false);
    }
  };

  const activePubs = publications
    .filter((p) => p.isActive)
    .sort((a, b) => a.order - b.order);
  const inactivePubs = publications.filter((p) => !p.isActive);

  if (!eventId) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: T.white,
          padding: "32px",
          borderRadius: "16px",
          boxShadow: T.shadowMd,
          border: `1px solid ${T.borderLight}`,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 16,
            background: T.softTeal,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <RiBookOpenLine size={28} color={T.primary} />
        </div>
        <h3
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: T.text,
            marginBottom: 8,
          }}
        >
          No Event Selected
        </h3>
        <p style={{ color: T.textMuted }}>
          Please select an event to manage publications.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: T.white,
        padding: "32px",
        borderRadius: "16px",
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy }}>
          📚 Manage Publications
        </h2>
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              color: T.textMuted,
              background: T.softBlue,
              padding: "4px 12px",
              borderRadius: "999px",
            }}
          >
            {activePubs.length} active · {inactivePubs.length} hidden
          </span>
          <button
            onClick={fetchPublications}
            style={{
              fontSize: "12px",
              color: T.primary,
              background: T.offWhite,
              padding: "4px 12px",
              borderRadius: "999px",
              border: `1px solid ${T.border}`,
              cursor: "pointer",
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>
      <p style={{ color: T.textMuted, marginBottom: "32px", fontSize: "14px" }}>
        Manage the publication venue information displayed on the public page.
      </p>

      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "32px",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
            setEditing(null);
          }}
          style={{
            padding: "10px 24px",
            background: T.gradPrimary,
            color: T.white,
            border: "none",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          {showForm ? <RiCloseLine size={18} /> : <RiAddLine size={18} />}
          {showForm ? "Close Form" : "Add Publication"}
        </button>
      </div>

      {/* ─── FORM ─── */}
      {showForm && (
        <div
          style={{
            background: T.offWhite,
            border: `1px solid ${T.border}`,
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "40px",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: T.navy,
              marginBottom: "20px",
            }}
          >
            {editing
              ? `✏️ Editing: ${editing.title}`
              : "➕ Add New Publication"}
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "16px",
            }}
          >
            {/* Title */}
            <div style={{ gridColumn: "span 2" }}>
              <label
                style={{
                  display: "block",
                  color: T.textMid,
                  fontSize: "13px",
                  marginBottom: "4px",
                  fontWeight: 600,
                }}
              >
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  color: T.text,
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = T.primary)}
                onBlur={(e) => (e.target.style.borderColor = T.border)}
                placeholder="e.g., Publication Venue"
              />
            </div>

            {/* Description */}
            <div style={{ gridColumn: "span 2" }}>
              <label
                style={{
                  display: "block",
                  color: T.textMid,
                  fontSize: "13px",
                  marginBottom: "4px",
                  fontWeight: 600,
                }}
              >
                Description{" "}
                <span style={{ color: T.primary, fontSize: "11px" }}>
                  (supports HTML)
                </span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows="6"
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  color: T.text,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "14px",
                  resize: "vertical",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = T.primary)}
                onBlur={(e) => (e.target.style.borderColor = T.border)}
                placeholder="Enter the publication venue information. Use <strong> for bold, <a> for links, etc."
              />
            </div>

            {/* Cover Image Upload */}
            <div>
              <label
                style={{
                  display: "block",
                  color: T.textMid,
                  fontSize: "13px",
                  marginBottom: "4px",
                  fontWeight: 600,
                }}
              >
                Cover Image
              </label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    border: `2px dashed ${T.border}`,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    background: T.offWhite,
                  }}
                >
                  {coverImagePreview || formData.coverImage ? (
                    <img
                      src={coverImagePreview || formData.coverImage}
                      alt="Cover"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <RiImageLine size={24} style={{ color: T.textMuted }} />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    style={{ display: "none" }}
                    id="cover-upload"
                  />
                  <label
                    htmlFor="cover-upload"
                    style={{
                      padding: "8px 16px",
                      background: T.softBlue,
                      color: T.primary,
                      border: `1px solid ${T.border}`,
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      display: "inline-block",
                    }}
                  >
                    <RiUploadLine
                      size={16}
                      style={{ display: "inline", marginRight: "6px" }}
                    />
                    {coverImageFile ? "Change Image" : "Upload Image"}
                  </label>
                  {coverImageFile && (
                    <button
                      onClick={() => {
                        setCoverImageFile(null);
                        setCoverImagePreview(null);
                      }}
                      style={{
                        marginLeft: "8px",
                        padding: "8px 12px",
                        background: "#FEE2E2",
                        color: "#EF4444",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <p
                style={{
                  fontSize: "11px",
                  color: T.textMuted,
                  marginTop: "4px",
                }}
              >
                Recommended: 400×400px or square, max 5MB
              </p>
            </div>

            {/* Order */}
            <div>
              <label
                style={{
                  display: "block",
                  color: T.textMid,
                  fontSize: "13px",
                  marginBottom: "4px",
                  fontWeight: 600,
                }}
              >
                Display Order
              </label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    order: parseInt(e.target.value) || 0,
                  })
                }
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  color: T.text,
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = T.primary)}
                onBlur={(e) => (e.target.style.borderColor = T.border)}
              />
            </div>

            {/* Active checkbox */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
                style={{
                  width: "18px",
                  height: "18px",
                  accentColor: T.primary,
                }}
              />
              <label style={{ color: T.textMid, fontSize: "13px" }}>
                ✅ Active (visible on website)
              </label>
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
            <button
              onClick={savePublication}
              disabled={loading || uploadingCover}
              style={{
                padding: "10px 24px",
                background: T.gradPrimary,
                color: T.white,
                border: "none",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: 600,
                opacity: loading || uploadingCover ? 0.7 : 1,
              }}
            >
              <RiSaveLine size={18} />
              {loading || uploadingCover
                ? "Saving..."
                : editing
                  ? "Update Publication"
                  : "Add Publication"}
            </button>
            {editing && (
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                  setEditing(null);
                }}
                style={{
                  padding: "10px 24px",
                  background: "#E5E7EB",
                  color: T.textMid,
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── ACTIVE PUBLICATIONS ─── */}
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "600",
          color: T.navy,
          marginBottom: "16px",
        }}
      >
        ✅ Active Publications ({activePubs.length})
      </h3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: "16px",
          marginBottom: "40px",
        }}
      >
        {activePubs.map((pub) => (
          <div
            key={pub._id}
            style={{
              background: T.offWhite,
              padding: "16px",
              borderRadius: "12px",
              border: `1px solid ${T.border}`,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = T.shadowMd;
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            <div
              style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}
            >
              {pub.coverImage ? (
                <img
                  src={pub.coverImage}
                  alt={pub.title}
                  style={{
                    width: "60px",
                    height: "80px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "60px",
                    height: "80px",
                    background: T.softTeal,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <RiFileTextLine size={24} style={{ color: T.textMuted }} />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p
                  style={{
                    fontWeight: 600,
                    color: T.text,
                    fontSize: "14px",
                    lineHeight: 1.3,
                    marginBottom: "4px",
                  }}
                >
                  {pub.title}
                </p>
                {pub.description && (
                  <div
                    style={{
                      fontSize: "13px",
                      color: T.textMid,
                      marginTop: "4px",
                      lineHeight: 1.6,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                    dangerouslySetInnerHTML={{ __html: pub.description }}
                  />
                )}
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginTop: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: "10px",
                      background: T.softTeal,
                      color: T.primary,
                      padding: "2px 8px",
                      borderRadius: "12px",
                    }}
                  >
                    Venue Info
                  </span>
                  <span style={{ fontSize: "10px", color: T.textMuted }}>
                    Order: {pub.order}
                  </span>
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "12px",
                borderTop: `1px solid ${T.border}`,
                paddingTop: "12px",
              }}
            >
              <button
                onClick={() => toggleActive(pub)}
                style={{
                  padding: "6px 10px",
                  background: "#FEF3C7",
                  color: "#92400E",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                🙈 Hide
              </button>
              <button
                onClick={() => {
                  setFormData({
                    title: pub.title || "",
                    description: pub.description || "",
                    coverImage: pub.coverImage || "",
                    isActive: pub.isActive !== undefined ? pub.isActive : true,
                    order: pub.order || 0,
                  });
                  setCoverImagePreview(pub.coverImage || null);
                  setEditing(pub);
                  setShowForm(true);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                style={{
                  padding: "6px 10px",
                  background: T.softBlue,
                  color: T.primary,
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                ✏️ Edit
              </button>
              <button
                onClick={() => deletePublication(pub._id)}
                style={{
                  padding: "6px 10px",
                  background: "#FEE2E2",
                  color: "#EF4444",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                🗑 Delete
              </button>
            </div>
          </div>
        ))}
        {activePubs.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: T.textMuted,
              gridColumn: "span 3",
            }}
          >
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 16,
                background: T.softTeal,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <RiBookOpenLine size={28} color={T.primary} />
            </div>
            <p
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: T.text,
                marginBottom: "8px",
              }}
            >
              No Active Publications
            </p>
            <p style={{ fontSize: "14px" }}>
              Add your first publication using the form above.
            </p>
          </div>
        )}
      </div>

      {/* ─── INACTIVE PUBLICATIONS ─── */}
      {inactivePubs.length > 0 && (
        <>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: T.navy,
              marginBottom: "16px",
            }}
          >
            🚫 Hidden Publications ({inactivePubs.length})
          </h3>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              opacity: 0.7,
              marginBottom: "40px",
            }}
          >
            {inactivePubs.map((pub) => (
              <div
                key={pub._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  background: T.offWhite,
                  padding: "12px 16px",
                  borderRadius: "12px",
                  border: `1px solid ${T.borderLight}`,
                }}
              >
                {pub.coverImage ? (
                  <img
                    src={pub.coverImage}
                    alt={pub.title}
                    style={{
                      width: "40px",
                      height: "50px",
                      objectFit: "cover",
                      borderRadius: "6px",
                    }}
                  />
                ) : (
                  <RiFileTextLine size={24} style={{ color: T.textMuted }} />
                )}
                <div style={{ flex: 1 }}>
                  <p style={{ color: T.text, fontWeight: 500 }}>{pub.title}</p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => toggleActive(pub)}
                    style={{
                      padding: "6px 12px",
                      background: "#E8F5E9",
                      color: "#4CAF50",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    👁 Show
                  </button>
                  <button
                    onClick={() => {
                      setFormData({
                        title: pub.title || "",
                        description: pub.description || "",
                        coverImage: pub.coverImage || "",
                        isActive:
                          pub.isActive !== undefined ? pub.isActive : true,
                        order: pub.order || 0,
                      });
                      setCoverImagePreview(pub.coverImage || null);
                      setEditing(pub);
                      setShowForm(true);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    style={{
                      padding: "6px 12px",
                      background: T.softBlue,
                      color: T.primary,
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => deletePublication(pub._id)}
                    style={{
                      padding: "6px 12px",
                      background: "#FEE2E2",
                      color: "#EF4444",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    🗑 Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div
        style={{
          padding: "16px",
          background: `${T.primary}10`,
          borderRadius: "12px",
          border: `1px solid ${T.primary}20`,
        }}
      >
        <p style={{ color: T.primary, fontSize: "13px" }}>
          💡 This publication will appear on the public Publications page. Use{" "}
          <strong>HTML tags</strong> in the description (e.g., &lt;strong&gt;,
          &lt;em&gt;, &lt;a&gt;) for formatting. Upload a cover image to display
          alongside the text.
        </p>
      </div>
    </motion.div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   ABOUT US TAB (SHORTENED - FOLLOWS SAME PATTERN)
   ══════════════════════════════════════════════════════════════════════ */
const AboutUsTab = ({ token, eventId }) => {
  const [aboutData, setAboutData] = useState(null);
  const [allAboutData, setAllAboutData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showNewVersionForm, setShowNewVersionForm] = useState(false);
  const [newVersionData, setNewVersionData] = useState({
    mainTitle: "",
    mainDescription: "",
    isActive: true,
    version: "1.0",
  });

  // ── Paragraph editing state ──
  const [paragraphs, setParagraphs] = useState([]);
  const [editingParagraphIndex, setEditingParagraphIndex] = useState(null);
  const [paragraphEditValue, setParagraphEditValue] = useState("");
  const [newParagraph, setNewParagraph] = useState("");

  useEffect(() => {
    if (eventId) {
      fetchAboutUs();
    }
  }, [eventId]);

  const fetchAboutUs = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/about-us/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (data.success) {
        setAllAboutData(data.data || []);
        const active = (data.data || []).find((a) => a.isActive === true);
        if (active) {
          setAboutData(active);
          // Split description into paragraphs
          const desc = active.mainDescription || "";
          const paras = desc.split("\n\n").filter((p) => p.trim());
          setParagraphs(paras.length > 0 ? paras : [""]);
        } else if (data.data && data.data.length > 0) {
          setAboutData(data.data[0]);
          const desc = data.data[0].mainDescription || "";
          const paras = desc.split("\n\n").filter((p) => p.trim());
          setParagraphs(paras.length > 0 ? paras : [""]);
        } else {
          setParagraphs([""]);
        }
      }
    } catch (error) {
      console.error("Error fetching about us:", error);
      toast.error(
        error.response?.data?.message || "Failed to load about us content",
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Sync paragraphs to description string ──
  const paragraphsToDescription = (paras) => {
    return paras.filter((p) => p.trim()).join("\n\n");
  };

  // ── Update full description from paragraphs ──
  const updateDescriptionFromParagraphs = async (newParagraphs) => {
    const description = paragraphsToDescription(newParagraphs);
    setParagraphs(newParagraphs);
    if (aboutData && aboutData._id) {
      await updateField("mainDescription", description);
    }
  };

  // ── Paragraph handlers ──
  const addParagraph = () => {
    if (newParagraph.trim()) {
      const newParas = [...paragraphs, newParagraph.trim()];
      updateDescriptionFromParagraphs(newParas);
      setNewParagraph("");
      toast.success("Paragraph added!");
    } else {
      toast.error("Please enter some text for the paragraph.");
    }
  };

  const startEditParagraph = (index) => {
    setEditingParagraphIndex(index);
    setParagraphEditValue(paragraphs[index] || "");
  };

  const saveParagraphEdit = (index) => {
    if (paragraphEditValue.trim()) {
      const newParas = [...paragraphs];
      newParas[index] = paragraphEditValue.trim();
      updateDescriptionFromParagraphs(newParas);
      setEditingParagraphIndex(null);
      setParagraphEditValue("");
      toast.success("Paragraph updated!");
    } else {
      toast.error("Paragraph cannot be empty.");
    }
  };

  const cancelParagraphEdit = () => {
    setEditingParagraphIndex(null);
    setParagraphEditValue("");
  };

  const deleteParagraph = (index) => {
    if (paragraphs.length <= 1) {
      toast.error("You need at least one paragraph.");
      return;
    }
    if (!window.confirm("Delete this paragraph?")) return;
    const newParas = paragraphs.filter((_, i) => i !== index);
    updateDescriptionFromParagraphs(newParas);
    toast.success("Paragraph deleted.");
  };

  const moveParagraphUp = (index) => {
    if (index === 0) return;
    const newParas = [...paragraphs];
    [newParas[index - 1], newParas[index]] = [
      newParas[index],
      newParas[index - 1],
    ];
    updateDescriptionFromParagraphs(newParas);
  };

  const moveParagraphDown = (index) => {
    if (index === paragraphs.length - 1) return;
    const newParas = [...paragraphs];
    [newParas[index], newParas[index + 1]] = [
      newParas[index + 1],
      newParas[index],
    ];
    updateDescriptionFromParagraphs(newParas);
  };

  // ── Existing update field (kept for title and others) ──
  const updateField = async (field, value) => {
    if (!aboutData || !aboutData._id) {
      toast.error("No active about us content to update");
      return;
    }

    const updatedData = { ...aboutData, [field]: value };

    try {
      setLoading(true);
      const payload = { ...updatedData, eventId };

      const { data } = await axios.put(
        `${API_BASE}/api/about-us/${aboutData._id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (data.success) {
        toast.success(`${field} updated successfully`);
        setAboutData(updatedData);
        setAllAboutData(
          allAboutData.map((a) => (a._id === aboutData._id ? updatedData : a)),
        );
        setEditingField(null);
        setEditValue("");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch (error) {
      console.error("Error updating:", error);
      toast.error(error.response?.data?.message || "Failed to update");
    } finally {
      setLoading(false);
    }
  };

  // ── Create, delete, set active (unchanged) ──
  const createNewVersion = async () => {
    if (!newVersionData.mainTitle) {
      toast.error("Main title is required");
      return;
    }
    try {
      setLoading(true);
      const payload = { ...newVersionData, eventId };
      const { data } = await axios.post(`${API_BASE}/api/about-us`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (data.success) {
        toast.success("New version created successfully");
        setShowNewVersionForm(false);
        setNewVersionData({
          mainTitle: "",
          mainDescription: "",
          isActive: true,
          version: "1.0",
        });
        await fetchAboutUs();
      } else {
        toast.error(data.message || "Failed to create version");
      }
    } catch (error) {
      console.error("Error creating version:", error);
      toast.error(error.response?.data?.message || "Failed to create version");
    } finally {
      setLoading(false);
    }
  };

  const setActiveVersion = async (item) => {
    if (!item._id) return;
    try {
      setLoading(true);
      for (const other of allAboutData) {
        if (other._id !== item._id && other.isActive === true) {
          await axios.patch(
            `${API_BASE}/api/about-us/${other._id}/toggle-active`,
            { eventId },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }
      }
      if (item.isActive !== true) {
        await axios.patch(
          `${API_BASE}/api/about-us/${item._id}/toggle-active`,
          { eventId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
      toast.success(`${item.mainTitle || "Version"} set as active`);
      await fetchAboutUs();
    } catch (error) {
      console.error("Error setting active version:", error);
      toast.error(
        error.response?.data?.message || "Failed to set active version",
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteVersion = async (id) => {
    if (!window.confirm("Delete this version? This action cannot be undone."))
      return;
    try {
      setLoading(true);
      await axios.delete(`${API_BASE}/api/about-us/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Version deleted successfully");
      await fetchAboutUs();
    } catch (error) {
      console.error("Error deleting version:", error);
      toast.error(error.response?.data?.message || "Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultAboutUs = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/api/about-us/seed/event/${eventId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.success) {
        toast.success("Default About Us content seeded!");
        await fetchAboutUs();
      } else {
        toast.error(data.message || "Failed to seed");
      }
    } catch (error) {
      console.error("Error seeding:", error);
      toast.error(error.response?.data?.message || "Failed to seed");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue || "");
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
  };

  // ── EditableText for single-line fields ──
  const EditableText = ({ field, value, placeholder }) => {
    if (editingField === field) {
      return (
        <div className="space-y-2">
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full p-2 border rounded-lg"
            style={{ borderColor: T.borderLight, color: "black" }}
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={() => updateField(field, editValue)}
              className="px-3 py-1 rounded text-white text-sm"
              style={{ background: T.primary }}
              disabled={loading}
            >
              Save
            </button>
            <button
              onClick={cancelEdit}
              className="px-3 py-1 rounded bg-gray-300 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="group relative">
        <div
          className={`${value ? "" : "text-gray-400 italic"} cursor-pointer hover:bg-gray-50 p-1 rounded transition`}
          onClick={() => startEdit(field, value)}
        >
          {value || placeholder || "Click to edit"}
        </div>
        <button
          onClick={() => startEdit(field, value)}
          className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-1 rounded transition"
          style={{ color: T.primary }}
        >
          <RiEditLine size={14} />
        </button>
      </div>
    );
  };

  if (loading && !aboutData) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        <p style={{ color: "black" }} className="mt-2">
          Loading...
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* ─── HEADER ─── */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: T.white }}>
            About Us Management
          </h3>
          <p className="text-sm mt-1" style={{ color: "white" }}>
            Manage your About Us page content
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowNewVersionForm(!showNewVersionForm)}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition"
            style={{ background: T.gradPrimary, color: T.white }}
            disabled={loading}
          >
            <RiAddLine size={18} /> Create New Version
          </button>
        </div>
      </div>

      {/* ─── CREATE NEW VERSION FORM ─── */}
      {showNewVersionForm && (
        <div
          className="rounded-xl p-6"
          style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
        >
          <h4 className="font-semibold mb-4" style={{ color: T.navy }}>
            Create New About Us Version
          </h4>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: "black" }}
              >
                Main Title *
              </label>
              <input
                type="text"
                value={newVersionData.mainTitle}
                onChange={(e) =>
                  setNewVersionData({
                    ...newVersionData,
                    mainTitle: e.target.value,
                  })
                }
                className="w-full p-2 border rounded-lg"
                style={{ borderColor: T.borderLight, color: "black" }}
                placeholder="e.g., About Immunology Summit 2026"
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: "black" }}
              >
                Version
              </label>
              <input
                type="text"
                value={newVersionData.version}
                onChange={(e) =>
                  setNewVersionData({
                    ...newVersionData,
                    version: e.target.value,
                  })
                }
                className="w-full p-2 border rounded-lg"
                style={{ borderColor: T.borderLight, color: "black" }}
                placeholder="1.0"
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: "black" }}
              >
                Main Description (optional, can add paragraphs later)
              </label>
              <textarea
                value={newVersionData.mainDescription}
                onChange={(e) =>
                  setNewVersionData({
                    ...newVersionData,
                    mainDescription: e.target.value,
                  })
                }
                className="w-full p-2 border rounded-lg"
                rows="4"
                style={{ borderColor: T.borderLight, color: "black" }}
                placeholder="Initial description (you can add paragraphs later)"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newVersionData.isActive}
                onChange={(e) =>
                  setNewVersionData({
                    ...newVersionData,
                    isActive: e.target.checked,
                  })
                }
                className="w-4 h-4"
              />
              <label className="text-sm font-medium" style={{ color: "black" }}>
                Set as Active Version
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={createNewVersion}
              className="px-4 py-2 rounded-lg text-white"
              style={{ background: T.primary }}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Version"}
            </button>
            <button
              onClick={() => setShowNewVersionForm(false)}
              className="px-4 py-2 rounded-lg bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ─── ACTIVE VERSION DISPLAY ─── */}
      {aboutData ? (
        <div
          className="rounded-xl overflow-hidden shadow-lg"
          style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
        >
          <div
            className="px-6 py-4 flex justify-between items-center"
            style={{ background: T.gradPrimary }}
          >
            <div>
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <RiFileTextLine size={24} /> Active Version
              </h3>
              <p className="text-sm text-white/70">
                Version {aboutData.version || "1.0"} • ID:{" "}
                {aboutData._id?.slice(-8)}
              </p>
            </div>
            <span
              className="px-3 py-1 rounded-full text-xs font-semibold"
              style={{ background: "#10B981", color: "white" }}
            >
              ACTIVE
            </span>
          </div>

          <div className="p-6 space-y-6">
            {/* Main Title */}
            <div>
              <label
                className="block text-sm font-semibold mb-1"
                style={{ color: T.navy }}
              >
                Main Title
              </label>
              <EditableText
                field="mainTitle"
                value={aboutData.mainTitle}
                placeholder="Enter main title"
              />
            </div>

            {/* ─── PARAGRAPH EDITOR ─── */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label
                  className="block text-sm font-semibold"
                  style={{ color: T.navy }}
                >
                  Description Paragraphs
                </label>
                <span className="text-xs" style={{ color: T.textMuted }}>
                  {paragraphs.length} paragraph
                  {paragraphs.length !== 1 ? "s" : ""}
                </span>
              </div>

              <div className="space-y-3">
                {paragraphs.map((para, idx) => (
                  <div
                    key={idx}
                    className="group flex items-start gap-2 p-3 rounded-lg border"
                    style={{
                      background: T.offWhite,
                      borderColor: T.borderLight,
                    }}
                  >
                    {editingParagraphIndex === idx ? (
                      // ── Edit mode ──
                      <div className="flex-1 space-y-2">
                        <textarea
                          value={paragraphEditValue}
                          onChange={(e) =>
                            setParagraphEditValue(e.target.value)
                          }
                          className="w-full p-2 border rounded-lg"
                          rows="3"
                          style={{
                            borderColor: T.border,
                            color: "black",
                            fontFamily: "'Inter', sans-serif",
                          }}
                          autoFocus
                        />
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => saveParagraphEdit(idx)}
                            className="px-3 py-1 rounded text-white text-sm"
                            style={{ background: T.primary }}
                            disabled={loading}
                          >
                            <RiSaveLine size={14} className="inline mr-1" />{" "}
                            Save
                          </button>
                          <button
                            onClick={cancelParagraphEdit}
                            className="px-3 py-1 rounded bg-gray-300 text-sm"
                          >
                            Cancel
                          </button>
                          <span className="text-xs text-gray-500 self-center">
                            💡 You can write multiple sentences – this will be
                            one paragraph.
                          </span>
                        </div>
                      </div>
                    ) : (
                      // ── Display mode ──
                      <>
                        <div className="flex-1">
                          <div
                            className="text-sm"
                            style={{
                              color: T.text,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {para || (
                              <span className="text-gray-400 italic">
                                Empty paragraph
                              </span>
                            )}
                          </div>
                          <div
                            className="text-xs mt-1"
                            style={{ color: T.textMuted }}
                          >
                            Paragraph #{idx + 1}
                          </div>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => startEditParagraph(idx)}
                            className="p-1 rounded hover:bg-gray-200"
                            style={{ color: T.primary }}
                            title="Edit"
                          >
                            <RiEditLine size={16} />
                          </button>
                          <button
                            onClick={() => moveParagraphUp(idx)}
                            className="p-1 rounded hover:bg-gray-200"
                            style={{ color: T.textMuted }}
                            title="Move Up"
                            disabled={idx === 0}
                          >
                            <RiArrowUpLine size={16} />
                          </button>
                          <button
                            onClick={() => moveParagraphDown(idx)}
                            className="p-1 rounded hover:bg-gray-200"
                            style={{ color: T.textMuted }}
                            title="Move Down"
                            disabled={idx === paragraphs.length - 1}
                          >
                            <RiArrowDownLine size={16} />
                          </button>
                          <button
                            onClick={() => deleteParagraph(idx)}
                            className="p-1 rounded hover:bg-red-100"
                            style={{ color: "#EF4444" }}
                            title="Delete"
                          >
                            <RiDeleteBinLine size={16} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* ─── Add new paragraph ─── */}
              <div className="mt-3 flex gap-2">
                <input
                  type="text"
                  value={newParagraph}
                  onChange={(e) => setNewParagraph(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addParagraph()}
                  className="flex-1 p-2 border rounded-lg"
                  style={{ borderColor: T.border, color: "black" }}
                  placeholder="Type new paragraph text and click Add..."
                />
                <button
                  onClick={addParagraph}
                  className="px-4 py-2 rounded-lg flex items-center gap-1"
                  style={{ background: T.primary, color: T.white }}
                  disabled={loading}
                >
                  <RiAddLine size={18} /> Add
                </button>
              </div>
              <p className="text-xs mt-1" style={{ color: T.textMuted }}>
                Each paragraph will appear as a separate block on the frontend.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="text-center py-12 rounded-xl"
          style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
        >
          <RiFileTextLine
            size={48}
            className="mx-auto mb-2"
            style={{ color: "black" }}
          />
          <p style={{ color: "black" }}>No About Us content available.</p>
          <p className="text-sm mt-1" style={{ color: "black" }}>
            Click "Create New Version" or "Load Default" to get started.
          </p>
        </div>
      )}

      {/* ─── ALL VERSIONS LIST ─── */}
      {allAboutData.length > 0 && (
        <div
          className="rounded-xl p-6"
          style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
        >
          <h3
            className="text-lg font-semibold mb-4 flex items-center gap-2"
            style={{ color: T.navy }}
          >
            <RiHistoryLine size={18} /> All Versions ({allAboutData.length})
          </h3>
          <div className="space-y-3">
            {allAboutData.map((item) => (
              <div
                key={item._id}
                className="flex items-center justify-between p-4 rounded-xl transition-all hover:shadow-md"
                style={{
                  background: item.isActive ? `${T.primary}08` : T.offWhite,
                  border: `1px solid ${item.isActive ? T.primary : T.borderLight}`,
                }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold" style={{ color: "black" }}>
                      {item.mainTitle || "Untitled"}
                    </h4>
                    {item.isActive && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: T.primary, color: T.white }}
                      >
                        ACTIVE
                      </span>
                    )}
                    <span className="text-xs" style={{ color: "black" }}>
                      v{item.version || "1.0"}
                    </span>
                    <span className="text-xs" style={{ color: "black" }}>
                      •{" "}
                      {
                        (item.mainDescription || "")
                          .split("\n\n")
                          .filter((p) => p.trim()).length
                      }{" "}
                      paragraphs
                    </span>
                  </div>
                  <p className="text-sm mt-1" style={{ color: "black" }}>
                    Created {new Date(item.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  {!item.isActive && (
                    <button
                      onClick={() => setActiveVersion(item)}
                      className="p-2 rounded-lg transition-all hover:scale-110"
                      style={{ color: "#10B981", background: "#DCFCE7" }}
                      title="Set as Active"
                      disabled={loading}
                    >
                      <RiCheckLine size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => deleteVersion(item._id)}
                    className="p-2 rounded-lg transition-all hover:scale-110"
                    style={{ color: "#EF4444", background: "#FEE2E2" }}
                    title="Delete"
                    disabled={loading}
                  >
                    <RiDeleteBinLine size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};
/* ══════════════════════════════════════════════════════════════════════
   SUBMISSION GUIDELINES TAB
   ══════════════════════════════════════════════════════════════════════ */
const SubmissionGuidelinesTab = ({ token, eventId }) => {
  const [guidelines, setGuidelines] = useState(null);
  const [allGuidelines, setAllGuidelines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "Submission Guidelines",
    isActive: true,
    guidelines: [],
  });
  const [newGuideline, setNewGuideline] = useState({
    title: "",
    description: "",
    icon: "📝",
    order: 0,
  });

  useEffect(() => {
    if (eventId) fetchGuidelines();
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [eventId]);

  useEffect(() => {
    document.body.style.overflow = showForm ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showForm]);

  const fetchGuidelines = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/submission-guidelines/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (data.success) {
        setAllGuidelines(data.data);
        const active = data.data.find((g) => g.isActive);
        if (active) setGuidelines(active);
      }
    } catch (error) {
      console.error("Error fetching submission guidelines:", error);
      toast.error("Failed to load submission guidelines");
    } finally {
      setLoading(false);
    }
  };

  const saveGuidelines = async () => {
    if (!formData.title) {
      toast.error("Title is required");
      return;
    }
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = withEventId(formData, eventId);
      if (editing) {
        await axios.put(
          `${API_BASE}/api/submission-guidelines/${editing._id}`,
          payload,
          config,
        );
        toast.success("Submission guidelines updated");
      } else {
        await axios.post(
          `${API_BASE}/api/submission-guidelines`,
          payload,
          config,
        );
        toast.success("Submission guidelines created");
      }
      await fetchGuidelines();
      setShowForm(false);
      setEditing(null);
      resetForm();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteGuidelines = async (id) => {
    if (!window.confirm("Delete these submission guidelines?")) return;
    try {
      await axios.delete(`${API_BASE}/api/submission-guidelines/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Submission guidelines deleted");
      await fetchGuidelines();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const seedDefaultGuidelines = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/submission-guidelines/seed/event/${eventId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Default submission guidelines seeded!");
      await fetchGuidelines();
    } catch (error) {
      console.error("Error seeding:", error);
      toast.error("Failed to seed");
    } finally {
      setLoading(false);
    }
  };

  const addGuidelineItem = () => {
    if (!newGuideline.title || !newGuideline.description) {
      toast.error("Title and description required");
      return;
    }
    setFormData({
      ...formData,
      guidelines: [
        ...formData.guidelines,
        { ...newGuideline, order: formData.guidelines.length },
      ],
    });
    setNewGuideline({ title: "", description: "", icon: "📝", order: 0 });
    toast.success("Guideline added");
  };

  const updateGuidelineItem = (index, field, value) => {
    const updated = [...formData.guidelines];
    updated[index][field] = value;
    setFormData({ ...formData, guidelines: updated });
  };

  const removeGuidelineItem = (index) => {
    if (window.confirm("Remove this guideline?")) {
      const updated = formData.guidelines.filter((_, i) => i !== index);
      setFormData({ ...formData, guidelines: updated });
      toast.success("Item removed");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "Submission Guidelines",
      isActive: true,
      guidelines: [],
    });
    setNewGuideline({ title: "", description: "", icon: "📝", order: 0 });
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    resetForm();
  };

  if (loading && !guidelines)
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        <p style={{ color: T.textMuted }} className="mt-2">
          Loading...
        </p>
      </div>
    );

  return (
    <>
      {/* ══════════ FORM MODAL ══════════ */}
      {showForm && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeForm();
          }}
        >
          <div
            className="rounded-2xl shadow-2xl w-full flex flex-col"
            style={{
              background: T.white,
              maxWidth: 760,
              maxHeight: "90vh",
              overflow: "hidden",
            }}
          >
            {/* Modal Header */}
            <div
              className="px-8 py-5 flex justify-between items-center flex-shrink-0"
              style={{ background: T.gradPrimary }}
            >
              <h2
                className="text-xl font-bold flex items-center gap-2"
                style={{ color: T.white }}
              >
                <RiFileTextLine size={22} />
                {editing
                  ? "Edit Submission Guidelines"
                  : "Create Submission Guidelines"}
              </h2>
              <button
                onClick={closeForm}
                className="p-1 rounded-full hover:bg-white/20 transition"
                style={{ color: T.white }}
              >
                <RiCloseLine size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-8 space-y-6">
              {/* Title */}
              <div>
                <label
                  className="block mb-2 text-sm font-semibold"
                  style={{ color: T.navy }}
                >
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full rounded-xl p-3 outline-none"
                  style={{
                    background: T.offWhite,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                  }}
                  placeholder="e.g., Submission Guidelines 2026"
                />
              </div>

              {/* Active Toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="w-5 h-5 accent-green-500"
                />
                <span style={{ color: T.textMid }}>
                  Set as Active (visible on website)
                </span>
              </label>

              {/* Add New Guideline Item */}
              <div
                className="rounded-xl p-5 space-y-4"
                style={{
                  background: T.offWhite,
                  border: `1px solid ${T.borderLight}`,
                }}
              >
                <h4 className="font-semibold" style={{ color: T.navy }}>
                  Add Guideline Item
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block mb-1 text-sm"
                      style={{ color: T.textMid }}
                    >
                      Title *
                    </label>
                    <input
                      type="text"
                      value={newGuideline.title}
                      onChange={(e) =>
                        setNewGuideline({
                          ...newGuideline,
                          title: e.target.value,
                        })
                      }
                      onKeyPress={(e) =>
                        e.key === "Enter" && addGuidelineItem()
                      }
                      className="w-full rounded-lg p-3 outline-none"
                      style={{
                        background: T.white,
                        border: `1px solid ${T.border}`,
                        color: T.text,
                      }}
                      placeholder="e.g., Abstract Format"
                    />
                  </div>
                  <div>
                    <label
                      className="block mb-1 text-sm"
                      style={{ color: T.textMid }}
                    >
                      Icon (emoji)
                    </label>
                    <input
                      type="text"
                      value={newGuideline.icon}
                      onChange={(e) =>
                        setNewGuideline({
                          ...newGuideline,
                          icon: e.target.value,
                        })
                      }
                      className="w-full rounded-lg p-3 outline-none"
                      style={{
                        background: T.white,
                        border: `1px solid ${T.border}`,
                        color: T.text,
                      }}
                      placeholder="📝"
                    />
                  </div>
                  <div>
                    <label
                      className="block mb-1 text-sm"
                      style={{ color: T.textMid }}
                    >
                      Order
                    </label>
                    <input
                      type="number"
                      value={newGuideline.order}
                      onChange={(e) =>
                        setNewGuideline({
                          ...newGuideline,
                          order: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full rounded-lg p-3 outline-none"
                      style={{
                        background: T.white,
                        border: `1px solid ${T.border}`,
                        color: T.text,
                      }}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label
                      className="block mb-1 text-sm"
                      style={{ color: T.textMid }}
                    >
                      Description *
                    </label>
                    <textarea
                      value={newGuideline.description}
                      onChange={(e) =>
                        setNewGuideline({
                          ...newGuideline,
                          description: e.target.value,
                        })
                      }
                      className="w-full rounded-lg p-3 outline-none resize-none"
                      rows="3"
                      style={{
                        background: T.white,
                        border: `1px solid ${T.border}`,
                        color: T.text,
                      }}
                      placeholder="Detailed description of this guideline..."
                    />
                  </div>
                </div>
                <button
                  onClick={addGuidelineItem}
                  className="px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition hover:scale-105"
                  style={{ background: `${T.primary}20`, color: T.primary }}
                >
                  <RiAddLine size={16} /> Add Guideline
                </button>
              </div>

              {/* Added Guidelines List */}
              {formData.guidelines.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold" style={{ color: T.navy }}>
                    Added Guidelines ({formData.guidelines.length})
                  </h4>
                  {[...formData.guidelines]
                    .sort((a, b) => a.order - b.order)
                    .map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-4 rounded-xl"
                        style={{
                          background: T.offWhite,
                          border: `1px solid ${T.borderLight}`,
                        }}
                      >
                        <span className="text-2xl flex-shrink-0">
                          {item.icon}
                        </span>
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <input
                              type="text"
                              value={item.title}
                              onChange={(e) =>
                                updateGuidelineItem(
                                  idx,
                                  "title",
                                  e.target.value,
                                )
                              }
                              className="font-semibold rounded-lg px-2 py-1 text-sm outline-none flex-1"
                              style={{
                                background: T.white,
                                border: `1px solid ${T.border}`,
                                color: T.text,
                                minWidth: 120,
                              }}
                            />
                            <span
                              className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                              style={{
                                background: `${T.primary}15`,
                                color: T.primary,
                              }}
                            >
                              #{item.order}
                            </span>
                          </div>
                          <textarea
                            value={item.description}
                            onChange={(e) =>
                              updateGuidelineItem(
                                idx,
                                "description",
                                e.target.value,
                              )
                            }
                            className="w-full rounded-lg px-2 py-1 text-sm outline-none resize-none"
                            rows="2"
                            style={{
                              background: T.white,
                              border: `1px solid ${T.border}`,
                              color: T.textMid,
                            }}
                          />
                        </div>
                        <button
                          onClick={() => removeGuidelineItem(idx)}
                          className="p-1 rounded flex-shrink-0 hover:bg-red-50 transition"
                          style={{ color: "#EF4444" }}
                        >
                          <RiDeleteBinLine size={16} />
                        </button>
                      </div>
                    ))}
                </div>
              )}

              {formData.guidelines.length === 0 && (
                <p
                  className="text-center py-4 text-sm"
                  style={{ color: T.textMuted }}
                >
                  No guideline items added yet. Use the form above to add items.
                </p>
              )}
            </div>

            {/* Modal Footer */}
            <div
              className="px-8 py-5 flex justify-end gap-3 flex-shrink-0"
              style={{
                borderTop: `1px solid ${T.borderLight}`,
                background: T.offWhite,
              }}
            >
              <button
                onClick={closeForm}
                className="px-6 py-2 rounded-xl transition hover:bg-gray-200"
                style={{ background: "#E5E7EB", color: T.textMid }}
              >
                Cancel
              </button>
              <button
                onClick={saveGuidelines}
                disabled={loading}
                className="px-6 py-2 rounded-xl flex items-center gap-2 transition hover:opacity-90"
                style={{ background: T.gradPrimary, color: T.white }}
              >
                <RiSaveLine size={18} />
                {loading
                  ? "Saving..."
                  : editing
                    ? "Update Guidelines"
                    : "Create Guidelines"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ MAIN CONTENT ══════════ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => {
              setShowForm(true);
              setEditing(null);
              resetForm();
            }}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition-all hover:scale-105"
            style={{ background: T.gradPrimary, color: T.white }}
          >
            <RiAddLine size={18} /> Create Submission Guidelines
          </button>
          <button
            onClick={seedDefaultGuidelines}
            disabled={loading}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition-all hover:scale-105"
            style={{ background: "#4CAF50", color: T.white }}
          >
            <RiFileTextLine size={18} /> Load Default
          </button>
        </div>

        {/* Active Guidelines Preview */}
        {guidelines && (
          <div
            className="rounded-xl overflow-hidden shadow-lg"
            style={{
              background: T.white,
              border: `1px solid ${T.borderLight}`,
            }}
          >
            <div className="px-6 py-4" style={{ background: T.gradPrimary }}>
              <div className="flex justify-between items-start flex-wrap gap-4">
                <div>
                  <h3
                    className="text-xl font-bold flex items-center gap-2"
                    style={{ color: T.white }}
                  >
                    <RiFileTextLine size={24} /> {guidelines.title}
                  </h3>
                  <p className="text-xs mt-2" style={{ color: `${T.white}99` }}>
                    {guidelines.guidelines?.length || 0} Guidelines • Active
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditing(guidelines);
                    setFormData(guidelines);
                    setShowForm(true);
                  }}
                  className="px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all hover:scale-105"
                  style={{ background: T.white, color: T.primary }}
                >
                  <RiEditLine size={16} /> Edit Guidelines
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {guidelines.guidelines
                  ?.sort((a, b) => a.order - b.order)
                  .map((item, idx) => (
                    <div
                      key={idx}
                      className="rounded-xl p-4 transition-all hover:shadow-md hover:-translate-y-1 cursor-pointer"
                      style={{
                        background: T.offWhite,
                        borderLeft: `4px solid ${T.primary}`,
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-3xl">{item.icon || "📝"}</div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <h4
                              className="font-semibold"
                              style={{ color: T.text }}
                            >
                              {item.title}
                            </h4>
                            <span
                              className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                              style={{
                                background: `${T.primary}15`,
                                color: T.primary,
                              }}
                            >
                              #{item.order}
                            </span>
                          </div>
                          <p
                            className="text-sm mt-1"
                            style={{ color: T.textMuted }}
                          >
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* All Guidelines List */}
        <div
          className="rounded-xl p-6"
          style={{
            background: T.white,
            border: `1px solid ${T.borderLight}`,
            boxShadow: T.shadow,
          }}
        >
          <h3
            className="text-lg font-semibold mb-4 flex items-center gap-2"
            style={{ color: T.navy }}
          >
            <RiFileTextLine /> All Submission Guidelines Sets
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {allGuidelines.map((g) => (
              <div
                key={g._id}
                className="flex items-center justify-between p-4 rounded-xl transition-all hover:shadow-md"
                style={{
                  background: g.isActive ? `${T.primary}08` : T.offWhite,
                  border: `1px solid ${g.isActive ? T.primary : T.borderLight}`,
                }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold" style={{ color: T.text }}>
                      {g.title}
                    </h4>
                    {g.isActive && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: T.primary, color: T.white }}
                      >
                        ACTIVE
                      </span>
                    )}
                  </div>
                  <p className="text-sm mt-1" style={{ color: T.textMuted }}>
                    {g.guidelines?.length || 0} guidelines •{" "}
                    {new Date(g.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => {
                      setEditing(g);
                      setFormData(g);
                      setShowForm(true);
                    }}
                    className="p-2 rounded-lg transition-all hover:scale-110"
                    style={{ color: T.primary, background: `${T.primary}15` }}
                    title="Edit"
                  >
                    <RiEditLine size={16} />
                  </button>
                  <button
                    onClick={() => deleteGuidelines(g._id)}
                    className="p-2 rounded-lg transition-all hover:scale-110"
                    style={{ color: "#EF4444", background: "#FEE2E2" }}
                    title="Delete"
                  >
                    <RiDeleteBinLine size={16} />
                  </button>
                </div>
              </div>
            ))}
            {allGuidelines.length === 0 && (
              <div
                className="col-span-2 text-center py-8 rounded-xl"
                style={{ background: T.offWhite }}
              >
                <RiFileTextLine
                  size={48}
                  className="mx-auto mb-2"
                  style={{ color: T.textMuted }}
                />
                <p style={{ color: T.textMuted }}>
                  No submission guidelines created yet.
                </p>
                <p className="text-sm mt-1" style={{ color: T.textMuted }}>
                  Click "Create Submission Guidelines" to get started.
                </p>
              </div>
            )}
          </div>
        </div>

       
      </motion.div>
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   PRESENTATION GUIDELINES TAB
   ══════════════════════════════════════════════════════════════════════ */
const PresentationGuidelinesTab = ({ token, eventId }) => {
  const [guidelines, setGuidelines] = useState(null);
  const [allGuidelines, setAllGuidelines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "Presentation Guidelines",
    description: "Important guidelines for presenters",
    isActive: true,
    guidelines: [],
  });
  const [newGuideline, setNewGuideline] = useState({
    title: "",
    description: "",
    icon: "📌",
    order: 0,
  });

  useEffect(() => {
    if (eventId) fetchGuidelines();
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [eventId]);

  useEffect(() => {
    if (showForm) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showForm]);

  const fetchGuidelines = async () => {
    if (!eventId) {
      console.error("Event ID is missing");
      toast.error("Event ID is required");
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/presentation-guidelines/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (data.success) {
        setAllGuidelines(data.data);
        const active = data.data.find((g) => g.isActive);
        if (active) setGuidelines(active);
      }
    } catch (error) {
      console.error("Error fetching presentation guidelines:", error);
      toast.error("Failed to load presentation guidelines");
    } finally {
      setLoading(false);
    }
  };

  const saveGuidelines = async () => {
    if (!formData.title) {
      toast.error("Title is required");
      return;
    }
    if (!eventId) {
      toast.error("Event ID is missing");
      return;
    }
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { ...formData, eventId };

      if (editing) {
        await axios.put(
          `${API_BASE}/api/presentation-guidelines/${editing._id}`,
          payload,
          config,
        );
        toast.success("Presentation guidelines updated");
      } else {
        await axios.post(
          `${API_BASE}/api/presentation-guidelines`,
          payload,
          config,
        );
        toast.success("Presentation guidelines created");
      }
      fetchGuidelines();
      setShowForm(false);
      setEditing(null);
      resetForm();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteGuidelines = async (id) => {
    if (!window.confirm("Delete these presentation guidelines?")) return;
    try {
      await axios.delete(`${API_BASE}/api/presentation-guidelines/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Presentation guidelines deleted");
      fetchGuidelines();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const seedDefaultGuidelines = async () => {
    if (!eventId) {
      toast.error("Event ID is missing");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/presentation-guidelines/seed/event/${eventId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Default presentation guidelines seeded!");
      fetchGuidelines();
    } catch (error) {
      console.error("Error seeding:", error);
      toast.error("Failed to seed");
    } finally {
      setLoading(false);
    }
  };

  const addGuidelineItem = () => {
    if (!newGuideline.title || !newGuideline.description) {
      toast.error("Title and description required");
      return;
    }
    setFormData({
      ...formData,
      guidelines: [
        ...formData.guidelines,
        {
          ...newGuideline,
          order: formData.guidelines.length,
        },
      ],
    });
    setNewGuideline({ title: "", description: "", icon: "📌", order: 0 });
    toast.success("Guideline added");
  };

  const updateGuidelineItem = (index, field, value) => {
    const updated = [...formData.guidelines];
    updated[index][field] = value;
    setFormData({ ...formData, guidelines: updated });
  };

  const removeGuidelineItem = (index) => {
    if (window.confirm("Remove this guideline?")) {
      const updated = formData.guidelines.filter((_, i) => i !== index);
      setFormData({ ...formData, guidelines: updated });
      toast.success("Item removed");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "Presentation Guidelines",
      description: "Important guidelines for presenters",
      isActive: true,
      guidelines: [],
    });
  };

  if (loading && !guidelines)
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        <p style={{ color: T.textMuted }} className="mt-2">
          Loading...
        </p>
      </div>
    );

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => {
              setShowForm(true);
              setEditing(null);
              resetForm();
            }}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition-all hover:scale-105"
            style={{ background: T.gradPrimary, color: T.white }}
          >
            <RiAddLine size={18} /> Create Presentation Guidelines
          </button>
          <button
            onClick={seedDefaultGuidelines}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition-all hover:scale-105"
            style={{ background: "#4CAF50", color: T.white }}
          >
            <RiFileTextLine size={18} /> Load Default
          </button>
        </div>

        {/* Modal Form */}
        {showForm && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          >
            <div
              style={{
                backgroundColor: T.white,
                borderRadius: "16px",
                maxWidth: "700px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                padding: "24px",
                position: "relative",
              }}
            >
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  marginBottom: "20px",
                  color: T.navy,
                }}
              >
                {editing ? "Edit" : "Create"} Presentation Guidelines
              </h3>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "black",
                  }}
                >
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: `1px solid ${T.border}`,
                    fontSize: "14px",
                    color: "black",
                  }}
                  placeholder="Enter guidelines title"
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "black",
                  }}
                >
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: `1px solid ${T.border}`,
                    fontSize: "14px",
                    minHeight: "80px",
                    color: "black",
                  }}
                  placeholder="Enter description"
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                  />
                  <span style={{ fontWeight: "600", color: "black" }}>
                    Set as Active Guidelines
                  </span>
                </label>
              </div>

              {/* Guidelines Items Section */}
              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    fontWeight: "700",
                    marginBottom: "16px",
                    color: "black",
                    fontSize: "16px",
                  }}
                >
                  Guidelines Items
                </h4>

                {formData.guidelines.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      marginBottom: "16px",
                      padding: "16px",
                      backgroundColor: T.offWhite,
                      borderRadius: "10px",
                      border: `1px solid ${T.borderLight}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: T.primary,
                        }}
                      >
                        Guideline #{idx + 1}
                      </span>
                      <button
                        onClick={() => removeGuidelineItem(idx)}
                        style={{
                          color: "#EF4444",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px",
                        }}
                      >
                        <RiDeleteBinLine size={18} />
                      </button>
                    </div>

                    <input
                      type="text"
                      placeholder="Guideline Title"
                      value={item.title}
                      onChange={(e) =>
                        updateGuidelineItem(idx, "title", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        marginBottom: "10px",
                        borderRadius: "6px",
                        border: `1px solid ${T.border}`,
                        fontSize: "14px",
                        color: "black",
                      }}
                    />

                    <textarea
                      placeholder="Guideline Description"
                      value={item.description}
                      onChange={(e) =>
                        updateGuidelineItem(idx, "description", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: `1px solid ${T.border}`,
                        fontSize: "14px",
                        minHeight: "60px",
                        color: "black",
                      }}
                    />

                    <div style={{ marginTop: "8px" }}>
                      <label style={{ fontSize: "12px", color: "black" }}>
                        Icon (Emoji)
                      </label>
                      <input
                        type="text"
                        placeholder="📌"
                        value={item.icon || "📌"}
                        onChange={(e) =>
                          updateGuidelineItem(idx, "icon", e.target.value)
                        }
                        style={{
                          width: "60px",
                          padding: "6px",
                          marginTop: "4px",
                          borderRadius: "6px",
                          border: `1px solid ${T.border}`,
                          fontSize: "16px",
                          textAlign: "center",
                          color: "black",
                        }}
                      />
                    </div>
                  </div>
                ))}

                {/* Add New Guideline Form */}
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    backgroundColor: T.softTeal,
                    borderRadius: "10px",
                    border: `1px dashed ${T.primary}`,
                  }}
                >
                  <h5
                    style={{
                      fontWeight: "600",
                      marginBottom: "12px",
                      color: "black",
                      fontSize: "14px",
                    }}
                  >
                    Add New Guideline
                  </h5>
                  <input
                    type="text"
                    placeholder="Guideline Title"
                    value={newGuideline.title}
                    onChange={(e) =>
                      setNewGuideline({
                        ...newGuideline,
                        title: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      marginBottom: "10px",
                      borderRadius: "6px",
                      border: `1px solid ${T.border}`,
                      fontSize: "14px",
                      color: "black",
                    }}
                  />
                  <textarea
                    placeholder="Guideline Description"
                    value={newGuideline.description}
                    onChange={(e) =>
                      setNewGuideline({
                        ...newGuideline,
                        description: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      marginBottom: "10px",
                      borderRadius: "6px",
                      border: `1px solid ${T.border}`,
                      fontSize: "14px",
                      minHeight: "60px",
                      color: "black",
                    }}
                  />
                  <button
                    onClick={addGuidelineItem}
                    style={{
                      background: T.primary,
                      color: T.white,
                      padding: "8px 16px",
                      borderRadius: "6px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    + Add Guideline
                  </button>
                </div>
              </div>

              {/* Form Actions */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                  marginTop: "24px",
                  paddingTop: "20px",
                  borderTop: `1px solid ${T.border}`,
                }}
              >
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditing(null);
                    resetForm();
                  }}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: `1px solid ${T.border}`,
                    backgroundColor: T.white,
                    color: "black",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveGuidelines}
                  disabled={loading}
                  style={{
                    background: T.gradPrimary,
                    color: T.white,
                    padding: "10px 24px",
                    borderRadius: "8px",
                    border: "none",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? "Saving..." : editing ? "Update" : "Create"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Active Guidelines Display */}
        {guidelines && (
          <div
            className="rounded-xl overflow-hidden shadow-lg"
            style={{
              background: T.white,
              border: `1px solid ${T.borderLight}`,
            }}
          >
            <div className="px-6 py-4" style={{ background: T.gradPrimary }}>
              <div className="flex justify-between items-start flex-wrap gap-4">
                <div>
                  <h3
                    className="text-xl font-bold flex items-center gap-2"
                    style={{ color: T.white }}
                  >
                    <RiFileTextLine size={24} /> {guidelines.title}
                  </h3>
                  <p className="text-sm mt-1" style={{ color: `${T.white}CC` }}>
                    {guidelines.description}
                  </p>
                  <p className="text-xs mt-2" style={{ color: `${T.white}99` }}>
                    {guidelines.guidelines?.length || 0} Guidelines • Active
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditing(guidelines);
                    setFormData(guidelines);
                    setShowForm(true);
                  }}
                  className="px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all hover:scale-105"
                  style={{ background: T.white, color: T.primary }}
                >
                  <RiEditLine size={16} /> Edit Guidelines
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {guidelines.guidelines
                  ?.sort((a, b) => a.order - b.order)
                  .map((item, idx) => (
                    <div
                      key={idx}
                      className="rounded-xl p-4 transition-all hover:shadow-md hover:-translate-y-1 cursor-pointer"
                      style={{
                        background: T.offWhite,
                        borderLeft: `4px solid ${T.primary}`,
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-3xl">{item.icon || "📌"}</div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4
                              className="font-semibold"
                              style={{ color: "black" }}
                            >
                              {item.title}
                            </h4>
                            <span
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{
                                background: `${T.primary}15`,
                                color: T.primary,
                              }}
                            >
                              #{item.order}
                            </span>
                          </div>
                          <p
                            className="text-sm mt-1"
                            style={{ color: "black" }}
                          >
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* All Guidelines Sets */}
        <div
          className="rounded-xl p-6"
          style={{
            background: T.white,
            border: `1px solid ${T.borderLight}`,
            boxShadow: T.shadow,
          }}
        >
          <h3
            className="text-lg font-semibold mb-4 flex items-center gap-2"
            style={{ color: T.navy }}
          >
            <RiFileTextLine /> All Presentation Guidelines Sets
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {allGuidelines.map((g) => (
              <div
                key={g._id}
                className="flex items-center justify-between p-4 rounded-xl transition-all hover:shadow-md"
                style={{
                  background: g.isActive ? `${T.primary}08` : T.offWhite,
                  border: `1px solid ${g.isActive ? T.primary : T.borderLight}`,
                }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold" style={{ color: "black" }}>
                      {g.title}
                    </h4>
                    {g.isActive && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: T.primary, color: T.white }}
                      >
                        ACTIVE
                      </span>
                    )}
                  </div>
                  <p className="text-sm mt-1" style={{ color: "black" }}>
                    {g.guidelines?.length || 0} guidelines •{" "}
                    {new Date(g.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditing(g);
                      setFormData(g);
                      setShowForm(true);
                    }}
                    className="p-2 rounded-lg transition-all hover:scale-110"
                    style={{ color: T.primary, background: `${T.primary}15` }}
                    title="Edit"
                  >
                    <RiEditLine size={16} />
                  </button>
                  <button
                    onClick={() => deleteGuidelines(g._id)}
                    className="p-2 rounded-lg transition-all hover:scale-110"
                    style={{ color: "#EF4444", background: "#FEE2E2" }}
                    title="Delete"
                  >
                    <RiDeleteBinLine size={16} />
                  </button>
                </div>
              </div>
            ))}
            {allGuidelines.length === 0 && (
              <div
                className="col-span-2 text-center py-8 rounded-xl"
                style={{ background: T.offWhite }}
              >
                <RiFileTextLine
                  size={48}
                  className="mx-auto mb-2"
                  style={{ color: T.textMuted }}
                />
                <p style={{ color: "black" }}>
                  No presentation guidelines created yet.
                </p>
                <p className="text-sm mt-1" style={{ color: "black" }}>
                  Click "Create Presentation Guidelines" to get started.
                </p>
              </div>
            )}
          </div>
        </div>

     
      </motion.div>
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   REGISTRATION FEE TAB
   ══════════════════════════════════════════════════════════════════════ */
const RegistrationFeeTab = ({ token, eventId }) => {
  const [feeData, setFeeData] = useState(null);
  const [allFeeData, setAllFeeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  const [formData, setFormData] = useState({
    title: "Registration Fee",
    subtitle:
      "Choose your preferred registration category and secure your participation",
    conferenceName: "Meetings21 Immunology Summit 2026",
    location: "Hyderabad International Convention Centre, India",
    currency: "USD",
    superEarlyDeadline: "",
    earlyDeadline: "",
    regularDeadline: "",
    isActive: true,
    categories: [],
    deadlines: [],
    importantNotes: [],
    version: "2026",
  });
  const [newCategory, setNewCategory] = useState({
    name: "",
    superEarlyPrice: 0,
    earlyPrice: 0,
    regularPrice: 0,
    benefits: [],
    order: 0,
    isActive: true,
  });
  const [newBenefit, setNewBenefit] = useState("");
  const [editingCategoryIndex, setEditingCategoryIndex] = useState(null);
  const [newDeadline, setNewDeadline] = useState({
    title: "",
    date: "",
    isUrgent: false,
    isActive: true,
  });
  const [editingDeadlineIndex, setEditingDeadlineIndex] = useState(null);
  const [newNote, setNewNote] = useState({
    message: "",
    severity: "info",
    order: 0,
    isActive: true,
  });
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);

  // ── Step wizard state ──
  const [step, setStep] = useState(0);
  const totalSteps = 4;

  // ── Ref for scrolling to form ──
  const formRef = useRef(null);

  // ── Scroll helper ──
  const scrollToForm = () => {
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  const severityOptions = [
    {
      value: "error",
      label: "Error",
      color: "#EF4444",
      bg: "#FEE2E2",
      icon: "🔴",
    },
    {
      value: "warning",
      label: "Warning",
      color: "#F59E0B",
      bg: "#FEF3C7",
      icon: "⚠️",
    },
    {
      value: "info",
      label: "Info",
      color: "#3B82F6",
      bg: "#EBF1FF",
      icon: "ℹ️",
    },
    {
      value: "success",
      label: "Success",
      color: "#10B981",
      bg: "#DCFCE7",
      icon: "✅",
    },
  ];

  useEffect(() => {
    if (eventId) fetchRegistrationFee();
  }, [eventId]);

  const fetchRegistrationFee = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/registration-fee/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (data.success) {
        setAllFeeData(data.data);
        const active = data.data.find((f) => f.isActive);
        if (active) setFeeData(active);
      }
    } catch (error) {
      console.error("Error fetching registration fee:", error);
      toast.error("Failed to load registration fee data");
    } finally {
      setLoading(false);
    }
  };

  const deactivateOtherVersions = async (excludeId) => {
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/registration-fee/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (data.success) {
        const others = data.data.filter(
          (f) => f._id !== excludeId && f.isActive === true,
        );
        for (const other of others) {
          await axios.put(
            `${API_BASE}/api/registration-fee/${other._id}`,
            { ...other, isActive: false, eventId },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          console.log(`Deactivated version: ${other._id}`);
        }
      }
    } catch (error) {
      console.error("Error deactivating other versions:", error);
    }
  };

  const saveRegistrationFee = async () => {
    if (!formData.title) {
      toast.error("Title is required");
      return;
    }
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { ...formData, eventId };

      if (payload.isActive) {
        const excludeId = editing ? editing._id : null;
        await deactivateOtherVersions(excludeId);
      }

      let response;
      if (editing) {
        response = await axios.put(
          `${API_BASE}/api/registration-fee/${editing._id}`,
          payload,
          config,
        );
        toast.success("Registration fee data updated");
      } else {
        response = await axios.post(
          `${API_BASE}/api/registration-fee`,
          payload,
          config,
        );
        toast.success("Registration fee data created");
      }
      fetchRegistrationFee();
      setShowForm(false);
      setEditing(null);
      resetForm();
      setStep(0);
    } catch (error) {
      console.error("Error saving:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteRegistrationFee = async (id) => {
    if (!window.confirm("Delete this registration fee data?")) return;
    try {
      await axios.delete(`${API_BASE}/api/registration-fee/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Registration fee data deleted");
      fetchRegistrationFee();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const seedDefaultRegistrationFee = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/registration-fee/seed/event/${eventId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Default registration fee data seeded!");
      fetchRegistrationFee();
    } catch (error) {
      console.error("Error seeding:", error);
      toast.error("Failed to seed");
    } finally {
      setLoading(false);
    }
  };

  const isDeadlineExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };
  const getExpiryStatus = (date) => {
    if (!date)
      return {
        status: "not_set",
        text: "Not Set",
        color: "#6B7FA3",
        bg: "#F1F5F9",
      };
    const expired = isDeadlineExpired(date);
    if (expired)
      return {
        status: "expired",
        text: "EXPIRED",
        color: "#EF4444",
        bg: "#FEE2E2",
      };
    return {
      status: "active",
      text: "ACTIVE",
      color: "#10B981",
      bg: "#DCFCE7",
    };
  };
  const formatDeadline = (date) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // ── Category CRUD ──
  const addCategory = () => {
    if (!newCategory.name) {
      toast.error("Category name is required");
      return;
    }
    if (
      newCategory.superEarlyPrice <= 0 &&
      newCategory.earlyPrice <= 0 &&
      newCategory.regularPrice <= 0
    ) {
      toast.error("At least one price must be set");
      return;
    }
    setFormData({
      ...formData,
      categories: [
        ...formData.categories,
        { ...newCategory, order: formData.categories.length },
      ],
    });
    resetCategoryForm();
    toast.success("Category added");
  };
  const updateCategory = (index) => {
    const updated = [...formData.categories];
    updated[index] = { ...updated[index], ...newCategory };
    setFormData({ ...formData, categories: updated });
    resetCategoryForm();
    setEditingCategoryIndex(null);
    toast.success("Category updated");
  };
  const removeCategory = (index) => {
    if (window.confirm("Remove this category?")) {
      const updated = formData.categories.filter((_, i) => i !== index);
      setFormData({ ...formData, categories: updated });
      toast.success("Category removed");
    }
  };
  const addBenefit = () => {
    if (newBenefit.trim()) {
      setNewCategory({
        ...newCategory,
        benefits: [...newCategory.benefits, newBenefit.trim()],
      });
      setNewBenefit("");
    }
  };
  const removeBenefit = (index) => {
    const updated = newCategory.benefits.filter((_, i) => i !== index);
    setNewCategory({ ...newCategory, benefits: updated });
  };
  const resetCategoryForm = () => {
    setNewCategory({
      name: "",
      superEarlyPrice: 0,
      earlyPrice: 0,
      regularPrice: 0,
      benefits: [],
      order: formData.categories.length,
      isActive: true,
    });
    setNewBenefit("");
  };
  const editCategory = (index) => {
    setNewCategory(formData.categories[index]);
    setEditingCategoryIndex(index);
  };

  // ── Deadline CRUD ──
  const addDeadline = () => {
    if (!newDeadline.title || !newDeadline.date) {
      toast.error("Deadline title and date are required");
      return;
    }
    setFormData({
      ...formData,
      deadlines: [...formData.deadlines, { ...newDeadline }],
    });
    setNewDeadline({ title: "", date: "", isUrgent: false, isActive: true });
    toast.success("Deadline added");
  };
  const updateDeadlineItem = (index) => {
    const updated = [...formData.deadlines];
    updated[index] = { ...updated[index], ...newDeadline };
    setFormData({ ...formData, deadlines: updated });
    setNewDeadline({ title: "", date: "", isUrgent: false, isActive: true });
    setEditingDeadlineIndex(null);
    toast.success("Deadline updated");
  };
  const removeDeadline = (index) => {
    if (window.confirm("Remove this deadline?")) {
      const updated = formData.deadlines.filter((_, i) => i !== index);
      setFormData({ ...formData, deadlines: updated });
      toast.success("Deadline removed");
    }
  };
  const editDeadline = (index) => {
    setNewDeadline(formData.deadlines[index]);
    setEditingDeadlineIndex(index);
  };

  // ── Note CRUD ──
  const addNote = () => {
    if (!newNote.message) {
      toast.error("Note message is required");
      return;
    }
    setFormData({
      ...formData,
      importantNotes: [
        ...formData.importantNotes,
        { ...newNote, order: formData.importantNotes.length },
      ],
    });
    setNewNote({ message: "", severity: "info", order: 0, isActive: true });
    toast.success("Note added");
  };
  const updateNoteItem = (index) => {
    const updated = [...formData.importantNotes];
    updated[index] = { ...updated[index], ...newNote };
    setFormData({ ...formData, importantNotes: updated });
    setNewNote({ message: "", severity: "info", order: 0, isActive: true });
    setEditingNoteIndex(null);
    toast.success("Note updated");
  };
  const removeNote = (index) => {
    if (window.confirm("Remove this note?")) {
      const updated = formData.importantNotes.filter((_, i) => i !== index);
      setFormData({ ...formData, importantNotes: updated });
      toast.success("Note removed");
    }
  };
  const editNote = (index) => {
    setNewNote(formData.importantNotes[index]);
    setEditingNoteIndex(index);
  };

  const resetForm = () => {
    setFormData({
      title: "Registration Fee",
      subtitle:
        "Choose your preferred registration category and secure your participation",
      conferenceName: "Meetings21 Immunology Summit 2026",
      location: "Hyderabad International Convention Centre, India",
      currency: "USD",
      superEarlyDeadline: "",
      earlyDeadline: "",
      regularDeadline: "",
      isActive: true,
      categories: [],
      deadlines: [],
      importantNotes: [],
      version: "2026",
    });
    setEditing(null);
    resetCategoryForm();
    setNewDeadline({ title: "", date: "", isUrgent: false, isActive: true });
    setNewNote({ message: "", severity: "info", order: 0, isActive: true });
    setStep(0);
  };

  const isBasicComplete = () => formData.title && formData.title.trim() !== "";
  const isCategoriesComplete = () =>
    formData.categories && formData.categories.length > 0;
  const isDeadlinesComplete = () =>
    formData.deadlines && formData.deadlines.length > 0;
  const isNotesComplete = () =>
    formData.importantNotes && formData.importantNotes.length > 0;

  const nextStep = () => {
    if (step === 0 && !isBasicComplete()) {
      toast.error("Please fill in all basic information");
      return;
    }
    if (step === 1 && !isCategoriesComplete()) {
      toast.error("Please add at least one registration category");
      return;
    }
    if (step === 2 && !isDeadlinesComplete()) {
      toast.error("Please add at least one deadline");
      return;
    }
    setStep(step + 1);
  };
  const prevStep = () => setStep(step - 1);
  const canSave =
    isBasicComplete() &&
    isCategoriesComplete() &&
    isDeadlinesComplete() &&
    isNotesComplete();

  if (loading && !feeData)
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        <p style={{ color: "black" }} className="mt-2">
          Loading...
        </p>
      </div>
    );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* ─── HEADER (white text) ─── */}
      <div
        className="flex justify-between items-center flex-wrap gap-4"
        style={{ color: "white" }}
      >
        <div>
          <h3 className="text-lg font-semibold" style={{ color: "white" }}>
            <RiMoneyDollarCircleLine className="inline mr-2" /> Registration Fee
            Management
          </h3>
          <p className="text-sm mt-1" style={{ color: "white" }}>
            Manage registration categories, pricing tiers, deadlines, and
            important notes
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (!showForm) {
                resetForm();
                setEditing(null);
                setActiveSection("basic");
                setStep(0);
                scrollToForm();
              }
            }}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition"
            style={{ background: T.gradPrimary, color: T.white }}
          >
            {showForm ? <RiCloseLine size={18} /> : <RiAddLine size={18} />}
            {showForm ? "Close Form" : "Create Registration Fee"}
          </button>
        </div>
      </div>

      {/* ─── FULL FORM ─── */}
      {showForm && (
        <div
          ref={formRef}
          className="rounded-xl p-6"
          style={{
            background: T.white,
            border: `1px solid ${T.borderLight}`,
            boxShadow: T.shadow,
          }}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold" style={{ color: T.navy }}>
              {editing
                ? "Edit Registration Fee"
                : "Create New Registration Fee"}
            </h3>
            <button
              onClick={() => setShowForm(false)}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <RiCloseLine size={20} />
            </button>
          </div>

          {/* ── Step progress ── */}
          <div
            className="flex items-center gap-4 mb-6 pb-2 border-b"
            style={{ borderColor: T.borderLight }}
          >
            {["Basic Info", "Categories", "Deadlines", "Important Notes"].map(
              (label, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === idx ? "bg-blue-600 text-white" : step > idx ? "bg-green-500 text-white" : "bg-gray-200 text-gray-600"}`}
                  >
                    {step > idx ? <RiCheckLine size={16} /> : idx + 1}
                  </div>
                  <span
                    className={`text-sm ${step === idx ? "font-bold text-blue-600" : step > idx ? "text-green-600" : "text-gray-500"}`}
                  >
                    {label}
                  </span>
                </div>
              ),
            )}
          </div>

          {/* ─── STEP 0: BASIC INFO ─── */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: "black" }}
                  >
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: "black" }}
                  >
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) =>
                      setFormData({ ...formData, currency: e.target.value })
                    }
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="INR">INR (₹)</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: "black" }}
                  >
                    Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) =>
                      setFormData({ ...formData, subtitle: e.target.value })
                    }
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: "black" }}
                  >
                    Conference Name
                  </label>
                  <input
                    type="text"
                    value={formData.conferenceName}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        conferenceName: e.target.value,
                      })
                    }
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: "black" }}
                  >
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: "black" }}
                  >
                    Edition / Year
                  </label>
                  <input
                    type="text"
                    value={formData.version}
                    onChange={(e) =>
                      setFormData({ ...formData, version: e.target.value })
                    }
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                    placeholder="e.g., 2026"
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: "black" }}
                  >
                    Super Early Bird Deadline
                  </label>
                  <input
                    type="datetime-local"
                    value={formatDateForInput(formData.superEarlyDeadline)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        superEarlyDeadline: e.target.value,
                      })
                    }
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: "black" }}
                  >
                    Early Bird Deadline
                  </label>
                  <input
                    type="datetime-local"
                    value={formatDateForInput(formData.earlyDeadline)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        earlyDeadline: e.target.value,
                      })
                    }
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: "black" }}
                  >
                    Regular Deadline
                  </label>
                  <input
                    type="datetime-local"
                    value={formatDateForInput(formData.regularDeadline)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        regularDeadline: e.target.value,
                      })
                    }
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="w-4 h-4"
                  />
                  <label
                    className="text-sm font-medium"
                    style={{ color: "black" }}
                  >
                    Set as Active (Visible to users)
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* ─── STEP 1: CATEGORIES ─── */}
          {step === 1 && (
            <div className="space-y-6">
              <div
                className="p-4 rounded-lg"
                style={{ background: T.offWhite }}
              >
                <h4 className="font-semibold mb-3" style={{ color: "black" }}>
                  {editingCategoryIndex !== null
                    ? "Edit Category"
                    : "Add New Category"}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Category Name (e.g., Student, Regular, Industry)"
                    value={newCategory.name}
                    onChange={(e) =>
                      setNewCategory({ ...newCategory, name: e.target.value })
                    }
                    className="p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                  <input
                    type="number"
                    placeholder="Super Early Price"
                    value={newCategory.superEarlyPrice}
                    onChange={(e) =>
                      setNewCategory({
                        ...newCategory,
                        superEarlyPrice: parseFloat(e.target.value),
                      })
                    }
                    className="p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                  <input
                    type="number"
                    placeholder="Early Price"
                    value={newCategory.earlyPrice}
                    onChange={(e) =>
                      setNewCategory({
                        ...newCategory,
                        earlyPrice: parseFloat(e.target.value),
                      })
                    }
                    className="p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                  <input
                    type="number"
                    placeholder="Regular Price"
                    value={newCategory.regularPrice}
                    onChange={(e) =>
                      setNewCategory({
                        ...newCategory,
                        regularPrice: parseFloat(e.target.value),
                      })
                    }
                    className="p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                </div>
                <div className="mt-3">
                  <label
                    className="text-sm font-medium"
                    style={{ color: "black" }}
                  >
                    Benefits/Inclusions
                  </label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="text"
                      placeholder="Add benefit (e.g., Access to all sessions)"
                      value={newBenefit}
                      onChange={(e) => setNewBenefit(e.target.value)}
                      className="flex-1 p-2 border rounded-lg"
                      style={{ borderColor: T.borderLight, color: "black" }}
                    />
                    <button
                      onClick={addBenefit}
                      className="px-3 py-2 rounded-lg text-white"
                      style={{ background: T.primary }}
                    >
                      + Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newCategory.benefits.map((b, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-sm rounded-full flex items-center gap-1"
                        style={{ background: T.white, color: "black" }}
                      >
                        ✓ {b}{" "}
                        <button
                          onClick={() => removeBenefit(idx)}
                          className="ml-1 text-red-500"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={
                      editingCategoryIndex !== null
                        ? () => updateCategory(editingCategoryIndex)
                        : addCategory
                    }
                    className="px-4 py-2 rounded-lg text-white"
                    style={{ background: T.primary }}
                  >
                    {editingCategoryIndex !== null
                      ? "Update Category"
                      : "Add Category"}
                  </button>
                  {editingCategoryIndex !== null && (
                    <button
                      onClick={() => {
                        resetCategoryForm();
                        setEditingCategoryIndex(null);
                      }}
                      className="px-4 py-2 rounded-lg bg-gray-300"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              {formData.categories.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2" style={{ color: "black" }}>
                    Current Categories ({formData.categories.length})
                  </h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {formData.categories.map((cat, idx) => (
                      <div
                        key={idx}
                        className="p-3 rounded-lg flex justify-between items-center"
                        style={{ background: T.offWhite }}
                      >
                        <div>
                          <p
                            className="font-semibold"
                            style={{ color: "black" }}
                          >
                            {cat.name}
                          </p>
                          <p className="text-sm" style={{ color: "black" }}>
                            Super Early: ${cat.superEarlyPrice} | Early: $
                            {cat.earlyPrice} | Regular: ${cat.regularPrice}
                          </p>
                          <div className="flex gap-1 mt-1">
                            {cat.benefits.slice(0, 2).map((b, i) => (
                              <span
                                key={i}
                                className="text-xs px-2 py-0.5 rounded bg-white"
                                style={{ color: "black" }}
                              >
                                ✓ {b}
                              </span>
                            ))}
                            {cat.benefits.length > 2 && (
                              <span
                                className="text-xs"
                                style={{ color: "black" }}
                              >
                                +{cat.benefits.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => editCategory(idx)}
                            className="p-1 rounded"
                            style={{ color: T.primary }}
                          >
                            <RiEditLine size={18} />
                          </button>
                          <button
                            onClick={() => removeCategory(idx)}
                            className="p-1 rounded"
                            style={{ color: "#EF4444" }}
                          >
                            <RiDeleteBinLine size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── STEP 2: DEADLINES ─── */}
          {step === 2 && (
            <div className="space-y-6">
              <div
                className="p-4 rounded-lg"
                style={{ background: T.offWhite }}
              >
                <h4 className="font-semibold mb-3" style={{ color: "black" }}>
                  {editingDeadlineIndex !== null
                    ? "Edit Deadline"
                    : "Add Custom Deadline"}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Deadline Title (e.g., Abstract Submission)"
                    value={newDeadline.title}
                    onChange={(e) =>
                      setNewDeadline({ ...newDeadline, title: e.target.value })
                    }
                    className="p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                  <input
                    type="datetime-local"
                    value={formatDateForInput(newDeadline.date)}
                    onChange={(e) =>
                      setNewDeadline({ ...newDeadline, date: e.target.value })
                    }
                    className="p-2 border rounded-lg"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newDeadline.isUrgent}
                      onChange={(e) =>
                        setNewDeadline({
                          ...newDeadline,
                          isUrgent: e.target.checked,
                        })
                      }
                    />
                    <span className="text-sm" style={{ color: "black" }}>
                      Mark as Urgent
                    </span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newDeadline.isActive}
                      onChange={(e) =>
                        setNewDeadline({
                          ...newDeadline,
                          isActive: e.target.checked,
                        })
                      }
                    />
                    <span className="text-sm" style={{ color: "black" }}>
                      Active
                    </span>
                  </label>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={
                      editingDeadlineIndex !== null
                        ? () => updateDeadlineItem(editingDeadlineIndex)
                        : addDeadline
                    }
                    className="px-4 py-2 rounded-lg text-white"
                    style={{ background: T.primary }}
                  >
                    {editingDeadlineIndex !== null
                      ? "Update Deadline"
                      : "Add Deadline"}
                  </button>
                  {editingDeadlineIndex !== null && (
                    <button
                      onClick={() => {
                        setNewDeadline({
                          title: "",
                          date: "",
                          isUrgent: false,
                          isActive: true,
                        });
                        setEditingDeadlineIndex(null);
                      }}
                      className="px-4 py-2 rounded-lg bg-gray-300"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              {formData.deadlines.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2" style={{ color: "black" }}>
                    Custom Deadlines ({formData.deadlines.length})
                  </h4>
                  <div className="space-y-2">
                    {formData.deadlines.map((dl, idx) => (
                      <div
                        key={idx}
                        className="p-3 rounded-lg flex justify-between items-center"
                        style={{
                          background: dl.isUrgent ? "#FEE2E2" : T.offWhite,
                        }}
                      >
                        <div>
                          <p
                            className="font-semibold"
                            style={{ color: "black" }}
                          >
                            {dl.title}{" "}
                            {dl.isUrgent && (
                              <span className="text-xs text-red-500 ml-2">
                                URGENT
                              </span>
                            )}
                          </p>
                          <p className="text-sm" style={{ color: "black" }}>
                            📅 {new Date(dl.date).toLocaleString()}
                          </p>
                          <p className="text-xs" style={{ color: "black" }}>
                            {dl.isActive ? "Active" : "Inactive"}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => editDeadline(idx)}
                            className="p-1 rounded"
                            style={{ color: T.primary }}
                          >
                            <RiEditLine size={18} />
                          </button>
                          <button
                            onClick={() => removeDeadline(idx)}
                            className="p-1 rounded"
                            style={{ color: "#EF4444" }}
                          >
                            <RiDeleteBinLine size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── STEP 3: IMPORTANT NOTES ─── */}
          {step === 3 && (
            <div className="space-y-6">
              <div
                className="p-4 rounded-lg"
                style={{ background: T.offWhite }}
              >
                <h4 className="font-semibold mb-3" style={{ color: "black" }}>
                  {editingNoteIndex !== null
                    ? "Edit Note"
                    : "Add Important Note"}
                </h4>
                <div className="space-y-3">
                  <textarea
                    placeholder="Note message"
                    value={newNote.message}
                    onChange={(e) =>
                      setNewNote({ ...newNote, message: e.target.value })
                    }
                    className="w-full p-2 border rounded-lg"
                    rows="3"
                    style={{ borderColor: T.borderLight, color: "black" }}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={newNote.severity}
                      onChange={(e) =>
                        setNewNote({ ...newNote, severity: e.target.value })
                      }
                      className="p-2 border rounded-lg"
                      style={{ borderColor: T.borderLight, color: "black" }}
                    >
                      {severityOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.icon} {opt.label}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newNote.isActive}
                        onChange={(e) =>
                          setNewNote({ ...newNote, isActive: e.target.checked })
                        }
                      />
                      <span className="text-sm" style={{ color: "black" }}>
                        Active
                      </span>
                    </label>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={
                      editingNoteIndex !== null
                        ? () => updateNoteItem(editingNoteIndex)
                        : addNote
                    }
                    className="px-4 py-2 rounded-lg text-white"
                    style={{ background: T.primary }}
                  >
                    {editingNoteIndex !== null ? "Update Note" : "Add Note"}
                  </button>
                  {editingNoteIndex !== null && (
                    <button
                      onClick={() => {
                        setNewNote({
                          message: "",
                          severity: "info",
                          order: 0,
                          isActive: true,
                        });
                        setEditingNoteIndex(null);
                      }}
                      className="px-4 py-2 rounded-lg bg-gray-300"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              {formData.importantNotes.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2" style={{ color: "black" }}>
                    Important Notes ({formData.importantNotes.length})
                  </h4>
                  <div className="space-y-2">
                    {formData.importantNotes.map((note, idx) => {
                      const severity =
                        severityOptions.find(
                          (s) => s.value === note.severity,
                        ) || severityOptions[2];
                      return (
                        <div
                          key={idx}
                          className="p-3 rounded-lg flex justify-between items-center"
                          style={{
                            background: severity.bg,
                            borderLeft: `3px solid ${severity.color}`,
                          }}
                        >
                          <div className="flex-1">
                            <p className="text-sm" style={{ color: "black" }}>
                              {severity.icon} {note.message}
                            </p>
                            <p
                              className="text-xs mt-1"
                              style={{ color: "black" }}
                            >
                              {note.isActive ? "Active" : "Inactive"}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => editNote(idx)}
                              className="p-1 rounded"
                              style={{ color: T.primary }}
                            >
                              <RiEditLine size={18} />
                            </button>
                            <button
                              onClick={() => removeNote(idx)}
                              className="p-1 rounded"
                              style={{ color: "#EF4444" }}
                            >
                              <RiDeleteBinLine size={18} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── NAVIGATION BUTTONS ─── */}
          <div
            className="flex gap-3 mt-6 pt-4 border-t"
            style={{ borderColor: T.borderLight }}
          >
            {step > 0 && (
              <button
                onClick={prevStep}
                className="px-6 py-2 rounded-lg flex items-center gap-2"
                style={{ background: "#E5E7EB", color: T.textMid }}
              >
                ← Back
              </button>
            )}
            {step < totalSteps - 1 && (
              <button
                onClick={nextStep}
                className="px-6 py-2 rounded-lg flex items-center gap-2"
                style={{ background: T.gradPrimary, color: T.white }}
              >
                Next →
              </button>
            )}
            {step === totalSteps - 1 && (
              <button
                onClick={saveRegistrationFee}
                disabled={loading || !canSave}
                className="px-6 py-2 rounded-lg flex items-center gap-2"
                style={{
                  background: canSave ? T.gradPrimary : "#CBD5E1",
                  color: canSave ? T.white : T.textMuted,
                  cursor: canSave ? "pointer" : "not-allowed",
                }}
              >
                {loading ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  <RiSaveLine size={18} />
                )}
                {editing ? "Update" : "Save"} Registration Fee
              </button>
            )}
            {step === 0 && (
              <button
                onClick={() => setShowForm(false)}
                className="px-6 py-2 rounded-lg bg-gray-300"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── DISPLAY ACTIVE FEE DATA ─── */}
      {feeData && (
        <div
          className="rounded-xl overflow-hidden shadow-lg"
          style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
        >
          <div className="px-6 py-4" style={{ background: T.gradPrimary }}>
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h3
                  className="text-xl font-bold flex items-center gap-2"
                  style={{ color: T.white }}
                >
                  <RiMoneyDollarCircleLine size={24} /> {feeData.title}
                </h3>
                <p className="text-sm mt-1" style={{ color: `${T.white}CC` }}>
                  {feeData.subtitle}
                </p>
                <div className="flex flex-wrap gap-3 mt-2">
                  <span
                    className="text-xs px-2 py-1 rounded"
                    style={{ background: `${T.white}20`, color: T.white }}
                  >
                    {feeData.conferenceName}
                  </span>
                  <span
                    className="text-xs px-2 py-1 rounded"
                    style={{ background: `${T.white}20`, color: T.white }}
                  >
                    {feeData.location}
                  </span>
                  <span
                    className="text-xs px-2 py-1 rounded"
                    style={{ background: `${T.white}20`, color: T.white }}
                  >
                    {feeData.categories?.length || 0} Categories
                  </span>
                  <span
                    className="text-xs px-2 py-1 rounded"
                    style={{ background: `${T.white}20`, color: T.white }}
                  >
                    Ed. {feeData.version || "2026"}
                  </span>
                  <span
                    className="text-xs px-2 py-1 rounded"
                    style={{ background: `${T.white}20`, color: T.white }}
                  >
                    Active
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  setEditing(feeData);
                  setFormData(feeData);
                  setShowForm(true);
                  setStep(0);
                  scrollToForm();
                }}
                className="px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all hover:scale-105"
                style={{ background: T.white, color: T.primary }}
              >
                <RiEditLine size={16} /> Edit
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="mb-6">
              <h4
                className="font-semibold mb-3 flex items-center gap-2"
                style={{ color: T.navy }}
              >
                <RiPriceTag3Line size={18} /> Pricing Tiers & Deadlines
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div
                  className="rounded-xl p-4"
                  style={{
                    background:
                      getExpiryStatus(feeData.superEarlyDeadline).status ===
                      "expired"
                        ? "#FEE2E2"
                        : T.softBlue,
                  }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold" style={{ color: T.primary }}>
                      🕐 SUPER EARLY BIRD
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        background: getExpiryStatus(feeData.superEarlyDeadline)
                          .bg,
                        color: getExpiryStatus(feeData.superEarlyDeadline)
                          .color,
                      }}
                    >
                      {getExpiryStatus(feeData.superEarlyDeadline).text}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: "black" }}>
                    Deadline: {formatDeadline(feeData.superEarlyDeadline)}
                  </p>
                  <p className="text-xs mt-1" style={{ color: T.primary }}>
                    Best discount - Limited time offer
                  </p>
                </div>
                <div
                  className="rounded-xl p-4"
                  style={{
                    background:
                      getExpiryStatus(feeData.earlyDeadline).status ===
                      "expired"
                        ? "#FEE2E2"
                        : T.softBlue,
                  }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold" style={{ color: T.accent }}>
                      ⏰ EARLY BIRD
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        background: getExpiryStatus(feeData.earlyDeadline).bg,
                        color: getExpiryStatus(feeData.earlyDeadline).color,
                      }}
                    >
                      {getExpiryStatus(feeData.earlyDeadline).text}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: "black" }}>
                    Deadline: {formatDeadline(feeData.earlyDeadline)}
                  </p>
                  <p className="text-xs mt-1" style={{ color: T.accent }}>
                    Early registration discount
                  </p>
                </div>
                <div
                  className="rounded-xl p-4"
                  style={{
                    background:
                      getExpiryStatus(feeData.regularDeadline).status ===
                      "expired"
                        ? "#FEE2E2"
                        : T.softBlue,
                  }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold" style={{ color: T.accentGold }}>
                      📅 STANDARD RATE
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        background: getExpiryStatus(feeData.regularDeadline).bg,
                        color: getExpiryStatus(feeData.regularDeadline).color,
                      }}
                    >
                      {getExpiryStatus(feeData.regularDeadline).text}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: "black" }}>
                    Deadline: {formatDeadline(feeData.regularDeadline)}
                  </p>
                  <p className="text-xs mt-1" style={{ color: T.accentGold }}>
                    Standard registration rate
                  </p>
                </div>
              </div>
            </div>

            <h4
              className="font-semibold mb-3 flex items-center gap-2"
              style={{ color: T.navy }}
            >
              <RiListCheck size={18} /> Registration Categories (
              {feeData.categories?.length || 0})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {feeData.categories
                ?.filter((c) => c.isActive)
                .sort((a, b) => a.order - b.order)
                .map((cat, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl p-4 transition-all hover:shadow-md"
                    style={{
                      background: T.offWhite,
                      borderLeft: `3px solid ${T.primary}`,
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h5
                          className="font-semibold text-base"
                          style={{ color: "black" }}
                        >
                          {cat.name}
                        </h5>
                        <div className="grid grid-cols-3 gap-2 mt-3">
                          <div
                            className="text-center p-2 rounded-lg"
                            style={{ background: T.white }}
                          >
                            <div
                              className="text-xs font-semibold"
                              style={{ color: T.primary }}
                            >
                              SUPER EARLY
                            </div>
                            <div
                              className="text-lg font-bold"
                              style={{ color: T.primary }}
                            >
                              ${cat.superEarlyPrice}
                            </div>
                          </div>
                          <div
                            className="text-center p-2 rounded-lg"
                            style={{ background: T.white }}
                          >
                            <div
                              className="text-xs font-semibold"
                              style={{ color: T.accent }}
                            >
                              EARLY
                            </div>
                            <div
                              className="text-lg font-bold"
                              style={{ color: T.accent }}
                            >
                              ${cat.earlyPrice}
                            </div>
                          </div>
                          <div
                            className="text-center p-2 rounded-lg"
                            style={{ background: T.white }}
                          >
                            <div
                              className="text-xs font-semibold"
                              style={{ color: T.accentGold }}
                            >
                              REGULAR
                            </div>
                            <div
                              className="text-lg font-bold"
                              style={{ color: T.accentGold }}
                            >
                              ${cat.regularPrice}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {cat.benefits?.slice(0, 2).map((b, i) => (
                            <span
                              key={i}
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{ background: T.white, color: "black" }}
                            >
                              ✓ {b}
                            </span>
                          ))}
                          {cat.benefits?.length > 2 && (
                            <span
                              className="text-xs"
                              style={{ color: "black" }}
                            >
                              +{cat.benefits.length - 2} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {feeData.deadlines?.length > 0 && (
              <>
                <h4
                  className="font-semibold mb-3 flex items-center gap-2"
                  style={{ color: T.navy }}
                >
                  <RiTimeLine size={18} /> Custom Deadlines (
                  {feeData.deadlines.length})
                </h4>
                <div className="flex flex-wrap gap-3 mb-6">
                  {feeData.deadlines
                    .filter((d) => d.isActive)
                    .map((dl, idx) => (
                      <div
                        key={idx}
                        className="rounded-xl p-3 min-w-[200px]"
                        style={{
                          background: dl.isUrgent ? "#FEE2E2" : T.offWhite,
                        }}
                      >
                        <p
                          className="font-semibold"
                          style={{ color: dl.isUrgent ? "#EF4444" : "black" }}
                        >
                          {dl.title}
                        </p>
                        <p className="text-sm mt-1" style={{ color: "black" }}>
                          📅 {new Date(dl.date).toLocaleString()}
                        </p>
                        {dl.isUrgent && (
                          <span className="text-xs text-red-500 mt-1 inline-block">
                            ⚠️ URGENT DEADLINE
                          </span>
                        )}
                      </div>
                    ))}
                </div>
              </>
            )}

            {feeData.importantNotes?.length > 0 && (
              <>
                <h4
                  className="font-semibold mb-3 flex items-center gap-2"
                  style={{ color: T.navy }}
                >
                  <RiErrorWarningLine size={18} /> Important Notes (
                  {feeData.importantNotes.length})
                </h4>
                <div className="space-y-2">
                  {feeData.importantNotes
                    .filter((n) => n.isActive)
                    .sort((a, b) => a.order - b.order)
                    .map((note, idx) => {
                      const severity =
                        severityOptions.find(
                          (s) => s.value === note.severity,
                        ) || severityOptions[2];
                      return (
                        <div
                          key={idx}
                          className="rounded-xl p-3 flex items-start gap-2"
                          style={{
                            background: severity.bg,
                            borderLeft: `3px solid ${severity.color}`,
                          }}
                        >
                          <span>{severity.icon}</span>
                          <p
                            className="text-sm flex-1"
                            style={{ color: severity.color }}
                          >
                            {note.message}
                          </p>
                        </div>
                      );
                    })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── ALL VERSIONS LIST ─── */}
      <div
        className="rounded-xl p-6"
        style={{
          background: T.white,
          border: `1px solid ${T.borderLight}`,
          boxShadow: T.shadow,
        }}
      >
        <h3
          className="text-lg font-semibold mb-4 flex items-center gap-2"
          style={{ color: T.navy }}
        >
          <RiHistoryLine size={18} /> All Registration Fee Sets (
          {allFeeData.length})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {allFeeData.map((fee) => (
            <div
              key={fee._id}
              className="flex items-center justify-between p-4 rounded-xl transition-all hover:shadow-md"
              style={{
                background: fee.isActive ? `${T.primary}08` : T.offWhite,
                border: `1px solid ${fee.isActive ? T.primary : T.borderLight}`,
              }}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold" style={{ color: "black" }}>
                    {fee.title}
                  </h4>
                  {fee.isActive && (
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={{ background: T.primary, color: T.white }}
                    >
                      ACTIVE
                    </span>
                  )}
                  <span className="text-xs" style={{ color: "black" }}>
                    Ed. {fee.version || "1.0"}
                  </span>
                </div>
                <p className="text-sm mt-1" style={{ color: "black" }}>
                  {fee.categories?.length || 0} categories •{" "}
                  {fee.conferenceName} •{" "}
                  {new Date(fee.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEditing(fee);
                    setFormData(fee);
                    setShowForm(true);
                    setStep(0);
                    scrollToForm();
                  }}
                  className="p-2 rounded-lg transition-all hover:scale-110"
                  style={{ color: T.primary, background: `${T.primary}15` }}
                  title="Edit"
                >
                  <RiEditLine size={16} />
                </button>
                <button
                  onClick={() => deleteRegistrationFee(fee._id)}
                  className="p-2 rounded-lg transition-all hover:scale-110"
                  style={{ color: "#EF4444", background: "#FEE2E2" }}
                  title="Delete"
                >
                  <RiDeleteBinLine size={16} />
                </button>
              </div>
            </div>
          ))}
          {allFeeData.length === 0 && (
            <div
              className="col-span-2 text-center py-8 rounded-xl"
              style={{ background: T.offWhite }}
            >
              <RiMoneyDollarCircleLine
                size={48}
                className="mx-auto mb-2"
                style={{ color: "black" }}
              />
              <p style={{ color: "black" }}>
                No registration fee data created yet.
              </p>
              <p className="text-sm mt-1" style={{ color: "black" }}>
                Click "Create Registration Fee" to get started.
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
/* ══════════════════════════════════════════════════════════════════════
   SPEAKER PRICING TAB
   ══════════════════════════════════════════════════════════════════════ */
const SpeakerPricingTab = ({ token, eventId }) => {
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    price: 0,
    priceDisplay: "",
    featured: false,
    color: "#06B6D4",
    features: [],
    order: 0,
    isActive: true,
  });
  const [newFeature, setNewFeature] = useState("");

  useEffect(() => {
    if (eventId) fetchPricingTiers();
  }, [eventId]);

  const fetchPricingTiers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/speaker-pricing/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      console.log("RAW pricing response:", JSON.stringify(data)); // 👈 add this

      let tiersArray = Array.isArray(data)
        ? data
        : data.data || data.tiers || data.results || data.pricingTiers || [];

      console.log("Tiers array extracted:", tiersArray); // 👈 and this

      setPricingTiers(tiersArray);
    } catch (error) {
      console.error("Error fetching pricing:", error);
      toast.error("Failed to load pricing data");
    } finally {
      setLoading(false);
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()],
      });
      setNewFeature("");
    }
  };

  const removeFeature = (index) => {
    const updated = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: updated });
  };

  const savePricing = async () => {
    if (!formData.title || formData.price <= 0) {
      toast.error("Title and price are required");
      return;
    }

    const payload = withEventId(
      {
        ...formData,
        priceDisplay: formData.priceDisplay || `$${formData.price}`,
      },
      eventId,
    );

    console.log("=== SAVE PRICING DEBUG ===");
    console.log("eventId:", eventId);
    console.log("formData:", JSON.stringify(formData));
    console.log("payload:", JSON.stringify(payload));

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      let response;

      if (editing) {
        response = await axios.put(
          `${API_BASE}/api/speaker-pricing/${editing._id}`,
          payload,
          config,
        );
        console.log("Update response:", JSON.stringify(response.data));
        toast.success("Pricing tier updated");
      } else {
        response = await axios.post(
          `${API_BASE}/api/speaker-pricing`,
          payload,
          config,
        );
        console.log("Create response:", JSON.stringify(response.data));
        toast.success("Pricing tier created");
      }

      setShowForm(false);
      setEditing(null);
      resetForm();
      await fetchPricingTiers();
    } catch (error) {
      console.error("Error saving pricing:", error);
      console.error("Error status:", error.response?.status);
      console.error("Error response:", JSON.stringify(error.response?.data));
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deletePricing = async (id) => {
    if (!window.confirm("Delete this pricing tier?")) return;
    try {
      await axios.delete(`${API_BASE}/api/speaker-pricing/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Pricing tier deactivated");
      fetchPricingTiers();
    } catch (error) {
      console.error("Error deleting pricing:", error);
      toast.error("Failed to delete");
    }
  };

  const toggleActive = async (tier) => {
    try {
      await axios.put(
        `${API_BASE}/api/speaker-pricing/${tier._id}`,
        withEventId({ ...tier, isActive: !tier.isActive }, eventId),
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(
        `Pricing tier ${!tier.isActive ? "activated" : "deactivated"}`,
      );
      fetchPricingTiers();
    } catch (error) {
      console.error("Error toggling:", error);
      toast.error("Failed to update");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      price: 0,
      priceDisplay: "",
      featured: false,
      color: "#06B6D4",
      features: [],
      order: 0,
      isActive: true,
    });
    setNewFeature("");
  };

  const activeTiers = pricingTiers
    .filter((t) => t.isActive !== false)
    .sort((a, b) => a.order - b.order);
  const inactiveTiers = pricingTiers.filter((t) => t.isActive === false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <div
          className="px-6 py-4 border-b"
          style={{ borderBottomColor: T.borderLight }}
        >
          <h3
            className="text-lg font-semibold flex items-center gap-2"
            style={{ color: T.navy }}
          >
            <RiEyeLine className="text-green-400" /> Live Pricing Preview
          </h3>
          <p className="text-sm mt-1" style={{ color: T.textMuted }}>
            This is how the pricing cards will appear on the website
          </p>
        </div>
        <div className="p-6">
          {activeTiers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {activeTiers.map((tier, idx) => (
                <div
                  key={tier._id}
                  className="rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-2"
                  style={{
                    background: tier.featured
                      ? `linear-gradient(135deg, ${tier.color}22, ${tier.color}11)`
                      : T.offWhite,
                    border: `1.5px solid ${tier.featured ? tier.color : T.border}`,
                    transform: tier.featured ? "scale(1.02)" : "none",
                  }}
                >
                  {tier.featured && (
                    <div
                      className="text-center py-1 text-xs font-semibold"
                      style={{ background: tier.color, color: T.white }}
                    >
                      POPULAR
                    </div>
                  )}
                  <div className="p-6 text-center">
                    <h3
                      className="text-lg font-semibold mb-2"
                      style={{ color: T.textMid }}
                    >
                      {tier.title}
                    </h3>
                    <div
                      className="text-4xl font-bold mb-4"
                      style={{ color: tier.color }}
                    >
                      {tier.priceDisplay}
                    </div>
                    <ul className="space-y-2 mb-6">
                      {tier.features.map((feature, fIdx) => (
                        <li
                          key={fIdx}
                          className="text-sm flex items-center justify-center gap-2"
                          style={{ color: T.textMuted }}
                        >
                          <span style={{ color: tier.color }}>✓</span> {feature}
                        </li>
                      ))}
                    </ul>
                    <button
                      className="w-full py-2 rounded-lg font-semibold transition-all hover:opacity-80"
                      style={{ background: tier.color, color: "#fff" }}
                    >
                      Register Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="text-center py-12 rounded-lg"
              style={{ background: T.offWhite }}
            >
              <RiMoneyDollarCircleLine
                size={48}
                className="mx-auto mb-2"
                style={{ color: T.textMuted }}
              />
              <p style={{ color: T.textMuted }}>No active pricing tiers</p>
              <p className="text-sm" style={{ color: T.textMuted }}>
                Create pricing tiers below to display on the website
              </p>
            </div>
          )}
        </div>
      </div>

      <div
        className="rounded-xl p-6"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <div className="flex justify-between items-center mb-4">
          <h3
            className="text-lg font-semibold flex items-center gap-2"
            style={{ color: T.navy }}
          >
            <RiMoneyDollarCircleLine className="text-yellow-400" />
            {editing ? "Edit Pricing Tier" : "Add New Pricing Tier"}
          </h3>
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (!showForm) resetForm();
              setEditing(null);
            }}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition"
            style={{ background: T.gradPrimary, color: T.white }}
          >
            {showForm ? <RiCloseLine size={18} /> : <RiAddLine size={18} />}
            {showForm ? "Close Form" : "Add Pricing Tier"}
          </button>
        </div>
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-6 overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="e.g., Oral Talk, Student Delegate"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Price (USD) *
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setFormData({
                        ...formData,
                        price: val,
                        priceDisplay: `$${val}`,
                      });
                    }}
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="699"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Display Price (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.priceDisplay}
                    onChange={(e) =>
                      setFormData({ ...formData, priceDisplay: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="$699"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Accent Color
                  </label>
                  <div className="flex gap-3 items-center">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) =>
                        setFormData({ ...formData, color: e.target.value })
                      }
                      className="w-12 h-12 rounded-lg cursor-pointer"
                      style={{ border: `1px solid ${T.border}` }}
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) =>
                        setFormData({ ...formData, color: e.target.value })
                      }
                      className="flex-1 rounded-lg p-3"
                      style={{
                        background: T.offWhite,
                        border: `1px solid ${T.border}`,
                        color: T.text,
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) =>
                      setFormData({ ...formData, featured: e.target.checked })
                    }
                    className="w-5 h-5 accent-yellow-500"
                  />
                  <label
                    className="flex items-center gap-1"
                    style={{ color: T.textMid }}
                  >
                    <RiStarFill className="text-yellow-400" /> Featured
                    (highlighted)
                  </label>
                </div>
              </div>
              <div>
                <label
                  className="block mb-2 text-sm"
                  style={{ color: T.textMid }}
                >
                  Features
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addFeature()}
                    className="flex-1 rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Enter a feature (e.g., Access to all talks)"
                  />
                  <button
                    onClick={addFeature}
                    className="px-4 py-2 rounded-lg"
                    style={{ background: `${T.primary}20`, color: T.primary }}
                  >
                    <RiAddLine size={20} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      style={{ background: T.offWhite, color: T.text }}
                    >
                      {feature}
                      <button
                        onClick={() => removeFeature(idx)}
                        className="hover:opacity-70"
                        style={{ color: "#EF4444" }}
                      >
                        <RiCloseLine size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                {formData.features.length === 0 && (
                  <p className="text-sm mt-2" style={{ color: T.textMuted }}>
                    No features added yet. Add features to display on the
                    pricing card.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="w-5 h-5 accent-green-500"
                />
                <label style={{ color: T.textMid }}>
                  Activate this pricing tier
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={savePricing}
                  disabled={loading}
                  className="px-6 py-2 rounded-lg flex items-center gap-2"
                  style={{ background: T.gradPrimary, color: T.white }}
                >
                  <RiSaveLine size={18} />
                  {loading
                    ? "Saving..."
                    : editing
                      ? "Update Tier"
                      : "Save Tier"}
                </button>
                {editing && (
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditing(null);
                      resetForm();
                    }}
                    className="px-6 py-2 rounded-lg"
                    style={{ background: "#E5E7EB", color: T.textMid }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div
        className="rounded-xl p-6"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: T.navy }}>
          ✅ Active Pricing Tiers ({activeTiers.length})
        </h3>
        <div className="space-y-3">
          {activeTiers.map((tier) => (
            <div
              key={tier._id}
              className="flex items-center gap-4 p-4 rounded-lg"
              style={{ background: T.offWhite }}
            >
              <div
                className="w-20 h-20 rounded-lg flex flex-col items-center justify-center"
                style={{
                  background: `${tier.color}22`,
                  borderLeft: `3px solid ${tier.color}`,
                }}
              >
                <span
                  className="text-lg font-bold"
                  style={{ color: tier.color }}
                >
                  {tier.priceDisplay}
                </span>
                <span className="text-xs" style={{ color: T.textMuted }}>
                  {tier.title.substring(0, 15)}
                </span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold" style={{ color: T.text }}>
                    {tier.title}
                  </h4>
                  {tier.featured && (
                    <span
                      className="px-2 py-0.5 rounded text-xs flex items-center gap-1"
                      style={{
                        background: `${T.accentGold}20`,
                        color: T.accentGold,
                      }}
                    >
                      <RiStarFill size={10} /> Featured
                    </span>
                  )}
                  <span className="text-xs" style={{ color: T.textMuted }}>
                    Order: {tier.order}
                  </span>
                </div>
                <p className="text-sm" style={{ color: T.textMuted }}>
                  {tier.features.length} features
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleActive(tier)}
                  className="p-2 rounded"
                  title="Deactivate"
                  style={{
                    color: T.accentGold,
                    background: `${T.accentGold}20`,
                  }}
                >
                  <RiEyeOffLine size={16} />
                </button>
                <button
                  onClick={() => {
                    setEditing(tier);
                    setFormData(tier);
                    setShowForm(true);
                  }}
                  className="p-2 rounded"
                  style={{ color: T.primary, background: `${T.primary}20` }}
                >
                  <RiEditLine size={16} />
                </button>
                <button
                  onClick={() => deletePricing(tier._id)}
                  className="p-2 rounded"
                  style={{ color: "#EF4444", background: "#FEE2E2" }}
                >
                  <RiDeleteBinLine size={16} />
                </button>
              </div>
            </div>
          ))}
          {activeTiers.length === 0 && (
            <p className="text-center py-4" style={{ color: T.textMuted }}>
              No active pricing tiers. Add and activate some above.
            </p>
          )}
        </div>
      </div>

      {inactiveTiers.length > 0 && (
        <div
          className="rounded-xl p-6 opacity-60"
          style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: T.navy }}>
            🚫 Inactive Pricing Tiers ({inactiveTiers.length})
          </h3>
          <div className="space-y-3">
            {inactiveTiers.map((tier) => (
              <div
                key={tier._id}
                className="flex items-center gap-4 p-3 rounded-lg opacity-50"
                style={{ background: T.offWhite }}
              >
                <div
                  className="w-16 h-16 rounded-lg flex items-center justify-center"
                  style={{ background: T.white }}
                >
                  <RiMoneyDollarCircleLine
                    size={24}
                    style={{ color: T.textMuted }}
                  />
                </div>
                <div className="flex-1">
                  <p className="font-semibold" style={{ color: T.text }}>
                    {tier.title}
                  </p>
                  <p className="text-sm" style={{ color: T.textMuted }}>
                    {tier.priceDisplay}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(tier)}
                    className="p-2 rounded"
                    title="Activate"
                    style={{ color: "#4CAF50", background: "#E8F5E9" }}
                  >
                    <RiEyeLine size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(tier);
                      setFormData(tier);
                      setShowForm(true);
                    }}
                    className="p-2 rounded"
                    style={{ color: T.primary, background: `${T.primary}20` }}
                  >
                    <RiEditLine size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

 
    </motion.div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   VENUE MANAGEMENT TAB
   ══════════════════════════════════════════════════════════════════════ */

const VenueManagementTab = ({ token, eventId }) => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    subtitle: "",
    description: "",
    address: "",
    area: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
    coordinates: { lat: 17.4933, lng: 78.3903 },
    mapZoom: 15,
    mapEmbedUrl: "",
    contactEmail: "",
    contactPhone: "",
    contactPerson: "",
    bannerImage: "",
    venueImage: "",
    galleryImages: [],
    amenities: [],
    airportInfo: {
      name: "Rajiv Gandhi International Airport (HYD)",
      distance: "32 km",
      travelTime: "45-60 min",
      route: "via PVNR Expressway",
    },
    nearbyTransport: [],
    parkingInfo:
      "Ample parking space available with 24/7 security surveillance",
    nearbyHotels: [],
    capacity: 500,
    totalHalls: 4,
    parkingSpaces: 100,
    isWheelchairAccessible: true,
    hasWiFi: true,
    hasPowerBackup: true,
    hasCatering: true,
    operatingHours: {
      weekdays: "9:00 AM – 6:00 PM",
      saturday: "10:00 AM – 2:00 PM",
      sunday: "Closed",
    },
    nearbyLandmarks: [],
    isActive: true,
    isPrimary: false,
    order: 0,
    version: "1.0",
  });
  const [newAmenity, setNewAmenity] = useState({
    name: "",
    description: "",
    icon: "RiBuildingLine",
    order: 0,
  });
  const [newTransport, setNewTransport] = useState({
    name: "",
    type: "metro",
    distance: "",
    details: "",
    order: 0,
  });
  const [newHotel, setNewHotel] = useState({
    name: "",
    distance: "",
    priceRange: "",
    rating: 0,
    order: 0,
  });

  useEffect(() => {
    if (eventId) {
      console.log("VenueManagementTab - Fetching venues for event:", eventId);
      fetchVenues();
    } else {
      console.warn("No eventId provided to VenueManagementTab");
      setDebugInfo("No eventId provided");
    }
  }, [eventId]);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      setDebugInfo("Fetching venues...");
      const url = `${API_BASE}/api/venue/event/${eventId}`;
      console.log("Fetching from URL:", url);

      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Fetched venues response:", data);
      setDebugInfo(
        `Response: success=${data.success}, venues count=${data.venues?.length || 0}`,
      );

      if (data.success) {
        const venuesData = data.venues || [];
        console.log("Venues data:", venuesData);
        setVenues(venuesData);
      } else {
        console.log("API returned success: false");
        setDebugInfo("API returned success: false");
        setVenues([]);
      }
    } catch (error) {
      console.error("Error fetching venues:", error);
      setDebugInfo(`Error: ${error.message}`);
      toast.error("Failed to load venues");
      setVenues([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      subtitle: "",
      description: "",
      address: "",
      area: "",
      city: "",
      state: "",
      country: "India",
      pincode: "",
      coordinates: { lat: 17.4933, lng: 78.3903 },
      mapZoom: 15,
      mapEmbedUrl: "",
      contactEmail: "",
      contactPhone: "",
      contactPerson: "",
      bannerImage: "",
      venueImage: "",
      galleryImages: [],
      amenities: [],
      airportInfo: {
        name: "Rajiv Gandhi International Airport (HYD)",
        distance: "32 km",
        travelTime: "45-60 min",
        route: "via PVNR Expressway",
      },
      nearbyTransport: [],
      parkingInfo:
        "Ample parking space available with 24/7 security surveillance",
      nearbyHotels: [],
      capacity: 500,
      totalHalls: 4,
      parkingSpaces: 100,
      isWheelchairAccessible: true,
      hasWiFi: true,
      hasPowerBackup: true,
      hasCatering: true,
      operatingHours: {
        weekdays: "9:00 AM – 6:00 PM",
        saturday: "10:00 AM – 2:00 PM",
        sunday: "Closed",
      },
      nearbyLandmarks: [],
      isActive: true,
      isPrimary: false,
      order: 0,
      version: "1.0",
    });
    setEditing(null);
    setNewAmenity({
      name: "",
      description: "",
      icon: "RiBuildingLine",
      order: 0,
    });
    setNewTransport({
      name: "",
      type: "metro",
      distance: "",
      details: "",
      order: 0,
    });
    setNewHotel({
      name: "",
      distance: "",
      priceRange: "",
      rating: 0,
      order: 0,
    });
  };

  const saveVenue = async () => {
    if (!formData.name || !formData.city || !formData.state) {
      toast.error("Name, city, and state are required");
      return;
    }

    if (!eventId) {
      toast.error("Event ID is missing");
      return;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = {
        ...formData,
        eventId: eventId,
      };

      console.log("Saving venue with payload:", payload);

      let response;
      if (editing) {
        response = await axios.put(
          `${API_BASE}/api/venue/${editing._id}`,
          payload,
          config,
        );
        console.log("Update response:", response.data);
        toast.success("Venue updated successfully");
      } else {
        response = await axios.post(`${API_BASE}/api/venue`, payload, config);
        console.log("Create response:", response.data);
        toast.success("Venue created successfully");
      }

      resetForm();
      setShowForm(false);
      await fetchVenues();
    } catch (error) {
      console.error("Error saving venue:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteVenue = async (id) => {
    if (!window.confirm("Delete this venue?")) return;
    try {
      await axios.delete(`${API_BASE}/api/venue/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Venue deleted");
      await fetchVenues();
    } catch (error) {
      console.error("Error deleting venue:", error);
      toast.error("Failed to delete");
    }
  };

  const toggleActive = async (venue) => {
    try {
      console.log(`Toggling active for venue: ${venue.name}`);

      const response = await axios.patch(
        `${API_BASE}/api/venue/${venue._id}/toggle-active`,
        { eventId: eventId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      console.log("Toggle response:", response.data);
      toast.success(`Venue ${!venue.isActive ? "activated" : "deactivated"}`);
      await fetchVenues();
    } catch (error) {
      console.error("Error toggling active:", error);
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  const setAsPrimary = async (venue) => {
    try {
      console.log(`Setting primary venue: ${venue.name}`);

      const response = await axios.patch(
        `${API_BASE}/api/venue/${venue._id}/set-primary`,
        { eventId: eventId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      console.log("Set primary response:", response.data);
      toast.success("Venue set as primary");
      await fetchVenues();
    } catch (error) {
      console.error("Error setting primary:", error);
      toast.error("Failed to set as primary");
    }
  };

  const seedDefaultVenue = async () => {
    if (!eventId) {
      toast.error("Event ID is required");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/venue/seed/event/${eventId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      console.log("Seed response:", response.data);
      toast.success("Default venue seeded successfully!");
      await fetchVenues();
    } catch (error) {
      console.error("Error seeding venue:", error);
      toast.error(
        error.response?.data?.message || "Failed to seed default venue",
      );
    } finally {
      setLoading(false);
    }
  };

  const addAmenity = () => {
    if (!newAmenity.name) {
      toast.error("Amenity name is required");
      return;
    }
    setFormData({
      ...formData,
      amenities: [
        ...formData.amenities,
        { ...newAmenity, order: formData.amenities.length },
      ],
    });
    setNewAmenity({
      name: "",
      description: "",
      icon: "RiBuildingLine",
      order: 0,
    });
  };

  const removeAmenity = (index) => {
    const updated = formData.amenities.filter((_, i) => i !== index);
    setFormData({ ...formData, amenities: updated });
  };

  const addTransport = () => {
    if (!newTransport.name) {
      toast.error("Transport name is required");
      return;
    }
    setFormData({
      ...formData,
      nearbyTransport: [
        ...formData.nearbyTransport,
        { ...newTransport, order: formData.nearbyTransport.length },
      ],
    });
    setNewTransport({
      name: "",
      type: "metro",
      distance: "",
      details: "",
      order: 0,
    });
  };

  const removeTransport = (index) => {
    const updated = formData.nearbyTransport.filter((_, i) => i !== index);
    setFormData({ ...formData, nearbyTransport: updated });
  };

  const addHotel = () => {
    if (!newHotel.name) {
      toast.error("Hotel name is required");
      return;
    }
    setFormData({
      ...formData,
      nearbyHotels: [
        ...formData.nearbyHotels,
        { ...newHotel, order: formData.nearbyHotels.length },
      ],
    });
    setNewHotel({
      name: "",
      distance: "",
      priceRange: "",
      rating: 0,
      order: 0,
    });
  };

  const removeHotel = (index) => {
    const updated = formData.nearbyHotels.filter((_, i) => i !== index);
    setFormData({ ...formData, nearbyHotels: updated });
  };

  const activeVenues = venues
    .filter((v) => v.isActive === true)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const inactiveVenues = venues.filter((v) => v.isActive === false);

  // All sections in one view - no tabs
  const renderAllSections = () => (
    <div className="space-y-8">
      {/* Basic Information Section */}
      <div className="border-b pb-2 mb-4">
        <h3 className="text-lg font-semibold text-black">
          📋 Basic Information
        </h3>
        <p className="text-sm mt-1 text-black">
          Configure the main venue details
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block mb-2 text-sm font-semibold text-black">
            Venue Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
            placeholder="e.g., Manjeera Trinity Corporate"
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-semibold text-black">
            Slug
          </label>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) =>
              setFormData({
                ...formData,
                slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
              })
            }
            className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
            placeholder="auto-generated-from-name"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block mb-2 text-sm font-semibold text-black">
            Subtitle
          </label>
          <input
            type="text"
            value={formData.subtitle}
            onChange={(e) =>
              setFormData({ ...formData, subtitle: e.target.value })
            }
            className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
            placeholder="Premier Corporate Venue"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block mb-2 text-sm font-semibold text-black">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition resize-none text-black"
            rows="3"
            placeholder="Detailed description of the venue..."
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-semibold text-black">
            Capacity
          </label>
          <input
            type="number"
            value={formData.capacity}
            onChange={(e) =>
              setFormData({
                ...formData,
                capacity: parseInt(e.target.value) || 0,
              })
            }
            className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
            placeholder="500"
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-semibold text-black">
            Total Halls
          </label>
          <input
            type="number"
            value={formData.totalHalls}
            onChange={(e) =>
              setFormData({
                ...formData,
                totalHalls: parseInt(e.target.value) || 0,
              })
            }
            className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
            placeholder="4"
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-semibold text-black">
            Parking Spaces
          </label>
          <input
            type="number"
            value={formData.parkingSpaces}
            onChange={(e) =>
              setFormData({
                ...formData,
                parkingSpaces: parseInt(e.target.value) || 0,
              })
            }
            className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
            placeholder="100"
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-semibold text-black">
            Order / Priority
          </label>
          <input
            type="number"
            value={formData.order}
            onChange={(e) =>
              setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
            }
            className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
            placeholder="0"
          />
        </div>
        <div className="flex items-center gap-6 mt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
              className="w-5 h-5 accent-green-500"
            />
            <span className="text-black">Active (visible on website)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isPrimary}
              onChange={(e) =>
                setFormData({ ...formData, isPrimary: e.target.checked })
              }
              className="w-5 h-5 accent-yellow-500"
            />
            <span className="text-black">Primary Venue</span>
          </label>
        </div>
      </div>

      {/* Location Section */}
      <div className="mt-8 border-t pt-6">
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold text-black">
            📍 Location Details
          </h3>
          <p className="text-sm mt-1 text-black">
            Physical address and mapping information
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block mb-2 text-sm font-semibold text-black">
              Address
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="Street address"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-black">
              Area / Locality
            </label>
            <input
              type="text"
              value={formData.area}
              onChange={(e) =>
                setFormData({ ...formData, area: e.target.value })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="e.g., HITEC City"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-black">
              City *
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) =>
                setFormData({ ...formData, city: e.target.value })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="Hyderabad"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-black">
              State *
            </label>
            <input
              type="text"
              value={formData.state}
              onChange={(e) =>
                setFormData({ ...formData, state: e.target.value })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="Telangana"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-black">
              Country
            </label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) =>
                setFormData({ ...formData, country: e.target.value })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-black">
              Pincode
            </label>
            <input
              type="text"
              value={formData.pincode}
              onChange={(e) =>
                setFormData({ ...formData, pincode: e.target.value })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="500032"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-black">
              Latitude
            </label>
            <input
              type="number"
              step="any"
              value={formData.coordinates.lat}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  coordinates: {
                    ...formData.coordinates,
                    lat: parseFloat(e.target.value) || 0,
                  },
                })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="17.4933"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-black">
              Longitude
            </label>
            <input
              type="number"
              step="any"
              value={formData.coordinates.lng}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  coordinates: {
                    ...formData.coordinates,
                    lng: parseFloat(e.target.value) || 0,
                  },
                })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="78.3903"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-black">
              Map Zoom Level
            </label>
            <input
              type="number"
              value={formData.mapZoom}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  mapZoom: parseInt(e.target.value) || 15,
                })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block mb-2 text-sm font-semibold text-black">
              Map Embed URL
            </label>
            <input
              type="text"
              value={formData.mapEmbedUrl}
              onChange={(e) =>
                setFormData({ ...formData, mapEmbedUrl: e.target.value })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="https://..."
            />
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="mt-8 border-t pt-6">
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold text-black">
            📞 Contact Information
          </h3>
          <p className="text-sm mt-1 text-black">
            Venue contact details and airport info
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block mb-2 text-sm font-semibold text-black">
              Contact Email
            </label>
            <input
              type="email"
              value={formData.contactEmail}
              onChange={(e) =>
                setFormData({ ...formData, contactEmail: e.target.value })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="venue@example.com"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-black">
              Contact Phone
            </label>
            <input
              type="text"
              value={formData.contactPhone}
              onChange={(e) =>
                setFormData({ ...formData, contactPhone: e.target.value })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="+91 XXXXXXXXXX"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-semibold text-black">
              Contact Person
            </label>
            <input
              type="text"
              value={formData.contactPerson}
              onChange={(e) =>
                setFormData({ ...formData, contactPerson: e.target.value })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="Venue Manager Name"
            />
          </div>
          <div className="md:col-span-2">
            <div className="border rounded-xl p-4 border-gray-200">
              <h4 className="font-semibold mb-3 text-black">
                ✈️ Airport Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-black">
                    Airport Name
                  </label>
                  <input
                    type="text"
                    value={formData.airportInfo.name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        airportInfo: {
                          ...formData.airportInfo,
                          name: e.target.value,
                        },
                      })
                    }
                    className="w-full rounded-lg p-2 mt-1 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
                  />
                </div>
                <div>
                  <label className="block text-xs text-black">Distance</label>
                  <input
                    type="text"
                    value={formData.airportInfo.distance}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        airportInfo: {
                          ...formData.airportInfo,
                          distance: e.target.value,
                        },
                      })
                    }
                    className="w-full rounded-lg p-2 mt-1 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
                  />
                </div>
                <div>
                  <label className="block text-xs text-black">
                    Travel Time
                  </label>
                  <input
                    type="text"
                    value={formData.airportInfo.travelTime}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        airportInfo: {
                          ...formData.airportInfo,
                          travelTime: e.target.value,
                        },
                      })
                    }
                    className="w-full rounded-lg p-2 mt-1 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
                  />
                </div>
                <div>
                  <label className="block text-xs text-black">Route</label>
                  <input
                    type="text"
                    value={formData.airportInfo.route}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        airportInfo: {
                          ...formData.airportInfo,
                          route: e.target.value,
                        },
                      })
                    }
                    className="w-full rounded-lg p-2 mt-1 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block mb-2 text-sm font-semibold text-black">
              Parking Information
            </label>
            <textarea
              value={formData.parkingInfo}
              onChange={(e) =>
                setFormData({ ...formData, parkingInfo: e.target.value })
              }
              className="w-full rounded-xl p-3 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition resize-none text-black"
              rows="2"
            />
          </div>
        </div>
      </div>

      {/* Amenities Section */}
      <div className="mt-8 border-t pt-6">
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold text-black">
            ✨ Amenities & Features
          </h3>
          <p className="text-sm mt-1 text-black">
            Add venue amenities and select available features
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border rounded-xl p-4 border-gray-200">
            <h4 className="font-semibold mb-3 text-black">Features</h4>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isWheelchairAccessible}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isWheelchairAccessible: e.target.checked,
                    })
                  }
                  className="w-4 h-4"
                />
                <span className="text-black">♿ Wheelchair Accessible</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.hasWiFi}
                  onChange={(e) =>
                    setFormData({ ...formData, hasWiFi: e.target.checked })
                  }
                  className="w-4 h-4"
                />
                <span className="text-black">📶 WiFi Available</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.hasPowerBackup}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      hasPowerBackup: e.target.checked,
                    })
                  }
                  className="w-4 h-4"
                />
                <span className="text-black">⚡ Power Backup</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.hasCatering}
                  onChange={(e) =>
                    setFormData({ ...formData, hasCatering: e.target.checked })
                  }
                  className="w-4 h-4"
                />
                <span className="text-black">🍽️ Catering Available</span>
              </label>
            </div>
          </div>

          <div className="border rounded-xl p-4 border-gray-200">
            <h4 className="font-semibold mb-3 text-black">Operating Hours</h4>
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-black">Weekdays</label>
                <input
                  type="text"
                  value={formData.operatingHours.weekdays}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      operatingHours: {
                        ...formData.operatingHours,
                        weekdays: e.target.value,
                      },
                    })
                  }
                  className="w-full rounded-lg p-2 mt-1 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
                />
              </div>
              <div>
                <label className="block text-xs text-black">Saturday</label>
                <input
                  type="text"
                  value={formData.operatingHours.saturday}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      operatingHours: {
                        ...formData.operatingHours,
                        saturday: e.target.value,
                      },
                    })
                  }
                  className="w-full rounded-lg p-2 mt-1 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
                />
              </div>
              <div>
                <label className="block text-xs text-black">Sunday</label>
                <input
                  type="text"
                  value={formData.operatingHours.sunday}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      operatingHours: {
                        ...formData.operatingHours,
                        sunday: e.target.value,
                      },
                    })
                  }
                  className="w-full rounded-lg p-2 mt-1 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded-xl p-4 border-gray-200 mt-4">
          <h4 className="font-semibold mb-3 text-black">Amenities List</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              value={newAmenity.name}
              onChange={(e) =>
                setNewAmenity({ ...newAmenity, name: e.target.value })
              }
              className="w-full rounded-lg p-2 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="Amenity name"
            />
            <input
              type="text"
              value={newAmenity.description}
              onChange={(e) =>
                setNewAmenity({ ...newAmenity, description: e.target.value })
              }
              className="w-full rounded-lg p-2 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="Description (optional)"
            />
          </div>
          <button
            onClick={addAmenity}
            className="px-4 py-2 rounded-lg text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
          >
            + Add Amenity
          </button>

          {formData.amenities.length > 0 && (
            <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
              {formData.amenities.map((item, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center p-2 rounded-lg bg-gray-50"
                >
                  <div>
                    <span className="font-medium text-black">{item.name}</span>
                    {item.description && (
                      <span className="text-sm ml-2 text-black">
                        - {item.description}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => removeAmenity(idx)}
                    className="text-red-500 hover:text-red-700"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Transport Section */}
      <div className="mt-8 border-t pt-6">
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold text-black">
            🚍 Nearby Transport
          </h3>
          <p className="text-sm mt-1 text-black">
            Metro, bus stops, and other transport options
          </p>
        </div>

        <div className="border rounded-xl p-4 border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <input
              type="text"
              value={newTransport.name}
              onChange={(e) =>
                setNewTransport({ ...newTransport, name: e.target.value })
              }
              className="w-full rounded-lg p-2 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="Station/Stop name"
            />
            <select
              value={newTransport.type}
              onChange={(e) =>
                setNewTransport({ ...newTransport, type: e.target.value })
              }
              className="w-full rounded-lg p-2 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
            >
              <option value="metro">🚇 Metro</option>
              <option value="bus">🚌 Bus</option>
              <option value="train">🚂 Train</option>
              <option value="cab">🚕 Cab/Auto</option>
            </select>
            <input
              type="text"
              value={newTransport.distance}
              onChange={(e) =>
                setNewTransport({ ...newTransport, distance: e.target.value })
              }
              className="w-full rounded-lg p-2 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="Distance (e.g., 500m)"
            />
          </div>
          <button
            onClick={addTransport}
            className="px-4 py-2 rounded-lg text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
          >
            + Add Transport
          </button>

          {formData.nearbyTransport.length > 0 && (
            <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
              {formData.nearbyTransport.map((item, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center p-2 rounded-lg bg-gray-50"
                >
                  <div>
                    <span className="font-medium text-black">{item.name}</span>
                    <span className="text-sm ml-2 text-black">
                      {item.type} - {item.distance}
                    </span>
                  </div>
                  <button
                    onClick={() => removeTransport(idx)}
                    className="text-red-500 hover:text-red-700"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hotels Section */}
      <div className="mt-8 border-t pt-6">
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold text-black">🏨 Nearby Hotels</h3>
          <p className="text-sm mt-1 text-black">
            Recommended accommodation options
          </p>
        </div>

        <div className="border rounded-xl p-4 border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <input
              type="text"
              value={newHotel.name}
              onChange={(e) =>
                setNewHotel({ ...newHotel, name: e.target.value })
              }
              className="w-full rounded-lg p-2 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="Hotel name"
            />
            <input
              type="text"
              value={newHotel.distance}
              onChange={(e) =>
                setNewHotel({ ...newHotel, distance: e.target.value })
              }
              className="w-full rounded-lg p-2 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="Distance"
            />
            <input
              type="text"
              value={newHotel.priceRange}
              onChange={(e) =>
                setNewHotel({ ...newHotel, priceRange: e.target.value })
              }
              className="w-full rounded-lg p-2 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
              placeholder="Price range"
            />
            <select
              value={newHotel.rating}
              onChange={(e) =>
                setNewHotel({ ...newHotel, rating: parseInt(e.target.value) })
              }
              className="w-full rounded-lg p-2 outline-none border border-gray-300 bg-gray-50 focus:bg-white focus:border-blue-500 transition text-black"
            >
              <option value={0}>Select Rating</option>
              <option value={3}>⭐⭐⭐ (3 Star)</option>
              <option value={4}>⭐⭐⭐⭐ (4 Star)</option>
              <option value={5}>⭐⭐⭐⭐⭐ (5 Star)</option>
            </select>
          </div>
          <button
            onClick={addHotel}
            className="px-4 py-2 rounded-lg text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
          >
            + Add Hotel
          </button>

          {formData.nearbyHotels.length > 0 && (
            <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
              {formData.nearbyHotels.map((item, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center p-2 rounded-lg bg-gray-50"
                >
                  <div>
                    <span className="font-medium text-black">{item.name}</span>
                    <span className="text-sm ml-2 text-black">
                      {item.distance} • {item.priceRange} •{" "}
                      {"⭐".repeat(item.rating)}
                    </span>
                  </div>
                  <button
                    onClick={() => removeHotel(idx)}
                    className="text-red-500 hover:text-red-700"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading && venues.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-black">Loading venues...</p>
          {debugInfo && <p className="mt-2 text-xs text-black">{debugInfo}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2 text-white">
            <RiMapPinLine className="inline mr-2" /> Venue Management
          </h3>
          <p className="text-sm mt-1 text-white">
            Manage conference venues, locations, amenities, and nearby
            facilities
          </p>
          {debugInfo && venues.length === 0 && !loading && (
            <p className="text-xs mt-1 text-orange-500">Debug: {debugInfo}</p>
          )}
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={fetchVenues}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition bg-gray-500 text-white hover:bg-gray-600"
          >
            <RiRefreshLine size={18} /> Refresh
          </button>
          <button
            onClick={seedDefaultVenue}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition bg-green-500 text-white hover:bg-green-600"
          >
            <RiFileTextLine size={18} /> Load Default Venue
          </button>
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (!showForm) resetForm();
              setEditing(null);
            }}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition bg-blue-600 text-white hover:bg-blue-700"
          >
            {showForm ? <RiCloseLine size={18} /> : <RiAddLine size={18} />}
            {showForm ? "Close Form" : "Add New Venue"}
          </button>
        </div>
      </div>

      {/* All Venues */}
      <div className="rounded-xl p-6 bg-white border border-gray-200 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2 text-black">
            <RiMapPinLine size={18} /> All Venues ({venues.length})
          </h3>
          <div className="flex gap-2 text-xs">
            <span className="px-2 py-1 rounded-full bg-green-100 text-green-700">
              Active: {activeVenues.length}
            </span>
            <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700">
              Inactive: {inactiveVenues.length}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {venues.length > 0 ? (
            venues
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((venue) => (
                <div
                  key={venue._id}
                  className={`rounded-xl p-5 border ${venue.isActive ? "bg-gray-50 border-gray-200" : "bg-gray-100 border-gray-300 opacity-60"}`}
                >
                  {/* Header Row */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-100">
                      <RiBuildingLine size={24} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-lg text-black">
                          {venue.name}
                        </h4>
                        {venue.isPrimary && (
                          <span className="px-2 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-600 whitespace-nowrap">
                            ⭐ PRIMARY
                          </span>
                        )}
                        {venue.isActive ? (
                          <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-600 whitespace-nowrap">
                            ✅ Active
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-500/20 text-gray-600 whitespace-nowrap">
                            ⛔ Inactive
                          </span>
                        )}
                        <span className="text-xs whitespace-nowrap text-black">
                          Order: {venue.order}
                        </span>
                      </div>
                      {venue.subtitle && (
                        <p className="text-sm text-black mt-0.5">
                          {venue.subtitle}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0 flex-wrap">
                      {!venue.isPrimary && venue.isActive && (
                        <button
                          onClick={() => setAsPrimary(venue)}
                          className="p-2 rounded bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                          title="Set as Primary"
                        >
                          <RiStarLine size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => toggleActive(venue)}
                        className={`p-2 rounded ${venue.isActive ? "bg-orange-100 text-orange-600 hover:bg-orange-200" : "bg-green-100 text-green-600 hover:bg-green-200"}`}
                        title={venue.isActive ? "Deactivate" : "Activate"}
                      >
                        {venue.isActive ? (
                          <RiEyeOffLine size={16} />
                        ) : (
                          <RiEyeLine size={16} />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setEditing(venue);
                          setFormData(venue);
                          setShowForm(true);
                        }}
                        className="p-2 rounded bg-blue-100 text-blue-600 hover:bg-blue-200"
                      >
                        <RiEditLine size={16} />
                      </button>
                      <button
                        onClick={() => deleteVenue(venue._id)}
                        className="p-2 rounded bg-red-100 text-red-600 hover:bg-red-200"
                      >
                        <RiDeleteBinLine size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  {venue.description && (
                    <p className="text-sm text-black mt-3">
                      {venue.description}
                    </p>
                  )}

                  {/* Stats Badges */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 whitespace-nowrap">
                      Capacity: {venue.capacity}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 whitespace-nowrap">
                      Halls: {venue.totalHalls}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 whitespace-nowrap">
                      Parking: {venue.parkingSpaces}
                    </span>
                    {venue.hasWiFi && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 whitespace-nowrap">
                        📶 WiFi
                      </span>
                    )}
                    {venue.isWheelchairAccessible && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 whitespace-nowrap">
                        ♿ Accessible
                      </span>
                    )}
                    {venue.hasCatering && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 whitespace-nowrap">
                        🍽️ Catering
                      </span>
                    )}
                    {venue.hasPowerBackup && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 whitespace-nowrap">
                        ⚡ Power Backup
                      </span>
                    )}
                  </div>

                  {/* Location & Contact */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <h5 className="text-xs font-bold uppercase tracking-wide text-black mb-1">
                        📍 Location
                      </h5>
                      <p className="text-sm text-black">
                        {venue.address && (
                          <>
                            {venue.address}
                            <br />
                          </>
                        )}
                        {venue.area && <>{venue.area}, </>}
                        {venue.city}, {venue.state}
                        {venue.pincode ? ` - ${venue.pincode}` : ""}
                        {venue.country && <>, {venue.country}</>}
                      </p>
                      {venue.coordinates && (
                        <p className="text-xs text-black mt-1">
                          Lat: {venue.coordinates.lat}, Lng:{" "}
                          {venue.coordinates.lng}
                        </p>
                      )}
                      {venue.slug && (
                        <p className="text-xs text-black mt-1">
                          Slug: {venue.slug}
                        </p>
                      )}
                    </div>

                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <h5 className="text-xs font-bold uppercase tracking-wide text-black mb-1">
                        📞 Contact
                      </h5>
                      {venue.contactPerson && (
                        <p className="text-sm text-black">
                          {venue.contactPerson}
                        </p>
                      )}
                      {venue.contactPhone && (
                        <p className="text-sm text-black">
                          📱 {venue.contactPhone}
                        </p>
                      )}
                      {venue.contactEmail && (
                        <p className="text-sm text-black">
                          ✉️ {venue.contactEmail}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Operating Hours */}
                  {venue.operatingHours && (
                    <div className="mt-4 bg-white rounded-lg p-3 border border-gray-200">
                      <h5 className="text-xs font-bold uppercase tracking-wide text-black mb-1">
                        ⏰ Operating Hours
                      </h5>
                      <div className="grid grid-cols-3 gap-2 text-sm text-black">
                        <div>
                          <span className="text-black">Weekdays:</span>{" "}
                          {venue.operatingHours.weekdays}
                        </div>
                        <div>
                          <span className="text-black">Saturday:</span>{" "}
                          {venue.operatingHours.saturday}
                        </div>
                        <div>
                          <span className="text-black">Sunday:</span>{" "}
                          {venue.operatingHours.sunday}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Airport Info */}
                  {venue.airportInfo?.name && (
                    <div className="mt-4 bg-white rounded-lg p-3 border border-gray-200">
                      <h5 className="text-xs font-bold uppercase tracking-wide text-black mb-1">
                        ✈️ Airport
                      </h5>
                      <p className="text-sm text-black">
                        {venue.airportInfo.name}
                      </p>
                      <p className="text-xs text-black">
                        {venue.airportInfo.distance} •{" "}
                        {venue.airportInfo.travelTime}{" "}
                        {venue.airportInfo.route
                          ? `via ${venue.airportInfo.route}`
                          : ""}
                      </p>
                    </div>
                  )}

                  {/* Amenities */}
                  {venue.amenities?.length > 0 && (
                    <div className="mt-4 bg-white rounded-lg p-3 border border-gray-200">
                      <h5 className="text-xs font-bold uppercase tracking-wide text-black mb-2">
                        ✨ Amenities ({venue.amenities.length})
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {venue.amenities
                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((a, idx) => (
                            <span
                              key={idx}
                              className="text-xs px-2 py-1 rounded-full bg-gray-100 text-black"
                            >
                              {a.name}
                              {a.description ? ` – ${a.description}` : ""}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Nearby Transport */}
                  {venue.nearbyTransport?.length > 0 && (
                    <div className="mt-4 bg-white rounded-lg p-3 border border-gray-200">
                      <h5 className="text-xs font-bold uppercase tracking-wide text-black mb-2">
                        🚍 Nearby Transport ({venue.nearbyTransport.length})
                      </h5>
                      <div className="flex flex-col gap-1">
                        {venue.nearbyTransport
                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((t, idx) => (
                            <p key={idx} className="text-sm text-black">
                              {t.type === "metro"
                                ? "🚇"
                                : t.type === "bus"
                                  ? "🚌"
                                  : t.type === "train"
                                    ? "🚂"
                                    : "🚕"}{" "}
                              {t.name} — {t.distance}{" "}
                              {t.details ? `(${t.details})` : ""}
                            </p>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Nearby Hotels */}
                  {venue.nearbyHotels?.length > 0 && (
                    <div className="mt-4 bg-white rounded-lg p-3 border border-gray-200">
                      <h5 className="text-xs font-bold uppercase tracking-wide text-black mb-2">
                        🏨 Nearby Hotels ({venue.nearbyHotels.length})
                      </h5>
                      <div className="flex flex-col gap-1">
                        {venue.nearbyHotels
                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((h, idx) => (
                            <p key={idx} className="text-sm text-black">
                              {h.name} — {h.distance} • {h.priceRange}{" "}
                              {h.rating ? `• ${"⭐".repeat(h.rating)}` : ""}
                            </p>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Parking Info */}
                  {venue.parkingInfo && (
                    <div className="mt-4 bg-blue-50 rounded-lg p-3 border border-blue-100">
                      <p className="text-sm text-black">
                        <span className="font-semibold text-blue-600">
                          💡 Parking:
                        </span>{" "}
                        {venue.parkingInfo}
                      </p>
                    </div>
                  )}
                </div>
              ))
          ) : (
            <div className="text-center py-8 rounded-xl bg-gray-50">
              <RiBuildingLine
                size={48}
                className="mx-auto mb-2 text-gray-400"
              />
              <p className="text-black">No venues available</p>
              <p className="text-sm mt-1 text-black">
                Click "Add New Venue" or "Load Default Venue" to get started
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal Form */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowForm(false);
              setEditing(null);
            }
          }}
        >
          <div className="rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col bg-white">
            {/* Modal Header */}
            <div className="px-8 py-5 flex justify-between items-center flex-shrink-0 bg-gradient-to-r from-blue-600 to-indigo-600">
              <h2 className="text-2xl font-bold flex items-center gap-2 text-white">
                <RiMapPinLine size={24} />
                {editing ? "Edit Venue" : "Add New Venue"}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                }}
                className="hover:opacity-80 p-1 rounded-full hover:bg-white/20 text-white"
              >
                <RiCloseLine size={24} />
              </button>
            </div>

            {/* Content - All sections in one scrollable view, no tabs */}
            <div
              className="flex-1 overflow-y-auto p-8 bg-white"
              style={{ maxHeight: "calc(90vh - 140px)" }}
            >
              {renderAllSections()}
            </div>

            {/* Modal Footer */}
            <div className="px-8 py-5 flex justify-end gap-3 flex-shrink-0 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                }}
                className="px-6 py-2 rounded-xl bg-gray-200 text-black hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={saveVenue}
                disabled={loading}
                className="px-6 py-2 rounded-xl flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 transition"
              >
                <RiSaveLine size={18} />{" "}
                {loading ? "Saving..." : editing ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// SummitManagement.jsx - Complete corrected PdfScheduleTab component

const PdfScheduleTab = ({ token, eventId }) => {
  const [pdfFile, setPdfFile] = useState(null);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [existingPdf, setExistingPdf] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Fetch PDF schedule when eventId changes
  useEffect(() => {
    if (eventId) {
      fetchExistingPdf();
    } else {
      console.warn("⚠️ No eventId provided to PdfScheduleTab");
    }
  }, [eventId]);

  // ✅ Corrected fetchExistingPdf - handles 404 gracefully
  const fetchExistingPdf = async () => {
    if (!eventId) {
      console.warn("❌ No eventId provided for fetchExistingPdf");
      return;
    }

    try {
      setLoading(true);
      console.log(`📡 Fetching PDF schedule for event: ${eventId}`);

      const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
      const { data } = await axios.get(
        `${API_BASE}/api/pdf-schedule/event/${eventId}`,
        config,
      );

      console.log("📦 Response:", data);

      if (data.success) {
        setExistingPdf(data.data);
        setDescription(data.data?.description || "");
        setPdfUrl(data.data?.pdfUrl || data.data?.url || null);
      } else {
        setExistingPdf(null);
        setPdfUrl(null);
      }
    } catch (error) {
      // ✅ 404 is expected when no PDF exists - this is normal
      console.log(
        `📋 No PDF schedule found for event: ${eventId} (This is normal if no PDF uploaded yet)`,
      );

      // Only show error toast for actual server errors (500, etc.)
      if (error.response?.status !== 404) {
        console.error("❌ Error fetching PDF:", error);
        toast.error("Failed to load PDF schedule");
      }
      setExistingPdf(null);
      setPdfUrl(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    setPdfFile(file);
  };

  // Updated save with eventId
  // In PdfScheduleTab component - save function
  const save = async () => {
    if (!eventId) {
      toast.error("No event specified. Please select an event first.");
      return;
    }

    if (!pdfFile && !existingPdf) {
      toast.error("Please select a PDF file to upload");
      return;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (pdfFile) {
        const formData = new FormData();
        formData.append("pdf", pdfFile);
        formData.append("description", description);
        formData.append("eventId", eventId);

        console.log(`📤 Uploading PDF file: ${pdfFile.name}`);

        const response = await axios.post(
          `${API_BASE}/api/pdf-schedule/event/${eventId}`,
          formData,
          {
            ...config,
            headers: {
              ...config.headers,
              "Content-Type": "multipart/form-data",
            },
          },
        );

        if (response.data.success) {
          toast.success("PDF uploaded successfully!");
          console.log("✅ PDF uploaded:", response.data);
        } else {
          toast.error(response.data.message || "Failed to upload PDF");
        }
      } else if (description !== existingPdf?.description) {
        await axios.put(
          `${API_BASE}/api/pdf-schedule/description/${eventId}`,
          { description, eventId },
          config,
        );
        toast.success("Description updated");
      }

      fetchExistingPdf();
      setPdfFile(null);
    } catch (err) {
      console.error("❌ Error saving PDF:", err);
      toast.error(err.response?.data?.message || "Failed to upload PDF");
    } finally {
      setLoading(false);
    }
  };

  // Updated deletePdf with eventId
  const deletePdf = async () => {
    if (!eventId) {
      toast.error("No event specified. Please select an event first.");
      return;
    }

    if (!window.confirm("Delete this PDF schedule?")) return;
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(
        `${API_BASE}/api/pdf-schedule/event/${eventId}`,
        config,
      );

      toast.success("PDF deleted successfully");
      setExistingPdf(null);
      setPdfUrl(null);
      setDescription("");
      fetchExistingPdf();
    } catch (error) {
      console.error("❌ Error deleting PDF:", error);
      toast.error(error.response?.data?.message || "Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    }
  };

  // No event ID error state
  if (!eventId) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: T.white,
          padding: "32px",
          borderRadius: "16px",
          boxShadow: T.shadowMd,
          border: `1px solid ${T.borderLight}`,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 16,
            background: T.softTeal,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <RiFilePdfLine size={28} color={T.primary} />
        </div>
        <h3
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: T.text,
            marginBottom: 8,
          }}
        >
          No Event Selected
        </h3>
        <p style={{ color: T.textMuted }}>
          Please select an event to manage PDF schedule.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: T.white,
        padding: "32px",
        borderRadius: "16px",
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy }}>
          📄 PDF Schedule Upload
        </h2>
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              color: T.textMuted,
              background: T.softBlue,
              padding: "4px 12px",
              borderRadius: "999px",
            }}
          >
            {pdfUrl ? "✅ Uploaded" : "⚠️ Not Uploaded"}
          </span>
          <button
            onClick={fetchExistingPdf}
            style={{
              fontSize: "12px",
              color: T.primary,
              background: T.offWhite,
              padding: "4px 12px",
              borderRadius: "999px",
              border: `1px solid ${T.border}`,
              cursor: "pointer",
            }}
          >
            <RiRefreshLine
              size={14}
              style={{ display: "inline", marginRight: "4px" }}
            />{" "}
            Refresh
          </button>
        </div>
      </div>
      <p style={{ color: T.textMuted, marginBottom: "32px", fontSize: "14px" }}>
        Upload a PDF schedule that users can download from the conference page.
      </p>

      {existingPdf && pdfUrl && (
        <div
          style={{
            background: T.offWhite,
            padding: "16px",
            borderRadius: "12px",
            marginBottom: "24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <p style={{ color: T.text, fontWeight: 500 }}>
              📄 Current Schedule PDF
            </p>
            <p style={{ color: T.textMuted, fontSize: "12px" }}>
              Uploaded:{" "}
              {existingPdf.updatedAt
                ? new Date(existingPdf.updatedAt).toLocaleDateString()
                : "N/A"}
            </p>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={downloadPdf}
              style={{
                padding: "8px 16px",
                background: T.softBlue,
                color: T.primary,
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontWeight: 600,
                fontSize: "13px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = T.primary;
                e.currentTarget.style.color = T.white;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = T.softBlue;
                e.currentTarget.style.color = T.primary;
              }}
            >
              <RiEyeLine size={16} /> Preview
            </button>
            <button
              onClick={deletePdf}
              style={{
                padding: "8px 16px",
                background: "#FEE2E2",
                color: "#EF4444",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontWeight: 600,
                fontSize: "13px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#FECACA";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#FEE2E2";
              }}
            >
              <RiDeleteBinLine size={16} /> Delete
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <div>
          <label
            style={{
              display: "block",
              color: T.textMid,
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows="4"
            style={{
              width: "100%",
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: "12px",
              padding: "12px",
              color: T.text,
              fontFamily: "'Inter', sans-serif",
              fontSize: "14px",
              resize: "vertical",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = T.primary)}
            onBlur={(e) => (e.target.style.borderColor = T.border)}
            placeholder="Enter a description for the schedule PDF..."
          />
        </div>

        <div>
          <label
            style={{
              display: "block",
              color: T.textMid,
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            Upload PDF File
          </label>
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            style={{
              width: "100%",
              padding: "8px",
              background: T.white,
              borderRadius: "8px",
              border: `1px solid ${T.border}`,
            }}
          />
          {pdfFile && (
            <p style={{ color: "#4CAF50", fontSize: "13px", marginTop: "8px" }}>
              Selected: {pdfFile.name}
            </p>
          )}
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button
            onClick={save}
            disabled={loading}
            style={{
              padding: "12px 32px",
              background: T.gradPrimary,
              color: T.white,
              border: "none",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: 600,
              opacity: loading ? 0.7 : 1,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = T.shadowMd;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <RiUploadLine size={18} />
            {loading
              ? "Uploading..."
              : existingPdf
                ? "Update PDF"
                : "Upload PDF"}
          </button>
        </div>
      </div>

      <div
        style={{
          marginTop: "32px",
          padding: "16px",
          background: `${T.primary}10`,
          borderRadius: "12px",
          border: `1px solid ${T.primary}20`,
        }}
      >
        <h4 style={{ color: T.primary, fontWeight: 600, marginBottom: "8px" }}>
          💡 Schedule PDF Management:
        </h4>
        <ul
          style={{
            color: T.textMuted,
            fontSize: "13px",
            margin: 0,
            paddingLeft: "20px",
            lineHeight: "1.8",
          }}
        >
          <li>• Upload a single PDF schedule file</li>
          <li>• Add a description that will appear with the download link</li>
          <li>• Users can download the schedule from the conference page</li>
          <li>• Maximum file size: 10MB</li>
        </ul>
      </div>
    </motion.div>
  );
};
/* ══════════════════════════════════════════════════════════════════════
   SUMMIT SPEAKERS TAB
   ══════════════════════════════════════════════════════════════════════ */
const SummitSpeakersTab = ({ token, eventId }) => {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    company: "",
    bio: "",
    imageUrl: "",
    linkedinUrl: "",
    twitterUrl: "",
    website: "",
    expertise: [],
    achievements: [],
    publications: [],
    speakerType: "plenary", // can be "plenary", "keynote", or "invited"
    isFeatured: false,
    isActive: true,
    order: 0,
    color: "#2A9D8F",
  });
  const [newExpertise, setNewExpertise] = useState("");
  const [newAchievement, setNewAchievement] = useState("");
  const [newPublication, setNewPublication] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [filterType, setFilterType] = useState("all");

  const colorOptions = [
    "#2A9D8F",
    "#3BBCAE",
    "#D4A017",
    "#E8B520",
    "#8B5CF6",
    "#EC4899",
    "#EF4444",
    "#F59E0B",
    "#10B981",
    "#3B82F6",
    "#6366F1",
    "#14B8A6",
    "#F472B6",
    "#8B5CF6",
    "#0EA5E9",
    "#F97316",
  ];

  useEffect(() => {
    if (eventId) fetchSpeakers();
  }, [eventId]);

  const fetchSpeakers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/summit-speakers/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (data.success) {
        let speakersArray = data.speakers || data.data || data.results || [];
        if (Array.isArray(data)) speakersArray = data;
        const speakersWithArrays = speakersArray.map((speaker) => ({
          ...speaker,
          expertise: speaker.expertise || [],
          achievements: speaker.achievements || [],
          publications: speaker.publications || [],
          color: speaker.color || "#2A9D8F",
        }));
        setSpeakers(speakersWithArrays);
      } else {
        console.error("API returned success=false:", data);
      }
    } catch (error) {
      console.error("Error fetching speakers:", error);
      toast.error("Failed to load speakers");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const addExpertise = () => {
    if (newExpertise.trim()) {
      setFormData({
        ...formData,
        expertise: [...(formData.expertise || []), newExpertise.trim()],
      });
      setNewExpertise("");
    }
  };

  const removeExpertise = (index) => {
    const updated = (formData.expertise || []).filter((_, i) => i !== index);
    setFormData({ ...formData, expertise: updated });
  };

  const addAchievement = () => {
    if (newAchievement.trim()) {
      setFormData({
        ...formData,
        achievements: [...(formData.achievements || []), newAchievement.trim()],
      });
      setNewAchievement("");
    }
  };

  const removeAchievement = (index) => {
    const updated = (formData.achievements || []).filter((_, i) => i !== index);
    setFormData({ ...formData, achievements: updated });
  };

  const addPublication = () => {
    if (newPublication.trim()) {
      setFormData({
        ...formData,
        publications: [...(formData.publications || []), newPublication.trim()],
      });
      setNewPublication("");
    }
  };

  const removePublication = (index) => {
    const updated = (formData.publications || []).filter((_, i) => i !== index);
    setFormData({ ...formData, publications: updated });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      role: "",
      company: "",
      bio: "",
      imageUrl: "",
      linkedinUrl: "",
      twitterUrl: "",
      website: "",
      expertise: [],
      achievements: [],
      publications: [],
      speakerType: "plenary",
      isFeatured: false,
      isActive: true,
      order: 0,
      color: "#2A9D8F",
    });
    setImageFile(null);
    setImagePreview(null);
    setEditing(null);
  };

  const saveSpeaker = async () => {
    if (
      !formData.name ||
      !formData.role ||
      !formData.company ||
      !formData.bio
    ) {
      toast.error("Name, role, company, and bio are required");
      return;
    }
    setLoading(true);
    try {
      let imageUrl = formData.imageUrl;
      if (imageFile) {
        const uploadFormData = new FormData();
        uploadFormData.append("image", imageFile);
        const uploadResponse = await axios.post(
          `${API_BASE}/api/upload/speaker-image`,
          uploadFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        if (uploadResponse.data.success)
          imageUrl = uploadResponse.data.imageUrl;
      }

      const payload = {
        ...formData,
        imageUrl,
        eventId,
        color: formData.color || "#2A9D8F",
      };
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (editing) {
        await axios.put(
          `${API_BASE}/api/summit-speakers/${editing._id}`,
          payload,
          config,
        );
        toast.success("Speaker updated successfully");
      } else {
        await axios.post(`${API_BASE}/api/summit-speakers`, payload, config);
        toast.success("Speaker created successfully");
      }
      resetForm();
      setShowForm(false);
      await fetchSpeakers();
    } catch (error) {
      console.error("Error saving speaker:", error);
      toast.error(error.response?.data?.message || "Failed to save speaker");
    } finally {
      setLoading(false);
    }
  };

  const deleteSpeaker = async (id) => {
    if (!window.confirm("Delete this speaker? This action cannot be undone."))
      return;
    try {
      await axios.delete(`${API_BASE}/api/summit-speakers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Speaker deleted successfully");
      fetchSpeakers();
    } catch (error) {
      console.error("Error deleting speaker:", error);
      toast.error("Failed to delete speaker");
    }
  };

  const toggleActive = async (speaker) => {
    try {
      await axios.put(
        `${API_BASE}/api/summit-speakers/${speaker._id}`,
        { ...speaker, isActive: !speaker.isActive, eventId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Speaker ${!speaker.isActive ? "activated" : "deactivated"}`,
      );
      fetchSpeakers();
    } catch (error) {
      console.error("Error toggling:", error);
      toast.error("Failed to update");
    }
  };

  const toggleFeatured = async (speaker) => {
    try {
      await axios.put(
        `${API_BASE}/api/summit-speakers/${speaker._id}/toggle-featured`,
        { eventId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Speaker ${!speaker.isFeatured ? "featured" : "unfeatured"}`,
      );
      fetchSpeakers();
    } catch (error) {
      console.error("Error toggling featured:", error);
      toast.error("Failed to update");
    }
  };

  // ── Counts for each type ──
  const plenaryCount = speakers.filter(
    (s) => s.isActive && s.speakerType === "plenary",
  ).length;
  const keynoteCount = speakers.filter(
    (s) => s.isActive && s.speakerType === "keynote",
  ).length;
  const invitedCount = speakers.filter(
    (s) => s.isActive && s.speakerType === "invited",
  ).length;
  const totalActive = plenaryCount + keynoteCount + invitedCount;

  const getFilteredSpeakers = () => {
    let filtered = speakers.filter((s) => s.isActive);
    if (filterType === "plenary")
      filtered = filtered.filter((s) => s.speakerType === "plenary");
    else if (filterType === "keynote")
      filtered = filtered.filter((s) => s.speakerType === "keynote");
    else if (filterType === "invited")
      filtered = filtered.filter((s) => s.speakerType === "invited");
    return filtered.sort((a, b) => a.order - b.order);
  };

  const inactiveSpeakers = speakers.filter((s) => !s.isActive);

  // ── Speaker type label and icon helpers ──
  const getTypeIcon = (type) => {
    switch (type) {
      case "plenary":
        return "🎤";
      case "keynote":
        return "🗝️";
      case "invited":
        return "📢";
      default:
        return "👤";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "plenary":
        return "purple";
      case "keynote":
        return "green";
      case "invited":
        return "blue";
      default:
        return "gray";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: T.white }}>
            Summit Speakers Management
          </h3>
          <p className="text-sm mt-1" style={{ color: T.white }}>
            Manage all speakers for this event
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (!showForm) resetForm();
            setEditing(null);
          }}
          className="px-4 py-2 rounded-lg flex items-center gap-2 transition"
          style={{ background: T.gradPrimary, color: T.white }}
        >
          {showForm ? <RiCloseLine size={18} /> : <RiAddLine size={18} />}
          {showForm ? "Close Form" : "Add New Speaker"}
        </button>
      </div>

      {/* ── Filter Tabs ── */}
      <div
        className="flex gap-2 pb-2 flex-wrap"
        style={{ borderBottom: `1px solid ${T.borderLight}` }}
      >
        <button
          onClick={() => setFilterType("all")}
          className={`px-4 py-2 rounded-lg text-sm transition ${
            filterType === "all"
              ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
              : "text-gray-400 hover:text-white"
          }`}
        >
          All ({totalActive})
        </button>
        <button
          onClick={() => setFilterType("plenary")}
          className={`px-4 py-2 rounded-lg text-sm transition flex items-center gap-2 ${
            filterType === "plenary"
              ? "bg-purple-500/20 text-purple-400 border border-purple-500/30"
              : "text-gray-400 hover:text-white"
          }`}
        >
          {getTypeIcon("plenary")} Plenary ({plenaryCount})
        </button>
        <button
          onClick={() => setFilterType("keynote")}
          className={`px-4 py-2 rounded-lg text-sm transition flex items-center gap-2 ${
            filterType === "keynote"
              ? "bg-green-500/20 text-green-400 border border-green-500/30"
              : "text-gray-400 hover:text-white"
          }`}
        >
          {getTypeIcon("keynote")} Keynote ({keynoteCount})
        </button>
        <button
          onClick={() => setFilterType("invited")}
          className={`px-4 py-2 rounded-lg text-sm transition flex items-center gap-2 ${
            filterType === "invited"
              ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
              : "text-gray-400 hover:text-white"
          }`}
        >
          {getTypeIcon("invited")} Invited ({invitedCount})
        </button>
      </div>

      {/* ── Form ── */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-xl p-6 overflow-hidden"
            style={{
              background: T.white,
              border: `1px solid ${T.borderLight}`,
            }}
          >
            <h4 className="font-semibold mb-4" style={{ color: T.navy }}>
              {editing ? "Edit Speaker" : "Create New Speaker"}
            </h4>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Dr. John Doe"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Role/Title *
                  </label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Professor of Immunology"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Company/Institution *
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) =>
                      setFormData({ ...formData, company: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Harvard University"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Bio *
                  </label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) =>
                      setFormData({ ...formData, bio: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    rows="6"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Speaker biography (no character limit)"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Image */}
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Profile Image
                  </label>
                  <div className="flex items-start gap-4">
                    <div
                      className="w-24 h-24 border border-dashed rounded-xl flex items-center justify-center overflow-hidden"
                      style={{ background: T.offWhite, borderColor: T.border }}
                    >
                      {imagePreview || formData.imageUrl ? (
                        <img
                          src={imagePreview || formData.imageUrl}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <RiUserStarLine
                          size={32}
                          style={{ color: T.textMuted }}
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        id="speaker-image"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="speaker-image"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition text-sm"
                        style={{
                          background: T.offWhite,
                          border: `1px solid ${T.border}`,
                          color: T.primary,
                        }}
                      >
                        <RiUploadLine size={16} />{" "}
                        {imageFile ? "Change Image" : "Upload Image"}
                      </label>
                      {formData.imageUrl && !imageFile && (
                        <p
                          className="text-xs mt-2"
                          style={{ color: T.textMuted }}
                        >
                          Current image will be kept if no new image selected
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Speaker Type - now with Invited option */}
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Speaker Type *
                  </label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="speakerType"
                        value="plenary"
                        checked={formData.speakerType === "plenary"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            speakerType: e.target.value,
                          })
                        }
                        className="w-4 h-4 accent-purple-500"
                      />
                      <span
                        className="flex items-center gap-1"
                        style={{ color: T.textMid }}
                      >
                        <span className="text-purple-400">🎤</span> Plenary
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="speakerType"
                        value="keynote"
                        checked={formData.speakerType === "keynote"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            speakerType: e.target.value,
                          })
                        }
                        className="w-4 h-4 accent-green-500"
                      />
                      <span
                        className="flex items-center gap-1"
                        style={{ color: T.textMid }}
                      >
                        <span className="text-green-400">🗝️</span> Keynote
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="speakerType"
                        value="invited"
                        checked={formData.speakerType === "invited"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            speakerType: e.target.value,
                          })
                        }
                        className="w-4 h-4 accent-blue-500"
                      />
                      <span
                        className="flex items-center gap-1"
                        style={{ color: T.textMid }}
                      >
                        <span className="text-blue-400">📢</span> Invited
                      </span>
                    </label>
                  </div>
                </div>

                {/* Color picker */}
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Speaker Color
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {colorOptions.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          backgroundColor: color,
                          border:
                            formData.color === color
                              ? "3px solid white"
                              : "2px solid transparent",
                          outline:
                            formData.color === color
                              ? `2px solid ${T.navy}`
                              : "none",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          boxShadow:
                            formData.color === color ? T.shadowMd : "none",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.1)";
                          e.currentTarget.style.zIndex = 10;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.zIndex = 0;
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-xs mt-2" style={{ color: T.textMuted }}>
                    Selected color:{" "}
                    <span style={{ color: formData.color, fontWeight: 700 }}>
                      {formData.color}
                    </span>
                  </p>
                </div>

                {/* Social links */}
                <div>
                  <label
                    className="block mb-2 text-sm flex items-center gap-2"
                    style={{ color: T.textMid }}
                  >
                    <RiLinkedinBoxLine /> LinkedIn URL
                  </label>
                  <input
                    type="url"
                    value={formData.linkedinUrl}
                    onChange={(e) =>
                      setFormData({ ...formData, linkedinUrl: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>

                <div>
                  <label
                    className="block mb-2 text-sm flex items-center gap-2"
                    style={{ color: T.textMid }}
                  >
                    <RiTwitterXLine /> Twitter/X URL
                  </label>
                  <input
                    type="url"
                    value={formData.twitterUrl}
                    onChange={(e) =>
                      setFormData({ ...formData, twitterUrl: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="https://twitter.com/username"
                  />
                </div>

                <div>
                  <label
                    className="block mb-2 text-sm flex items-center gap-2"
                    style={{ color: T.textMid }}
                  >
                    <RiGlobalLine /> Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) =>
                      setFormData({ ...formData, website: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="https://example.com"
                  />
                </div>

                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Expertise, Achievements, Publications (same as before) */}
            <div className="mt-6">
              <label
                className="block mb-2 text-sm flex items-center gap-2"
                style={{ color: T.textMid }}
              >
                <RiPriceTagLine /> Areas of Expertise (Point-wise)
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newExpertise}
                  onChange={(e) => setNewExpertise(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addExpertise()}
                  className="flex-1 rounded-lg p-3"
                  style={{
                    background: T.offWhite,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                  }}
                  placeholder="e.g., Cancer Immunology"
                />
                <button
                  onClick={addExpertise}
                  className="px-4 py-2 rounded-lg"
                  style={{ background: `${T.primary}20`, color: T.primary }}
                >
                  <RiAddLine size={20} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(formData.expertise || []).map((exp, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    style={{
                      background: `${T.primary}20`,
                      color: T.primaryLight,
                    }}
                  >
                    ✓ {exp}
                    <button
                      onClick={() => removeExpertise(idx)}
                      className="hover:opacity-70"
                      style={{ color: "#EF4444" }}
                    >
                      <RiCloseLine size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label
                className="block mb-2 text-sm flex items-center gap-2"
                style={{ color: T.textMid }}
              >
                <RiAwardLine /> Key Achievements (Point-wise)
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newAchievement}
                  onChange={(e) => setNewAchievement(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addAchievement()}
                  className="flex-1 rounded-lg p-3"
                  style={{
                    background: T.offWhite,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                  }}
                  placeholder="e.g., Nobel Prize Winner 2020"
                />
                <button
                  onClick={addAchievement}
                  className="px-4 py-2 rounded-lg"
                  style={{ background: `${T.primary}20`, color: T.primary }}
                >
                  <RiAddLine size={20} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(formData.achievements || []).map((ach, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    style={{
                      background: `${T.accentGold}20`,
                      color: T.accentGold,
                    }}
                  >
                    🏆 {ach}
                    <button
                      onClick={() => removeAchievement(idx)}
                      className="hover:opacity-70"
                      style={{ color: "#EF4444" }}
                    >
                      <RiCloseLine size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label
                className="block mb-2 text-sm flex items-center gap-2"
                style={{ color: T.textMid }}
              >
                <RiFileTextLine /> Key Publications (Point-wise)
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newPublication}
                  onChange={(e) => setNewPublication(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addPublication()}
                  className="flex-1 rounded-lg p-3"
                  style={{
                    background: T.offWhite,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                  }}
                  placeholder="e.g., Nature Immunology 2024: Vol 25, pp 123-135"
                />
                <button
                  onClick={addPublication}
                  className="px-4 py-2 rounded-lg"
                  style={{ background: `${T.primary}20`, color: T.primary }}
                >
                  <RiAddLine size={20} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(formData.publications || []).map((pub, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    style={{ background: `${T.primary}10`, color: T.textMid }}
                  >
                    📄 {pub}
                    <button
                      onClick={() => removePublication(idx)}
                      className="hover:opacity-70"
                      style={{ color: "#EF4444" }}
                    >
                      <RiCloseLine size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-6">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.isFeatured}
                  onChange={(e) =>
                    setFormData({ ...formData, isFeatured: e.target.checked })
                  }
                  className="w-5 h-5 accent-yellow-500"
                />
                <span
                  className="flex items-center gap-1"
                  style={{ color: T.textMid }}
                >
                  <RiStarFill className="text-yellow-400" /> Featured Speaker
                </span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="w-5 h-5 accent-green-500"
                />
                <span style={{ color: T.textMid }}>
                  Active (visible on website)
                </span>
              </label>
            </div>

            <div
              className="flex gap-3 mt-6 pt-4"
              style={{ borderTop: `1px solid ${T.border}` }}
            >
              <button
                onClick={saveSpeaker}
                disabled={loading}
                className="px-6 py-2 rounded-lg flex items-center gap-2"
                style={{ background: T.gradPrimary, color: T.white }}
              >
                <RiSaveLine size={18} />
                {loading
                  ? "Saving..."
                  : editing
                    ? "Update Speaker"
                    : "Create Speaker"}
              </button>
              {editing && (
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditing(null);
                    resetForm();
                  }}
                  className="px-6 py-2 rounded-lg"
                  style={{ background: "#E5E7EB", color: T.textMid }}
                >
                  Cancel
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Active Speakers List ── */}
      <div
        className="rounded-xl p-6"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold" style={{ color: T.navy }}>
            {filterType === "all" && `✅ All Active Speakers (${totalActive})`}
            {filterType === "plenary" &&
              `🎤 Plenary Speakers (${plenaryCount})`}
            {filterType === "keynote" &&
              `🗝️ Keynote Speakers (${keynoteCount})`}
            {filterType === "invited" &&
              `📢 Invited Speakers (${invitedCount})`}
          </h3>
          {filterType !== "all" && (
            <button
              onClick={() => setFilterType("all")}
              className="text-sm hover:underline"
              style={{ color: T.primary }}
            >
              View All Speakers →
            </button>
          )}
        </div>

        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {getFilteredSpeakers().map((speaker) => (
            <div
              key={speaker._id}
              className="flex items-center gap-4 p-4 rounded-lg transition"
              style={{
                background: T.offWhite,
                borderLeft: `4px solid ${speaker.color || "#2A9D8F"}`,
              }}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden"
                style={{
                  border: `2px solid ${speaker.color || "#2A9D8F"}`,
                }}
              >
                <img
                  src={
                    speaker.imageUrl ||
                    `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      speaker.name,
                    )}&background=2A9D8F&color=fff`
                  }
                  alt={speaker.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold" style={{ color: T.text }}>
                    {speaker.name}
                  </h4>
                  <span
                    className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 ${
                      speaker.speakerType === "plenary"
                        ? "bg-purple-500/20 text-purple-400"
                        : speaker.speakerType === "keynote"
                          ? "bg-green-500/20 text-green-400"
                          : "bg-blue-500/20 text-blue-400"
                    }`}
                  >
                    {speaker.speakerType === "plenary" && "🎤 Plenary"}
                    {speaker.speakerType === "keynote" && "🗝️ Keynote"}
                    {speaker.speakerType === "invited" && "📢 Invited"}
                  </span>
                  {speaker.isFeatured && (
                    <span
                      className="px-2 py-0.5 rounded text-xs flex items-center gap-1"
                      style={{
                        background: `${T.accentGold}20`,
                        color: T.accentGold,
                      }}
                    >
                      <RiStarFill size={10} /> Featured
                    </span>
                  )}
                  <span className="text-xs" style={{ color: T.textMuted }}>
                    Order: {speaker.order}
                  </span>
                  <span
                    className="px-2 py-0.5 rounded text-xs flex items-center gap-1"
                    style={{
                      background: `${speaker.color || "#2A9D8F"}20`,
                      color: speaker.color || "#2A9D8F",
                    }}
                  >
                    <span
                      className="w-2 h-2 rounded-full inline-block"
                      style={{ background: speaker.color || "#2A9D8F" }}
                    />{" "}
                    Color
                  </span>
                </div>
                <p className="text-sm" style={{ color: T.textMuted }}>
                  {speaker.role} at {speaker.company}
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(speaker.expertise || []).slice(0, 3).map((exp, idx) => (
                    <span
                      key={idx}
                      className="text-xs px-2 py-0.5 rounded"
                      style={{
                        background: `${speaker.color || "#2A9D8F"}20`,
                        color: speaker.color || "#2A9D8F",
                      }}
                    >
                      ✓ {exp}
                    </span>
                  ))}
                  {(speaker.expertise || []).length > 3 && (
                    <span className="text-xs" style={{ color: T.textMuted }}>
                      +{(speaker.expertise || []).length - 3}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleFeatured(speaker)}
                  className={`p-2 rounded transition ${
                    speaker.isFeatured ? "text-yellow-400" : "text-gray-500"
                  }`}
                  style={{
                    background: speaker.isFeatured
                      ? `${T.accentGold}20`
                      : T.white,
                  }}
                  title={
                    speaker.isFeatured
                      ? "Remove from featured"
                      : "Add to featured"
                  }
                >
                  <RiStarLine size={16} />
                </button>
                <button
                  onClick={() => toggleActive(speaker)}
                  className="p-2 rounded"
                  title="Deactivate"
                  style={{
                    color: T.accentGold,
                    background: `${T.accentGold}20`,
                  }}
                >
                  <RiEyeOffLine size={16} />
                </button>
                <button
                  onClick={() => {
                    setEditing(speaker);
                    setFormData(speaker);
                    setImagePreview(speaker.imageUrl);
                    setShowForm(true);
                  }}
                  className="p-2 rounded"
                  style={{ color: T.primary, background: `${T.primary}20` }}
                >
                  <RiEditLine size={16} />
                </button>
                <button
                  onClick={() => deleteSpeaker(speaker._id)}
                  className="p-2 rounded"
                  style={{ color: "#EF4444", background: "#FEE2E2" }}
                >
                  <RiDeleteBinLine size={16} />
                </button>
              </div>
            </div>
          ))}

          {getFilteredSpeakers().length === 0 && (
            <p className="text-center py-4" style={{ color: T.textMuted }}>
              {filterType === "all" &&
                "No active speakers. Add speakers above and activate them."}
              {filterType === "plenary" &&
                "No plenary speakers. Add a speaker and select 'Plenary Speaker' type."}
              {filterType === "keynote" &&
                "No keynote speakers. Add a speaker and select 'Keynote Speaker' type."}
              {filterType === "invited" &&
                "No invited speakers. Add a speaker and select 'Invited Speaker' type."}
            </p>
          )}
        </div>
      </div>

      {/* ── Inactive Speakers ── */}
      {inactiveSpeakers.length > 0 && (
        <div
          className="rounded-xl p-6 opacity-60"
          style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: T.navy }}>
            🚫 Inactive Speakers ({inactiveSpeakers.length})
          </h3>
          <div className="space-y-3">
            {inactiveSpeakers.map((speaker) => (
              <div
                key={speaker._id}
                className="flex items-center gap-4 p-3 rounded-lg opacity-50"
                style={{
                  background: T.offWhite,
                  borderLeft: `4px solid ${speaker.color || "#2A9D8F"}`,
                }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: T.white }}
                >
                  <RiUserStarLine size={20} style={{ color: T.textMuted }} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold" style={{ color: T.text }}>
                    {speaker.name}
                  </p>
                  <p className="text-sm" style={{ color: T.textMuted }}>
                    {speaker.role} at {speaker.company}
                  </p>
                  <span
                    className={`text-xs px-2 py-0.5 rounded inline-block mt-1 ${
                      speaker.speakerType === "plenary"
                        ? "bg-purple-500/20 text-purple-400"
                        : speaker.speakerType === "keynote"
                          ? "bg-green-500/20 text-green-400"
                          : "bg-blue-500/20 text-blue-400"
                    }`}
                  >
                    {speaker.speakerType === "plenary" && "🎤 Plenary"}
                    {speaker.speakerType === "keynote" && "🗝️ Keynote"}
                    {speaker.speakerType === "invited" && "📢 Invited"}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(speaker)}
                    className="p-2 rounded"
                    title="Activate"
                    style={{ color: "#4CAF50", background: "#E8F5E9" }}
                  >
                    <RiEyeLine size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(speaker);
                      setFormData(speaker);
                      setImagePreview(speaker.imageUrl);
                      setShowForm(true);
                    }}
                    className="p-2 rounded"
                    style={{ color: T.primary, background: `${T.primary}20` }}
                  >
                    <RiEditLine size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   SCHEDULE TAB (CORRECTED)
   ══════════════════════════════════════════════════════════════════════ */
const ScheduleTab = ({ token, eventId }) => {
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [allSchedules, setAllSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    edition: new Date().getFullYear().toString(),
    dateRange: "",
    venue: "",
    isActive: true,
    registrationTemplate: {
      conferenceName: "",
      headerTitle: "Your registration is Successful",
      greetingMessage: "Hello {name},",
      successMessage:
        "We have successfully received your payment towards registration for the conference.",
      paymentDetailsTitle: "Payment Details",
      registrationNumberPrefix: "#REG",
      currency: "USD",
      footerAddress: "",
      footerNote:
        "Please visit conference website for cancellation policy and terms and conditions.",
      buttonText: "Submit Abstract",
      buttonLink: "/submit-abstract",
      primaryColor: "#1a5f7a",
      secondaryColor: "#0e7c86",
    },
    schedule: [],
  });
  const [newScheduleItem, setNewScheduleItem] = useState({
    day: "",
    date: "",
    time: "",
    title: "",
    speaker: "",
    affiliation: "",
    location: "",
    type: "session",
    description: "",
    order: 0,
  });

  useEffect(() => {
    if (eventId) fetchSchedules();
  }, [eventId]);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      console.log(`Fetching schedules for event: ${eventId}`);
      const url = `${API_BASE}/api/schedule/event/${eventId}`;
      console.log("API URL:", url);

      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Fetched schedules response:", data);

      if (data.success) {
        console.log("Number of schedules found:", data.data?.length || 0);
        setAllSchedules(data.data || []);

        const active = data.data?.find((s) => s.isActive === true);
        console.log("Active schedule found:", active);

        if (active && active.schedule && active.schedule.length > 0) {
          setActiveSchedule(active);
        } else {
          const scheduleWithItems = data.data?.find(
            (s) => s.schedule && s.schedule.length > 0,
          );
          if (scheduleWithItems && !active) {
            console.log(
              "Found schedule with items but not active:",
              scheduleWithItems,
            );
          }
          setActiveSchedule(null);
        }
      } else {
        console.log("API returned success: false");
        setAllSchedules([]);
      }
    } catch (error) {
      console.error("Error fetching schedules:", error);
      console.error("Error response:", error.response?.data);
      toast.error("Failed to load schedules");
      setAllSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      edition: new Date().getFullYear().toString(),
      dateRange: "",
      venue: "",
      isActive: true,
      registrationTemplate: {
        conferenceName: "",
        headerTitle: "Your registration is Successful",
        greetingMessage: "Hello {name},",
        successMessage:
          "We have successfully received your payment towards registration for the conference.",
        paymentDetailsTitle: "Payment Details",
        registrationNumberPrefix: "#REG",
        currency: "USD",
        footerAddress: "",
        footerNote:
          "Please visit conference website for cancellation policy and terms and conditions.",
        buttonText: "Submit Abstract",
        buttonLink: "/submit-abstract",
        primaryColor: "#1a5f7a",
        secondaryColor: "#0e7c86",
      },
      schedule: [],
    });
    setEditing(null);
  };

  const addScheduleItem = () => {
    if (!newScheduleItem.title || !newScheduleItem.time) {
      toast.error("Title and time are required");
      return;
    }
    setFormData({
      ...formData,
      schedule: [
        ...formData.schedule,
        { ...newScheduleItem, order: formData.schedule.length },
      ],
    });
    setNewScheduleItem({
      day: "",
      date: "",
      time: "",
      title: "",
      speaker: "",
      affiliation: "",
      location: "",
      type: "session",
      description: "",
      order: 0,
    });
    toast.success("Schedule item added");
  };

  const removeScheduleItem = (index) => {
    if (window.confirm("Remove this schedule item?")) {
      const updated = formData.schedule.filter((_, i) => i !== index);
      setFormData({ ...formData, schedule: updated });
      toast.success("Item removed");
    }
  };

  const updateScheduleItem = (index, field, value) => {
    const updated = [...formData.schedule];
    updated[index][field] = value;
    setFormData({ ...formData, schedule: updated });
  };

  const saveSchedule = async () => {
    if (!formData.title) {
      toast.error("Schedule title is required");
      return;
    }

    if (!eventId) {
      toast.error("No event selected. Please select an event first.");
      return;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { ...formData, eventId };

      console.log("Saving schedule payload:", payload);

      let result;
      if (editing) {
        result = await axios.put(
          `${API_BASE}/api/schedule/${editing._id}`,
          payload,
          config,
        );
        console.log("Update response:", result.data);
        toast.success("Schedule updated successfully");

        if (formData.isActive && result.data?.data) {
          setActiveSchedule(result.data.data);
        }
      } else {
        result = await axios.post(`${API_BASE}/api/schedule`, payload, config);
        console.log("Create response:", result.data);
        toast.success("Schedule created successfully");

        if (formData.isActive && result.data?.data) {
          setActiveSchedule(result.data.data);
        }
      }

      resetForm();
      setShowForm(false);
      await fetchSchedules();
    } catch (error) {
      console.error("Error saving schedule:", error);
      console.error("Error details:", error.response?.data);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    try {
      await axios.delete(`${API_BASE}/api/schedule/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Schedule deleted");
      fetchSchedules();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const setAsActive = async (schedule) => {
    if (!schedule.schedule || schedule.schedule.length === 0) {
      toast.error(
        "Cannot activate a schedule with no items. Please add schedule items first.",
      );
      return;
    }

    try {
      setLoading(true);
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.put(
        `${API_BASE}/api/schedule/${schedule._id}`,
        { ...schedule, isActive: true, eventId },
        config,
      );

      console.log("Activation response:", response.data);

      if (response.data.success) {
        toast.success("Schedule activated");
        setActiveSchedule(response.data.data || schedule);
        await fetchSchedules();
      } else {
        toast.error(response.data.message || "Failed to activate");
      }
    } catch (error) {
      console.error("Error activating schedule:", error);
      toast.error(error.response?.data?.message || "Failed to activate");
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultSchedule = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/schedule/seed/event/${eventId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Default schedule seeded!");
      fetchSchedules();
    } catch (error) {
      console.error("Error seeding schedule:", error);
      toast.error("Failed to seed schedule");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Updated schedule types with "Opening Ceremony" and "Plenary"
  const scheduleTypes = [
    { value: "opening", label: "Opening Ceremony", color: "#D4A017" },
    { value: "plenary", label: "Plenary", color: "#8B5CF6" },
    { value: "keynote", label: "Keynote", color: "#3B82F6" },
    { value: "session", label: "Technical Session", color: "#2A9D8F" },
    { value: "workshop", label: "Workshop", color: "#F59E0B" },
    { value: "break", label: "Break", color: "#6B7280" },
    { value: "poster", label: "Poster Session", color: "#EC4899" },
    { value: "networking", label: "Networking", color: "#14B8A6" },
    { value: "panel", label: "Panel Discussion", color: "#EF4444" },
    { value: "closing", label: "Closing Ceremony", color: "#10B981" },
  ];

  const groupedSchedule = (scheduleItems) => {
    const grouped = {};
    scheduleItems.forEach((item) => {
      if (!grouped[item.day]) grouped[item.day] = [];
      grouped[item.day].push(item);
    });
    Object.keys(grouped).forEach((day) => {
      grouped[day].sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    return grouped;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <div
          className="px-6 py-4 border-b"
          style={{ borderBottomColor: T.borderLight }}
        >
          <h3
            className="text-lg font-semibold flex items-center gap-2"
            style={{ color: T.navy }}
          >
            <RiEyeLine className="text-green-400" /> Live Schedule Preview
          </h3>
          <p className="text-sm mt-1" style={{ color: T.textMuted }}>
            This is how the active schedule will appear on the website
          </p>
        </div>
        <div className="p-6 max-h-[600px] overflow-y-auto">
          {activeSchedule ? (
            <div>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold" style={{ color: T.text }}>
                  {activeSchedule.title}
                </h2>
                <p className="mt-1" style={{ color: T.textMuted }}>
                  {activeSchedule.description}
                </p>
                <div
                  className="flex justify-center gap-4 mt-3 text-sm"
                  style={{ color: T.textMuted }}
                >
                  <span>
                    <RiCalendarLine className="inline mr-1" />{" "}
                    {activeSchedule.dateRange}
                  </span>
                  <span>
                    <RiMapPinLine className="inline mr-1" />{" "}
                    {activeSchedule.venue}
                  </span>
                </div>
              </div>
              {Object.entries(groupedSchedule(activeSchedule.schedule)).map(
                ([day, items]) => (
                  <div key={day} className="mb-6">
                    <h3
                      className="text-xl font-semibold mb-3 pb-2"
                      style={{
                        color: T.primaryLight,
                        borderBottom: `1px solid ${T.primary}30`,
                      }}
                    >
                      {day}
                    </h3>
                    {items.map((item, idx) => {
                      const typeInfo =
                        scheduleTypes.find((t) => t.value === item.type) ||
                        scheduleTypes[2];
                      return (
                        <div
                          key={idx}
                          className="mb-3 p-3 rounded-lg transition"
                          style={{ background: T.offWhite }}
                        >
                          <div className="flex flex-wrap gap-3">
                            <span
                              className="text-sm font-mono min-w-[100px]"
                              style={{ color: T.primaryLight }}
                            >
                              {item.time}
                            </span>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4
                                  className="font-semibold"
                                  style={{ color: T.text }}
                                >
                                  {item.title}
                                </h4>
                                <span
                                  className="text-xs px-2 py-0.5 rounded"
                                  style={{
                                    backgroundColor: `${typeInfo.color}33`,
                                    color: typeInfo.color,
                                  }}
                                >
                                  {typeInfo.label}
                                </span>
                              </div>
                              {item.speaker && (
                                <p
                                  className="text-sm mt-1"
                                  style={{ color: T.textMuted }}
                                >
                                  🎤 {item.speaker}
                                  {item.affiliation && `, ${item.affiliation}`}
                                </p>
                              )}
                              {item.location && (
                                <p
                                  className="text-xs mt-1"
                                  style={{ color: T.textMuted }}
                                >
                                  <RiMapPinLine
                                    className="inline mr-1"
                                    size={12}
                                  />{" "}
                                  {item.location}
                                </p>
                              )}
                              {item.description && (
                                <p
                                  className="text-sm mt-2"
                                  style={{ color: T.textMuted }}
                                >
                                  {item.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ),
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <RiCalendarEventLine
                size={48}
                className="mx-auto mb-3"
                style={{ color: T.textMuted }}
              />
              <p style={{ color: T.textMuted }}>No active schedule</p>
              <p className="text-sm" style={{ color: T.textMuted }}>
                Create a schedule and activate it to display here
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (!showForm) resetForm();
            setEditing(null);
          }}
          className="px-4 py-2 rounded-lg flex items-center gap-2"
          style={{ background: T.gradPrimary, color: T.white }}
        >
          {showForm ? <RiCloseLine /> : <RiAddLine />}
          {showForm ? "Close Form" : "Create New Schedule"}
        </button>
        <button
          onClick={seedDefaultSchedule}
          className="px-4 py-2 rounded-lg flex items-center gap-2"
          style={{ background: "#4CAF50", color: T.white }}
        >
          <RiFileTextLine /> Load Default Schedule
        </button>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-xl p-6 overflow-hidden"
            style={{
              background: T.white,
              border: `1px solid ${T.borderLight}`,
            }}
          >
            <h3
              className="text-lg font-semibold mb-4"
              style={{ color: T.navy }}
            >
              {editing ? "Edit Schedule" : "Create New Schedule"}
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Schedule Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="e.g., International Conference on Materials Science 2024"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    rows="3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Brief description of the conference/schedule"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Edition
                  </label>
                  <input
                    type="text"
                    value={formData.edition}
                    onChange={(e) =>
                      setFormData({ ...formData, edition: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="2024"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Date Range
                  </label>
                  <input
                    type="text"
                    value={formData.dateRange}
                    onChange={(e) =>
                      setFormData({ ...formData, dateRange: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="December 10-12, 2024"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Venue
                  </label>
                  <input
                    type="text"
                    value={formData.venue}
                    onChange={(e) =>
                      setFormData({ ...formData, venue: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Boston Convention Center, Boston, MA"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm flex items-center gap-2"
                    style={{ color: T.textMid }}
                  >
                    <RiSettings4Line /> Registration Template Settings
                  </label>
                  <button
                    onClick={() => {
                      document
                        .getElementById("template-settings")
                        .classList.toggle("hidden");
                    }}
                    className="text-sm hover:underline"
                    style={{ color: T.primary }}
                  >
                    Configure PDF Template →
                  </button>
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="w-5 h-5 accent-green-500"
                  />
                  <label style={{ color: T.textMid }}>
                    Set as active schedule (replaces current active)
                  </label>
                </div>
              </div>
            </div>

            <div
              id="template-settings"
              className="hidden mt-6 p-4 rounded-lg"
              style={{
                background: T.offWhite,
                border: `1px solid ${T.border}`,
              }}
            >
              <h4 className="font-semibold mb-3" style={{ color: T.navy }}>
                PDF Registration Template
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={formData.registrationTemplate.conferenceName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      registrationTemplate: {
                        ...formData.registrationTemplate,
                        conferenceName: e.target.value,
                      },
                    })
                  }
                  placeholder="Conference Name"
                  className="rounded-lg p-2 text-sm"
                  style={{
                    background: T.white,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                  }}
                />
                <input
                  type="text"
                  value={formData.registrationTemplate.headerTitle}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      registrationTemplate: {
                        ...formData.registrationTemplate,
                        headerTitle: e.target.value,
                      },
                    })
                  }
                  placeholder="Header Title"
                  className="rounded-lg p-2 text-sm"
                  style={{
                    background: T.white,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                  }}
                />
                <input
                  type="text"
                  value={formData.registrationTemplate.primaryColor}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      registrationTemplate: {
                        ...formData.registrationTemplate,
                        primaryColor: e.target.value,
                      },
                    })
                  }
                  placeholder="Primary Color (e.g., #1a5f7a)"
                  className="rounded-lg p-2 text-sm"
                  style={{
                    background: T.white,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                  }}
                />
                <input
                  type="text"
                  value={formData.registrationTemplate.secondaryColor}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      registrationTemplate: {
                        ...formData.registrationTemplate,
                        secondaryColor: e.target.value,
                      },
                    })
                  }
                  placeholder="Secondary Color"
                  className="rounded-lg p-2 text-sm"
                  style={{
                    background: T.white,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                  }}
                />
              </div>
            </div>

            <div className="mt-6">
              <h4 className="font-semibold mb-3" style={{ color: T.navy }}>
                Schedule Items
              </h4>
              <div className="space-y-3 mb-4 max-h-[400px] overflow-y-auto">
                {formData.schedule.map((item, idx) => {
                  const typeInfo =
                    scheduleTypes.find((t) => t.value === item.type) ||
                    scheduleTypes[2];
                  return (
                    <div
                      key={idx}
                      className="rounded-lg p-3"
                      style={{
                        background: T.offWhite,
                        borderLeft: `4px solid ${typeInfo.color}`,
                      }}
                    >
                      <div className="flex gap-2 mb-2 flex-wrap">
                        <input
                          type="text"
                          value={item.day}
                          onChange={(e) =>
                            updateScheduleItem(idx, "day", e.target.value)
                          }
                          placeholder="Day (e.g., Day 1)"
                          className="w-24 rounded-lg p-2 text-sm"
                          style={{
                            background: T.white,
                            border: `1px solid ${T.border}`,
                            color: T.text,
                          }}
                        />
                        <input
                          type="text"
                          value={item.time}
                          onChange={(e) =>
                            updateScheduleItem(idx, "time", e.target.value)
                          }
                          placeholder="Time (e.g., 09:00 - 10:30)"
                          className="w-32 rounded-lg p-2 text-sm"
                          style={{
                            background: T.white,
                            border: `1px solid ${T.border}`,
                            color: T.text,
                          }}
                        />
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) =>
                            updateScheduleItem(idx, "title", e.target.value)
                          }
                          placeholder="Title"
                          className="flex-1 rounded-lg p-2 text-sm"
                          style={{
                            background: T.white,
                            border: `1px solid ${T.border}`,
                            color: T.text,
                          }}
                        />
                        <select
                          value={item.type}
                          onChange={(e) =>
                            updateScheduleItem(idx, "type", e.target.value)
                          }
                          className="w-32 rounded-lg p-2 text-sm"
                          style={{
                            background: T.white,
                            border: `1px solid ${T.border}`,
                            color: T.text,
                          }}
                        >
                          {scheduleTypes.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          value={item.order}
                          onChange={(e) =>
                            updateScheduleItem(
                              idx,
                              "order",
                              parseInt(e.target.value) || 0,
                            )
                          }
                          className="w-16 rounded-lg p-2 text-sm"
                          style={{
                            background: T.white,
                            border: `1px solid ${T.border}`,
                            color: T.text,
                          }}
                          placeholder="Order"
                        />
                        <button
                          onClick={() => removeScheduleItem(idx)}
                          className="p-2 rounded"
                          style={{ color: "#EF4444", background: "#FEE2E2" }}
                        >
                          <RiDeleteBinLine size={16} />
                        </button>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <input
                          type="text"
                          value={item.speaker || ""}
                          onChange={(e) =>
                            updateScheduleItem(idx, "speaker", e.target.value)
                          }
                          placeholder="Speaker"
                          className="flex-1 rounded-lg p-2 text-sm"
                          style={{
                            background: T.white,
                            border: `1px solid ${T.border}`,
                            color: T.text,
                          }}
                        />
                        <input
                          type="text"
                          value={item.affiliation || ""}
                          onChange={(e) =>
                            updateScheduleItem(
                              idx,
                              "affiliation",
                              e.target.value,
                            )
                          }
                          placeholder="Affiliation"
                          className="flex-1 rounded-lg p-2 text-sm"
                          style={{
                            background: T.white,
                            border: `1px solid ${T.border}`,
                            color: T.text,
                          }}
                        />
                        <input
                          type="text"
                          value={item.location || ""}
                          onChange={(e) =>
                            updateScheduleItem(idx, "location", e.target.value)
                          }
                          placeholder="Location"
                          className="flex-1 rounded-lg p-2 text-sm"
                          style={{
                            background: T.white,
                            border: `1px solid ${T.border}`,
                            color: T.text,
                          }}
                        />
                      </div>
                      <textarea
                        value={item.description || ""}
                        onChange={(e) =>
                          updateScheduleItem(idx, "description", e.target.value)
                        }
                        placeholder="Description"
                        className="mt-2 w-full rounded-lg p-2 text-sm"
                        rows="2"
                        style={{
                          background: T.white,
                          border: `1px solid ${T.border}`,
                          color: T.text,
                        }}
                      />
                    </div>
                  );
                })}
                {formData.schedule.length === 0 && (
                  <p
                    className="text-center py-4"
                    style={{ color: T.textMuted }}
                  >
                    No schedule items added yet.
                  </p>
                )}
              </div>

              <div
                className="border-t pt-4"
                style={{ borderTopColor: T.border }}
              >
                <h5 className="text-sm mb-3" style={{ color: T.textMuted }}>
                  Add New Schedule Item:
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                  <input
                    type="text"
                    value={newScheduleItem.day}
                    onChange={(e) =>
                      setNewScheduleItem({
                        ...newScheduleItem,
                        day: e.target.value,
                      })
                    }
                    placeholder="Day"
                    className="rounded-lg p-2 text-sm"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  />
                  <input
                    type="text"
                    value={newScheduleItem.time}
                    onChange={(e) =>
                      setNewScheduleItem({
                        ...newScheduleItem,
                        time: e.target.value,
                      })
                    }
                    placeholder="Time *"
                    className="rounded-lg p-2 text-sm"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  />
                  <input
                    type="text"
                    value={newScheduleItem.title}
                    onChange={(e) =>
                      setNewScheduleItem({
                        ...newScheduleItem,
                        title: e.target.value,
                      })
                    }
                    placeholder="Title *"
                    className="rounded-lg p-2 text-sm"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                  <input
                    type="text"
                    value={newScheduleItem.speaker}
                    onChange={(e) =>
                      setNewScheduleItem({
                        ...newScheduleItem,
                        speaker: e.target.value,
                      })
                    }
                    placeholder="Speaker"
                    className="rounded-lg p-2 text-sm"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  />
                  <input
                    type="text"
                    value={newScheduleItem.affiliation}
                    onChange={(e) =>
                      setNewScheduleItem({
                        ...newScheduleItem,
                        affiliation: e.target.value,
                      })
                    }
                    placeholder="Affiliation"
                    className="rounded-lg p-2 text-sm"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  />
                  <input
                    type="text"
                    value={newScheduleItem.location}
                    onChange={(e) =>
                      setNewScheduleItem({
                        ...newScheduleItem,
                        location: e.target.value,
                      })
                    }
                    placeholder="Location"
                    className="rounded-lg p-2 text-sm"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={newScheduleItem.type}
                    onChange={(e) =>
                      setNewScheduleItem({
                        ...newScheduleItem,
                        type: e.target.value,
                      })
                    }
                    className="flex-1 rounded-lg p-2 text-sm"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  >
                    {scheduleTypes.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={newScheduleItem.order}
                    onChange={(e) =>
                      setNewScheduleItem({
                        ...newScheduleItem,
                        order: parseInt(e.target.value) || 0,
                      })
                    }
                    placeholder="Order"
                    className="w-24 rounded-lg p-2 text-sm"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  />
                </div>
                <textarea
                  value={newScheduleItem.description}
                  onChange={(e) =>
                    setNewScheduleItem({
                      ...newScheduleItem,
                      description: e.target.value,
                    })
                  }
                  placeholder="Description"
                  className="mt-2 w-full rounded-lg p-2 text-sm"
                  rows="2"
                  style={{
                    background: T.offWhite,
                    border: `1px solid ${T.border}`,
                    color: T.text,
                  }}
                />
                <button
                  onClick={addScheduleItem}
                  className="mt-3 px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
                  style={{ background: `${T.primary}20`, color: T.primary }}
                >
                  <RiAddLine size={14} /> Add Schedule Item
                </button>
              </div>
            </div>

            <div
              className="flex gap-3 mt-6 pt-4"
              style={{ borderTop: `1px solid ${T.border}` }}
            >
              <button
                onClick={saveSchedule}
                disabled={loading}
                className="px-6 py-2 rounded-lg flex items-center gap-2"
                style={{ background: T.gradPrimary, color: T.white }}
              >
                <RiSaveLine size={18} />
                {loading
                  ? "Saving..."
                  : editing
                    ? "Update Schedule"
                    : "Create Schedule"}
              </button>
              {editing && (
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditing(null);
                    resetForm();
                  }}
                  className="px-6 py-2 rounded-lg"
                  style={{ background: "#E5E7EB", color: T.textMid }}
                >
                  Cancel
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className="rounded-xl p-6"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: T.navy }}>
          📋 All Schedules
        </h3>
        <div className="space-y-3">
          {allSchedules.map((schedule) => (
            <div
              key={schedule._id}
              className="flex items-center justify-between p-4 rounded-lg"
              style={{ background: T.offWhite }}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold" style={{ color: T.text }}>
                    {schedule.title}
                  </h4>
                  {schedule.isActive && (
                    <span
                      className="px-2 py-0.5 rounded text-xs"
                      style={{ background: `${T.primary}20`, color: T.primary }}
                    >
                      ACTIVE
                    </span>
                  )}
                  <span className="text-xs" style={{ color: T.textMuted }}>
                    v{schedule.edition}
                  </span>
                </div>
                <p className="text-sm" style={{ color: T.textMuted }}>
                  {schedule.schedule?.length || 0} sessions •{" "}
                  {schedule.dateRange || "No date range"}
                </p>
              </div>
              <div className="flex gap-2">
                {!schedule.isActive && (
                  <button
                    onClick={() => setAsActive(schedule)}
                    className="p-2 rounded"
                    title="Activate"
                    style={{ color: "#4CAF50", background: "#E8F5E9" }}
                  >
                    <RiEyeLine size={16} />
                  </button>
                )}
                <button
                  onClick={() => {
                    setEditing(schedule);
                    setFormData(schedule);
                    setShowForm(true);
                  }}
                  className="p-2 rounded"
                  style={{ color: T.primary, background: `${T.primary}20` }}
                >
                  <RiEditLine size={16} />
                </button>
                <button
                  onClick={() => deleteSchedule(schedule._id)}
                  className="p-2 rounded"
                  style={{ color: "#EF4444", background: "#FEE2E2" }}
                >
                  <RiDeleteBinLine size={16} />
                </button>
              </div>
            </div>
          ))}
          {allSchedules.length === 0 && (
            <p className="text-center py-4" style={{ color: T.textMuted }}>
              No schedules created yet. Create one above.
            </p>
          )}
        </div>
      </div>

    </motion.div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   SUMMIT TOPICS TAB
   ══════════════════════════════════════════════════════════════════════ */
const SummitTopicsTab = ({ token, eventId }) => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#1E3A5F",
    order: 0,
    isActive: true,
  });

  // ── Ref for scrolling to form ──
  const formRef = useRef(null);
  const scrollToForm = () => {
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  useEffect(() => {
    if (eventId) fetchTopics();
  }, [eventId]);

  const fetchTopics = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/summit-topics/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (data.success) setTopics(data.summitTopics || []);
    } catch (error) {
      console.error("Error fetching topics:", error);
      toast.error("Failed to load summit topics");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      color: "#1E3A5F",
      order: 0,
      isActive: true,
    });
    setEditing(null);
  };

  const saveTopic = async () => {
    if (!formData.name || !formData.description) {
      toast.error("Name and description are required");
      return;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { ...formData, eventId };

      const existing = topics.find(
        (t) =>
          t.name.toLowerCase() === formData.name.trim().toLowerCase() &&
          t._id !== editing?._id,
      );

      if (existing) {
        await axios.put(
          `${API_BASE}/api/summit-topics/${existing._id}`,
          payload,
          config,
        );
        toast.success(`Topic "${formData.name}" updated successfully`);
      } else if (editing) {
        await axios.put(
          `${API_BASE}/api/summit-topics/${editing._id}`,
          payload,
          config,
        );
        toast.success("Topic updated successfully");
      } else {
        await axios.post(`${API_BASE}/api/summit-topics`, payload, config);
        toast.success("Topic created successfully");
      }

      resetForm();
      setShowForm(false);
      fetchTopics();
    } catch (error) {
      console.error("Error saving topic:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteTopic = async (id) => {
    if (!window.confirm("Delete this topic? This action cannot be undone."))
      return;
    try {
      await axios.delete(`${API_BASE}/api/summit-topics/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Topic deleted successfully");
      fetchTopics();
    } catch (error) {
      console.error("Error deleting topic:", error);
      toast.error("Failed to delete topic");
    }
  };

  const toggleActive = async (topic) => {
    try {
      await axios.put(
        `${API_BASE}/api/summit-topics/${topic._id}`,
        { ...topic, isActive: !topic.isActive, eventId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Topic ${!topic.isActive ? "activated" : "deactivated"}`);
      fetchTopics();
    } catch (error) {
      console.error("Error toggling:", error);
      toast.error("Failed to update");
    }
  };

  const seedDefaultTopics = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/summit-topics/seed/event/${eventId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Default topics seeded!");
      fetchTopics();
    } catch (error) {
      console.error("Error seeding topics:", error);
      toast.error("Failed to seed topics");
    } finally {
      setLoading(false);
    }
  };

  const allActiveTopics = topics.filter((t) => t.isActive);
  const inactiveTopics = topics.filter((t) => !t.isActive);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          className="rounded-xl p-4"
          style={{
            background: `${T.primary}20`,
            border: `1px solid ${T.primary}30`,
          }}
        >
          <div className="text-2xl font-bold" style={{ color: T.white }}>
            {topics.length}
          </div>
          <div className="text-sm" style={{ color: T.textMuted }}>
            Total Topics
          </div>
        </div>
        <div
          className="rounded-xl p-4"
          style={{ background: "#4CAF5020", border: "1px solid #4CAF5030" }}
        >
          <div className="text-2xl font-bold" style={{ color: T.white }}>
            {allActiveTopics.length}
          </div>
          <div className="text-sm" style={{ color: T.textMuted }}>
            Active Topics
          </div>
        </div>
        <div
          className="rounded-xl p-4"
          style={{ background: "#8B5CF620", border: "1px solid #8B5CF630" }}
        >
          <div className="text-2xl font-bold" style={{ color: T.white }}>
            {inactiveTopics.length}
          </div>
          <div className="text-sm" style={{ color: T.textMuted }}>
            Inactive Topics
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 justify-between items-center">
        <div className="flex gap-3">
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (!showForm) {
                resetForm();
                setEditing(null);
                scrollToForm();
              }
            }}
            className="px-4 py-2 rounded-lg flex items-center gap-2"
            style={{ background: T.gradPrimary, color: T.white }}
          >
            {showForm ? <RiCloseLine /> : <RiAddLine />}
            {showForm ? "Close Form" : "Add New Topic"}
          </button>
          <button
            onClick={seedDefaultTopics}
            className="px-4 py-2 rounded-lg flex items-center gap-2"
            style={{ background: "#4CAF50", color: T.white }}
          >
            <RiFileTextLine /> Load Default Topics
          </button>
        </div>
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            ref={formRef} // ── Ref for scrolling ──
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-xl p-6 overflow-hidden"
            style={{
              background: T.white,
              border: `1px solid ${T.borderLight}`,
            }}
          >
            <h3
              className="text-lg font-semibold mb-4"
              style={{ color: T.navy }}
            >
              {editing ? "Edit Summit Topic" : "Create New Summit Topic"}
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Topic Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="e.g., Advanced Nanomaterials"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    rows="3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Detailed description of the topic"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Color
                  </label>
                  <div className="flex gap-3 items-center">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) =>
                        setFormData({ ...formData, color: e.target.value })
                      }
                      className="w-12 h-12 rounded-lg cursor-pointer"
                      style={{ border: `1px solid ${T.border}` }}
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) =>
                        setFormData({ ...formData, color: e.target.value })
                      }
                      className="flex-1 rounded-lg p-3"
                      style={{
                        background: T.offWhite,
                        border: `1px solid ${T.border}`,
                        color: T.text,
                      }}
                      placeholder="#1E3A5F"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
                className="w-5 h-5 accent-green-500"
              />
              <label style={{ color: T.textMid }}>
                Active (visible on website)
              </label>
            </div>
            <div
              className="flex gap-3 mt-6 pt-4"
              style={{ borderTop: `1px solid ${T.border}` }}
            >
              <button
                onClick={saveTopic}
                disabled={loading}
                className="px-6 py-2 rounded-lg flex items-center gap-2"
                style={{ background: T.gradPrimary, color: T.white }}
              >
                <RiSaveLine size={18} />
                {loading
                  ? "Saving..."
                  : editing
                    ? "Update Topic"
                    : "Create Topic"}
              </button>
              {editing && (
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditing(null);
                    resetForm();
                  }}
                  className="px-6 py-2 rounded-lg"
                  style={{ background: "#E5E7EB", color: T.textMid }}
                >
                  Cancel
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Topics List */}
      <div
        className="rounded-xl p-6"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold" style={{ color: T.navy }}>
            📚 Active Summit Topics ({allActiveTopics.length})
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allActiveTopics.map((topic) => {
            return (
              <div
                key={topic._id}
                className="rounded-lg p-4 transition-all hover:scale-105 cursor-pointer group"
                style={{
                  background: T.offWhite,
                  borderLeft: `3px solid ${topic.color}`,
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold"
                      style={{
                        backgroundColor: `${topic.color}22`,
                        color: topic.color,
                      }}
                    >
                      {topic.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h4 className="font-semibold" style={{ color: T.text }}>
                        {topic.name}
                      </h4>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button
                      onClick={() => toggleActive(topic)}
                      className="p-1.5 rounded"
                      title="Deactivate"
                      style={{
                        color: T.accentGold,
                        background: `${T.accentGold}20`,
                      }}
                    >
                      <RiEyeOffLine size={14} />
                    </button>
                    <button
                      onClick={() => {
                        setEditing(topic);
                        setFormData(topic);
                        setShowForm(true);
                        scrollToForm(); // ── Scroll to form ──
                      }}
                      className="p-1.5 rounded"
                      style={{ color: T.primary, background: `${T.primary}20` }}
                    >
                      <RiEditLine size={14} />
                    </button>
                    <button
                      onClick={() => deleteTopic(topic._id)}
                      className="p-1.5 rounded"
                      style={{ color: "#EF4444", background: "#FEE2E2" }}
                    >
                      <RiDeleteBinLine size={14} />
                    </button>
                  </div>
                </div>
                <p
                  className="text-sm mt-3 line-clamp-2"
                  style={{ color: T.textMuted }}
                >
                  {topic.description}
                </p>
                <div
                  className="mt-3 flex justify-between items-center text-xs"
                  style={{ color: T.textMuted }}
                >
                  <span>Order: {topic.order}</span>
                </div>
              </div>
            );
          })}
        </div>
        {allActiveTopics.length === 0 && (
          <div className="text-center py-12">
            <RiFolderChartLine
              size={48}
              className="mx-auto mb-3"
              style={{ color: T.textMuted }}
            />
            <p style={{ color: T.textMuted }}>No active topics found</p>
            <p className="text-sm" style={{ color: T.textMuted }}>
              Add topics above to get started
            </p>
          </div>
        )}
      </div>

      {/* Inactive Topics */}
      {inactiveTopics.length > 0 && (
        <div
          className="rounded-xl p-6 opacity-60"
          style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: T.navy }}>
            🚫 Inactive Topics ({inactiveTopics.length})
          </h3>
          <div className="space-y-3">
            {inactiveTopics.map((topic) => (
              <div
                key={topic._id}
                className="flex items-center gap-4 p-3 rounded-lg opacity-50"
                style={{ background: T.offWhite }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
                  style={{
                    backgroundColor: `${topic.color}22`,
                    color: topic.color,
                  }}
                >
                  {topic.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="font-semibold" style={{ color: T.text }}>
                    {topic.name}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(topic)}
                    className="p-2 rounded"
                    title="Activate"
                    style={{ color: "#4CAF50", background: "#E8F5E9" }}
                  >
                    <RiEyeLine size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(topic);
                      setFormData(topic);
                      setShowForm(true);
                      scrollToForm(); // ── Scroll to form ──
                    }}
                    className="p-2 rounded"
                    style={{ color: T.primary, background: `${T.primary}20` }}
                  >
                    <RiEditLine size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

const TopicsTab = ({ token, eventId }) => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    order: 0,
    isActive: true,
  });
  const [editing, setEditing] = useState(null);

  // ── Ref for scrolling to form ──
  const formRef = useRef(null);
  const scrollToForm = () => {
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  useEffect(() => {
    if (eventId) fetchTopics();
  }, [eventId]);

  const fetchTopics = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/topics/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (data.success) setTopics(data.topics);
    } catch (error) {
      console.error("Error fetching topics:", error);
      toast.error("Failed to load topics");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: "", description: "", order: 0, isActive: true });
    setEditing(null);
  };

  const save = async () => {
    if (!form.name || !form.description) {
      toast.error("Name and description are required");
      return;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { ...form, eventId };

      const existing = topics.find(
        (t) =>
          t.name.toLowerCase() === form.name.trim().toLowerCase() &&
          t._id !== editing?._id,
      );

      if (existing) {
        await axios.put(
          `${API_BASE}/api/topics/${existing._id}`,
          payload,
          config,
        );
        toast.success(`Topic "${form.name}" updated successfully`);
      } else if (editing) {
        await axios.put(
          `${API_BASE}/api/topics/${editing._id}`,
          payload,
          config,
        );
        toast.success("Topic updated successfully");
      } else {
        await axios.post(`${API_BASE}/api/topics`, payload, config);
        toast.success("Topic created successfully");
      }

      resetForm();
      fetchTopics();
    } catch (error) {
      console.error("Error saving topic:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteTopic = async (id) => {
    if (!window.confirm("Delete this topic?")) return;
    try {
      await axios.delete(`${API_BASE}/api/topics/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      fetchTopics();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const updateStatus = async (id, isActive) => {
    try {
      await axios.put(
        `${API_BASE}/api/topics/${id}`,
        { eventId, isActive },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Topic ${isActive ? "activated" : "deactivated"}`);
      fetchTopics();
    } catch {
      toast.error("Failed to update");
    }
  };

  const startEdit = (topic) => {
    setEditing(topic);
    setForm({
      name: topic.name,
      description: topic.description,
      order: topic.order || 0,
      isActive: topic.isActive,
    });
    scrollToForm(); // ── Scroll to form ──
  };

  const activeTopics = topics.filter((t) => t.isActive);
  const inactiveTopics = topics.filter((t) => !t.isActive);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: T.white,
        padding: "32px",
        borderRadius: "16px",
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy }}>
          Manage Conference Topics
        </h2>
        <span
          style={{
            fontSize: "12px",
            color: T.textMuted,
            background: T.softBlue,
            padding: "4px 12px",
            borderRadius: "999px",
          }}
        >
          {activeTopics.length} active · {inactiveTopics.length} hidden
        </span>
      </div>
      <p style={{ color: T.textMuted, marginBottom: "32px", fontSize: "14px" }}>
        Add and manage conference topics that will be displayed on the website.
      </p>

      <div
        ref={formRef} // ── Ref for scrolling ──
        style={{
          background: T.offWhite,
          border: `1px solid ${T.border}`,
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "40px",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "semibold",
            color: T.navy,
            marginBottom: "20px",
          }}
        >
          {editing ? `Editing: ${editing.name}` : "Add New Topic"}
        </h3>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                fontSize: "13px",
                marginBottom: "4px",
              }}
            >
              Topic Name *
            </label>
            <input
              type="text"
              placeholder="e.g., Immunotherapy"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                fontSize: "13px",
                marginBottom: "4px",
              }}
            >
              Description *
            </label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows="3"
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
              placeholder="Describe the topic..."
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                color: T.textMid,
                fontSize: "13px",
                marginBottom: "4px",
              }}
            >
              Display Order
            </label>
            <input
              type="number"
              value={form.order}
              onChange={(e) =>
                setForm({ ...form, order: parseInt(e.target.value) || 0 })
              }
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              style={{ width: "18px", height: "18px", accentColor: T.primary }}
            />
            <label style={{ color: T.textMid, fontSize: "13px" }}>
              Show on Website (active)
            </label>
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
          <button
            onClick={save}
            disabled={loading}
            style={{
              padding: "10px 24px",
              background: T.gradPrimary,
              color: T.white,
              border: "none",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            <RiSaveLine size={18} />
            {loading ? "Saving..." : editing ? "Update Topic" : "Add Topic"}
          </button>
          {editing && (
            <button
              onClick={resetForm}
              style={{
                padding: "10px 24px",
                background: "#E5E7EB",
                color: T.textMid,
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3
        style={{
          fontSize: "18px",
          fontWeight: "semibold",
          color: T.navy,
          marginBottom: "16px",
        }}
      >
        ✅ Active Topics ({activeTopics.length})
      </h3>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          marginBottom: "40px",
        }}
      >
        {activeTopics
          .sort((a, b) => a.order - b.order)
          .map((topic) => (
            <div
              key={topic._id}
              style={{
                background: T.offWhite,
                padding: "16px",
                borderRadius: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div style={{ flex: 1 }}>
                <h4 style={{ color: T.text, fontWeight: "bold" }}>
                  {topic.name}
                </h4>
                <p style={{ color: T.textMuted, fontSize: "13px" }}>
                  {topic.description}
                </p>
                <p
                  style={{
                    color: T.textMuted,
                    fontSize: "11px",
                    marginTop: "4px",
                  }}
                >
                  Order: {topic.order}
                </p>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => updateStatus(topic._id, !topic.isActive)}
                  style={{
                    padding: "8px",
                    background: "#FEF3C7",
                    color: T.accentGold,
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                  title="Deactivate"
                >
                  <RiEyeOffLine size={16} />
                </button>
                <button
                  onClick={() => startEdit(topic)}
                  style={{
                    padding: "8px",
                    background: T.softBlue,
                    color: T.primary,
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  <RiEditLine size={16} />
                </button>
                <button
                  onClick={() => deleteTopic(topic._id)}
                  style={{
                    padding: "8px",
                    background: "#FEE2E2",
                    color: "#EF4444",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  <RiDeleteBinLine size={16} />
                </button>
              </div>
            </div>
          ))}
        {activeTopics.length === 0 && !loading && (
          <div
            style={{ textAlign: "center", padding: "32px", color: T.textMuted }}
          >
            No active topics yet.
          </div>
        )}
      </div>

      {inactiveTopics.length > 0 && (
        <>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "semibold",
              color: T.navy,
              marginBottom: "16px",
            }}
          >
            🚫 Hidden Topics ({inactiveTopics.length})
          </h3>
          <div style={{ opacity: 0.6 }}>
            {inactiveTopics.map((topic) => (
              <div
                key={topic._id}
                style={{
                  background: T.offWhite,
                  padding: "16px",
                  borderRadius: "12px",
                  marginBottom: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div>
                  <h4 style={{ color: T.text, fontWeight: "bold" }}>
                    {topic.name}
                  </h4>
                  <p style={{ color: T.textMuted, fontSize: "13px" }}>
                    {topic.description}
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => updateStatus(topic._id, !topic.isActive)}
                    style={{
                      padding: "6px 12px",
                      background: "#E8F5E9",
                      color: "#4CAF50",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    Activate
                  </button>
                  <button
                    onClick={() => startEdit(topic)}
                    style={{
                      padding: "8px",
                      background: T.softBlue,
                      color: T.primary,
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <RiEditLine size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
};
/* ══════════════════════════════════════════════════════════════════════
   COMMITTEE MANAGEMENT TAB
   ══════════════════════════════════════════════════════════════════════ */
const CommitteeManagementTab = ({ token, eventId }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    institution: "",
    bio: "",
    field: "",
    country: "",
    countryCode: "🌍",
    color: "#1A56DB",
    initials: "",
    email: "",
    linkedin: "",
    twitter: "",
    order: 0,
    isActive: true,
    isChair: false,
    isCoChair: false,
    isMember: true,
  });

  useEffect(() => {
    if (eventId) fetchMembers();
  }, [eventId]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_BASE}/api/committee/event/${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      let membersArray = [];
      if (data.success && data.data && Array.isArray(data.data)) {
        membersArray = data.data;
      } else if (
        data.success &&
        data.committee &&
        Array.isArray(data.committee)
      ) {
        membersArray = data.committee;
      } else if (Array.isArray(data)) {
        membersArray = data;
      } else if (data.data && Array.isArray(data.data)) {
        membersArray = data.data;
      } else {
        console.error("Unexpected response format:", data);
        membersArray = [];
      }

      setMembers(membersArray);
    } catch (error) {
      console.error("Error fetching committee members:", error);
      toast.error(
        error.response?.data?.message || "Failed to load committee members",
      );
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      institution: "",
      bio: "",
      field: "",
      country: "",
      countryCode: "🌍",
      color: "#1A56DB",
      initials: "",
      email: "",
      linkedin: "",
      twitter: "",
      order: 0,
      isActive: true,
      isChair: false,
      isCoChair: false,
      isMember: true,
    });
    setEditing(null);
  };

  const saveMember = async () => {
    if (
      !formData.name ||
      !formData.institution ||
      !formData.bio ||
      !formData.field
    ) {
      toast.error("Name, institution, bio, and field are required");
      return;
    }

    if (!formData.initials) {
      const initials = formData.name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
      formData.initials = initials;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { ...formData, eventId };

      if (editing) {
        await axios.put(
          `${API_BASE}/api/committee/${editing._id}`,
          payload,
          config,
        );
        toast.success("Committee member updated successfully");
      } else {
        await axios.post(`${API_BASE}/api/committee`, payload, config);
        toast.success("Committee member added successfully");
      }
      resetForm();
      setShowForm(false);
      await fetchMembers();
    } catch (error) {
      console.error("Error saving member:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Delete this committee member?")) return;
    try {
      await axios.delete(`${API_BASE}/api/committee/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Committee member deleted");
      await fetchMembers();
    } catch (error) {
      console.error("Error deleting member:", error);
      toast.error(error.response?.data?.message || "Failed to delete");
    }
  };

  const toggleActive = async (member) => {
    try {
      await axios.put(
        `${API_BASE}/api/committee/${member._id}`,
        { ...member, isActive: !member.isActive, eventId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Member ${!member.isActive ? "activated" : "deactivated"}`);
      await fetchMembers();
    } catch (error) {
      console.error("Error toggling active status:", error);
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  const seedDefaultMembers = async () => {
    if (!eventId) {
      toast.error("No event ID available");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/committee/seed/event/${eventId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        toast.success(
          response.data.message ||
            "Default committee members seeded successfully!",
        );
        await fetchMembers();
      } else {
        toast.error(
          response.data.message || "Failed to seed committee members",
        );
      }
    } catch (error) {
      console.error("Error seeding committee members:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to seed default committee members",
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── Complete country list (193 UN members + extras) ───
  const countryCodes = [
    { code: "🇦🇫", country: "Afghanistan" },
    { code: "🇦🇱", country: "Albania" },
    { code: "🇩🇿", country: "Algeria" },
    { code: "🇦🇩", country: "Andorra" },
    { code: "🇦🇴", country: "Angola" },
    { code: "🇦🇬", country: "Antigua and Barbuda" },
    { code: "🇦🇷", country: "Argentina" },
    { code: "🇦🇲", country: "Armenia" },
    { code: "🇦🇺", country: "Australia" },
    { code: "🇦🇹", country: "Austria" },
    { code: "🇦🇿", country: "Azerbaijan" },
    { code: "🇧🇸", country: "Bahamas" },
    { code: "🇧🇭", country: "Bahrain" },
    { code: "🇧🇩", country: "Bangladesh" },
    { code: "🇧🇧", country: "Barbados" },
    { code: "🇧🇾", country: "Belarus" },
    { code: "🇧🇪", country: "Belgium" },
    { code: "🇧🇿", country: "Belize" },
    { code: "🇧🇯", country: "Benin" },
    { code: "🇧🇹", country: "Bhutan" },
    { code: "🇧🇴", country: "Bolivia" },
    { code: "🇧🇦", country: "Bosnia and Herzegovina" },
    { code: "🇧🇼", country: "Botswana" },
    { code: "🇧🇷", country: "Brazil" },
    { code: "🇧🇳", country: "Brunei" },
    { code: "🇧🇬", country: "Bulgaria" },
    { code: "🇧🇫", country: "Burkina Faso" },
    { code: "🇧🇮", country: "Burundi" },
    { code: "🇰🇭", country: "Cambodia" },
    { code: "🇨🇲", country: "Cameroon" },
    { code: "🇨🇦", country: "Canada" },
    { code: "🇨🇻", country: "Cape Verde" },
    { code: "🇨🇫", country: "Central African Republic" },
    { code: "🇹🇩", country: "Chad" },
    { code: "🇨🇱", country: "Chile" },
    { code: "🇨🇳", country: "China" },
    { code: "🇨🇴", country: "Colombia" },
    { code: "🇰🇲", country: "Comoros" },
    { code: "🇨🇩", country: "Congo (DRC)" },
    { code: "🇨🇬", country: "Congo (Republic)" },
    { code: "🇨🇷", country: "Costa Rica" },
    { code: "🇭🇷", country: "Croatia" },
    { code: "🇨🇺", country: "Cuba" },
    { code: "🇨🇾", country: "Cyprus" },
    { code: "🇨🇿", country: "Czech Republic" },
    { code: "🇩🇰", country: "Denmark" },
    { code: "🇩🇯", country: "Djibouti" },
    { code: "🇩🇲", country: "Dominica" },
    { code: "🇩🇴", country: "Dominican Republic" },
    { code: "🇪🇨", country: "Ecuador" },
    { code: "🇪🇬", country: "Egypt" },
    { code: "🇸🇻", country: "El Salvador" },
    { code: "🇬🇶", country: "Equatorial Guinea" },
    { code: "🇪🇷", country: "Eritrea" },
    { code: "🇪🇪", country: "Estonia" },
    { code: "🇸🇿", country: "Eswatini" },
    { code: "🇪🇹", country: "Ethiopia" },
    { code: "🇫🇯", country: "Fiji" },
    { code: "🇫🇮", country: "Finland" },
    { code: "🇫🇷", country: "France" },
    { code: "🇬🇦", country: "Gabon" },
    { code: "🇬🇲", country: "Gambia" },
    { code: "🇬🇪", country: "Georgia" },
    { code: "🇩🇪", country: "Germany" },
    { code: "🇬🇭", country: "Ghana" },
    { code: "🇬🇷", country: "Greece" },
    { code: "🇬🇩", country: "Grenada" },
    { code: "🇬🇹", country: "Guatemala" },
    { code: "🇬🇳", country: "Guinea" },
    { code: "🇬🇼", country: "Guinea-Bissau" },
    { code: "🇬🇾", country: "Guyana" },
    { code: "🇭🇹", country: "Haiti" },
    { code: "🇭🇳", country: "Honduras" },
    { code: "🇭🇺", country: "Hungary" },
    { code: "🇮🇸", country: "Iceland" },
    { code: "🇮🇳", country: "India" },
    { code: "🇮🇩", country: "Indonesia" },
    { code: "🇮🇷", country: "Iran" },
    { code: "🇮🇶", country: "Iraq" },
    { code: "🇮🇪", country: "Ireland" },
    { code: "🇮🇱", country: "Israel" },
    { code: "🇮🇹", country: "Italy" },
    { code: "🇯🇲", country: "Jamaica" },
    { code: "🇯🇵", country: "Japan" },
    { code: "🇯🇴", country: "Jordan" },
    { code: "🇰🇿", country: "Kazakhstan" },
    { code: "🇰🇪", country: "Kenya" },
    { code: "🇰🇮", country: "Kiribati" },
    { code: "🇰🇼", country: "Kuwait" },
    { code: "🇰🇬", country: "Kyrgyzstan" },
    { code: "🇱🇦", country: "Laos" },
    { code: "🇱🇻", country: "Latvia" },
    { code: "🇱🇧", country: "Lebanon" },
    { code: "🇱🇸", country: "Lesotho" },
    { code: "🇱🇷", country: "Liberia" },
    { code: "🇱🇾", country: "Libya" },
    { code: "🇱🇮", country: "Liechtenstein" },
    { code: "🇱🇹", country: "Lithuania" },
    { code: "🇱🇺", country: "Luxembourg" },
    { code: "🇲🇬", country: "Madagascar" },
    { code: "🇲🇼", country: "Malawi" },
    { code: "🇲🇾", country: "Malaysia" },
    { code: "🇲🇻", country: "Maldives" },
    { code: "🇲🇱", country: "Mali" },
    { code: "🇲🇹", country: "Malta" },
    { code: "🇲🇭", country: "Marshall Islands" },
    { code: "🇲🇷", country: "Mauritania" },
    { code: "🇲🇺", country: "Mauritius" },
    { code: "🇲🇽", country: "Mexico" },
    { code: "🇫🇲", country: "Micronesia" },
    { code: "🇲🇩", country: "Moldova" },
    { code: "🇲🇨", country: "Monaco" },
    { code: "🇲🇳", country: "Mongolia" },
    { code: "🇲🇪", country: "Montenegro" },
    { code: "🇲🇦", country: "Morocco" },
    { code: "🇲🇿", country: "Mozambique" },
    { code: "🇲🇲", country: "Myanmar" },
    { code: "🇳🇦", country: "Namibia" },
    { code: "🇳🇷", country: "Nauru" },
    { code: "🇳🇵", country: "Nepal" },
    { code: "🇳🇱", country: "Netherlands" },
    { code: "🇳🇿", country: "New Zealand" },
    { code: "🇳🇮", country: "Nicaragua" },
    { code: "🇳🇪", country: "Niger" },
    { code: "🇳🇬", country: "Nigeria" },
    { code: "🇰🇵", country: "North Korea" },
    { code: "🇲🇰", country: "North Macedonia" },
    { code: "🇳🇴", country: "Norway" },
    { code: "🇴🇲", country: "Oman" },
    { code: "🇵🇰", country: "Pakistan" },
    { code: "🇵🇼", country: "Palau" },
    { code: "🇵🇦", country: "Panama" },
    { code: "🇵🇬", country: "Papua New Guinea" },
    { code: "🇵🇾", country: "Paraguay" },
    { code: "🇵🇪", country: "Peru" },
    { code: "🇵🇭", country: "Philippines" },
    { code: "🇵🇱", country: "Poland" },
    { code: "🇵🇹", country: "Portugal" },
    { code: "🇶🇦", country: "Qatar" },
    { code: "🇷🇴", country: "Romania" },
    { code: "🇷🇺", country: "Russia" },
    { code: "🇷🇼", country: "Rwanda" },
    { code: "🇰🇳", country: "Saint Kitts and Nevis" },
    { code: "🇱🇨", country: "Saint Lucia" },
    { code: "🇻🇨", country: "Saint Vincent and the Grenadines" },
    { code: "🇼🇸", country: "Samoa" },
    { code: "🇸🇲", country: "San Marino" },
    { code: "🇸🇹", country: "São Tomé and Príncipe" },
    { code: "🇸🇦", country: "Saudi Arabia" },
    { code: "🇸🇳", country: "Senegal" },
    { code: "🇷🇸", country: "Serbia" },
    { code: "🇸🇨", country: "Seychelles" },
    { code: "🇸🇱", country: "Sierra Leone" },
    { code: "🇸🇬", country: "Singapore" },
    { code: "🇸🇰", country: "Slovakia" },
    { code: "🇸🇮", country: "Slovenia" },
    { code: "🇸🇧", country: "Solomon Islands" },
    { code: "🇸🇴", country: "Somalia" },
    { code: "🇿🇦", country: "South Africa" },
    { code: "🇰🇷", country: "South Korea" },
    { code: "🇸🇸", country: "South Sudan" },
    { code: "🇪🇸", country: "Spain" },
    { code: "🇱🇰", country: "Sri Lanka" },
    { code: "🇸🇩", country: "Sudan" },
    { code: "🇸🇷", country: "Suriname" },
    { code: "🇸🇪", country: "Sweden" },
    { code: "🇨🇭", country: "Switzerland" },
    { code: "🇸🇾", country: "Syria" },
    { code: "🇹🇼", country: "Taiwan" },
    { code: "🇹🇯", country: "Tajikistan" },
    { code: "🇹🇿", country: "Tanzania" },
    { code: "🇹🇭", country: "Thailand" },
    { code: "🇹🇱", country: "Timor-Leste" },
    { code: "🇹🇬", country: "Togo" },
    { code: "🇹🇴", country: "Tonga" },
    { code: "🇹🇹", country: "Trinidad and Tobago" },
    { code: "🇹🇳", country: "Tunisia" },
    { code: "🇹🇷", country: "Turkey" },
    { code: "🇹🇲", country: "Turkmenistan" },
    { code: "🇹🇻", country: "Tuvalu" },
    { code: "🇺🇬", country: "Uganda" },
    { code: "🇺🇦", country: "Ukraine" },
    { code: "🇦🇪", country: "UAE" },
    { code: "🇬🇧", country: "UK" },
    { code: "🇺🇸", country: "USA" },
    { code: "🇺🇾", country: "Uruguay" },
    { code: "🇺🇿", country: "Uzbekistan" },
    { code: "🇻🇺", country: "Vanuatu" },
    { code: "🇻🇦", country: "Vatican City" },
    { code: "🇻🇪", country: "Venezuela" },
    { code: "🇻🇳", country: "Vietnam" },
    { code: "🇾🇪", country: "Yemen" },
    { code: "🇿🇲", country: "Zambia" },
    { code: "🇿🇼", country: "Zimbabwe" },
    // Additional territories / non-UN members
    { code: "🌍", country: "Kosovo" },
    { code: "🌍", country: "Palestine" },
    { code: "🌍", country: "Western Sahara" },
  ].sort((a, b) => a.country.localeCompare(b.country));

  const colorOptions = [
    { value: "#1A56DB", label: "Blue" },
    { value: "#3B82F6", label: "Light Blue" },
    { value: "#8B5CF6", label: "Purple" },
    { value: "#10B981", label: "Green" },
    { value: "#F59E0B", label: "Gold" },
    { value: "#EF4444", label: "Red" },
    { value: "#EC4899", label: "Pink" },
    { value: "#06B6D4", label: "Cyan" },
  ];

  const activeMembers = members
    .filter((m) => m.isActive === true)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const inactiveMembers = members.filter((m) => m.isActive === false);

  if (loading && members.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-cyan-500 border-t-transparent" />
          <p className="mt-2 text-gray-500">Loading committee members...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: T.White }}>
            👥 Committee Management
          </h3>
          <p className="text-sm mt-1" style={{ color: T.white }}>
            Manage organizing committee members (Plenary, Keynote, etc.)
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (!showForm) resetForm();
            setEditing(null);
          }}
          className="px-4 py-2 rounded-lg flex items-center gap-2 transition"
          style={{ background: T.gradPrimary, color: T.white }}
        >
          {showForm ? <RiCloseLine size={18} /> : <RiAddLine size={18} />}
          {showForm ? "Close Form" : "Add Committee Member"}
        </button>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-xl p-6 overflow-hidden"
            style={{
              background: T.white,
              border: `1px solid ${T.borderLight}`,
            }}
          >
            <h4 className="font-semibold mb-4" style={{ color: T.navy }}>
              {editing ? "Edit Committee Member" : "Add New Committee Member"}
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Prof. John Doe"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Institution *
                  </label>
                  <input
                    type="text"
                    value={formData.institution}
                    onChange={(e) =>
                      setFormData({ ...formData, institution: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Harvard University, USA"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Field of Expertise *
                  </label>
                  <input
                    type="text"
                    value={formData.field}
                    onChange={(e) =>
                      setFormData({ ...formData, field: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Immunology & Nanomedicine"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Bio *
                  </label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) =>
                      setFormData({ ...formData, bio: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    rows="4"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="Expert in Immunology with extensive research..."
                    maxLength="500"
                  />
                  <p className="text-xs mt-1" style={{ color: T.textMuted }}>
                    {formData.bio.length}/500 characters
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Initials *
                  </label>
                  <input
                    type="text"
                    value={formData.initials}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        initials: e.target.value.toUpperCase().slice(0, 2),
                      })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="JD"
                    maxLength="2"
                  />
                </div>
                {/* ─── COUNTRY FIELD ─── */}
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Country *
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      list="country-list"
                      value={formData.country}
                      onChange={(e) => {
                        const val = e.target.value;
                        const matched = countryCodes.find(
                          (c) => c.country.toLowerCase() === val.toLowerCase(),
                        );
                        if (matched) {
                          setFormData({
                            ...formData,
                            country: matched.country,
                            countryCode: matched.code,
                          });
                        } else {
                          setFormData({ ...formData, country: val });
                        }
                      }}
                      className="flex-1 rounded-lg p-3"
                      style={{
                        background: T.offWhite,
                        border: `1px solid ${T.border}`,
                        color: T.text,
                      }}
                      placeholder="Type country name…"
                    />
                    <datalist id="country-list">
                      {countryCodes.map((c) => (
                        <option key={c.code} value={c.country} />
                      ))}
                    </datalist>
                    <select
                      value={formData.countryCode}
                      onChange={(e) => {
                        const selected = countryCodes.find(
                          (c) => c.code === e.target.value,
                        );
                        setFormData({
                          ...formData,
                          countryCode: e.target.value,
                          country: selected
                            ? selected.country
                            : formData.country,
                        });
                      }}
                      className="w-24 rounded-lg p-3"
                      style={{
                        background: T.offWhite,
                        border: `1px solid ${T.border}`,
                        color: T.text,
                        fontSize: 24,
                      }}
                    >
                      {countryCodes.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* ─── END COUNTRY FIELD ─── */}
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Accent Color
                  </label>
                  <div className="flex gap-3 items-center">
                    <select
                      value={formData.color}
                      onChange={(e) =>
                        setFormData({ ...formData, color: e.target.value })
                      }
                      className="flex-1 rounded-lg p-3"
                      style={{
                        background: T.offWhite,
                        border: `1px solid ${T.border}`,
                        color: T.text,
                      }}
                    >
                      {colorOptions.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) =>
                        setFormData({ ...formData, color: e.target.value })
                      }
                      className="w-12 h-12 rounded-lg cursor-pointer"
                      style={{ border: `1px solid ${T.border}` }}
                    />
                  </div>
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    LinkedIn URL
                  </label>
                  <input
                    type="url"
                    value={formData.linkedin}
                    onChange={(e) =>
                      setFormData({ ...formData, linkedin: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-6">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.isChair}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isChair: e.target.checked,
                      isCoChair: e.target.checked ? false : formData.isCoChair,
                    })
                  }
                  className="w-5 h-5 accent-purple-500"
                />
                <span style={{ color: T.textMid }}>👑 Chair</span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.isCoChair}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isCoChair: e.target.checked,
                      isChair: e.target.checked ? false : formData.isChair,
                    })
                  }
                  className="w-5 h-5 accent-blue-500"
                />
                <span style={{ color: T.textMid }}>🎯 Co-Chair</span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="w-5 h-5 accent-green-500"
                />
                <span style={{ color: T.textMid }}>
                  Active (visible on website)
                </span>
              </label>
            </div>
            <div
              className="flex gap-3 mt-6 pt-4"
              style={{ borderTop: `1px solid ${T.border}` }}
            >
              <button
                onClick={saveMember}
                disabled={loading}
                className="px-6 py-2 rounded-lg flex items-center gap-2"
                style={{ background: T.gradPrimary, color: T.white }}
              >
                <RiSaveLine size={18} />
                {loading
                  ? "Saving..."
                  : editing
                    ? "Update Member"
                    : "Add Member"}
              </button>
              {editing && (
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditing(null);
                    resetForm();
                  }}
                  className="px-6 py-2 rounded-lg"
                  style={{ background: "#E5E7EB", color: T.textMid }}
                >
                  Cancel
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Members Section */}
      <div
        className="rounded-xl p-6"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: T.navy }}>
          ✅ Active Committee Members ({activeMembers.length})
        </h3>
        <div className="space-y-3">
          {activeMembers.length > 0 ? (
            activeMembers.map((member) => (
              <div
                key={member._id}
                className="flex items-center gap-4 p-3 rounded-lg"
                style={{ background: T.offWhite }}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ background: member.color }}
                >
                  {member.initials}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold" style={{ color: T.text }}>
                      {member.name}
                    </h4>
                    <span className="text-lg">{member.countryCode}</span>
                    {member.isChair && (
                      <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">
                        👑 Chair
                      </span>
                    )}
                    {member.isCoChair && (
                      <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">
                        🎯 Co-Chair
                      </span>
                    )}
                    <span className="text-xs" style={{ color: T.textMuted }}>
                      Order: {member.order || 0}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: T.textMuted }}>
                    {member.institution}
                  </p>
                  <p className="text-xs" style={{ color: T.primary }}>
                    {member.field}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(member)}
                    className="p-2 rounded"
                    style={{
                      color: T.accentGold,
                      background: `${T.accentGold}20`,
                    }}
                    title="Deactivate"
                  >
                    <RiEyeOffLine size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(member);
                      setFormData(member);
                      setShowForm(true);
                    }}
                    className="p-2 rounded"
                    style={{ color: T.primary, background: `${T.primary}20` }}
                    title="Edit"
                  >
                    <RiEditLine size={16} />
                  </button>
                  <button
                    onClick={() => deleteMember(member._id)}
                    className="p-2 rounded"
                    style={{ color: "#EF4444", background: "#FEE2E2" }}
                    title="Delete"
                  >
                    <RiDeleteBinLine size={16} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center py-4" style={{ color: T.textMuted }}>
              No active committee members. Add members above and activate them.
            </p>
          )}
        </div>
      </div>

      {/* Inactive Members Section */}
      {inactiveMembers.length > 0 && (
        <div
          className="rounded-xl p-6 opacity-60"
          style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: T.navy }}>
            🚫 Inactive Members ({inactiveMembers.length})
          </h3>
          <div className="space-y-3">
            {inactiveMembers.map((member) => (
              <div
                key={member._id}
                className="flex items-center gap-4 p-3 rounded-lg opacity-50"
                style={{ background: T.offWhite }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: member.color, opacity: 0.5 }}
                >
                  <span className="text-white font-bold text-sm">
                    {member.initials}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold" style={{ color: T.text }}>
                    {member.name}
                  </p>
                  <p className="text-sm" style={{ color: T.textMuted }}>
                    {member.institution}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(member)}
                    className="p-2 rounded"
                    style={{ color: "#4CAF50", background: "#E8F5E9" }}
                    title="Activate"
                  >
                    <RiEyeLine size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(member);
                      setFormData(member);
                      setShowForm(true);
                    }}
                    className="p-2 rounded"
                    style={{ color: T.primary, background: `${T.primary}20` }}
                    title="Edit"
                  >
                    <RiEditLine size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Seed Default Button */}
      <div className="flex gap-3">
        <button
          onClick={seedDefaultMembers}
          disabled={loading}
          className="px-4 py-2 rounded-lg flex items-center gap-2"
          style={{ background: "#4CAF50", color: T.white }}
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
          ) : (
            <RiFileTextLine size={18} />
          )}
          Load Default Committee
        </button>
      </div>
    </motion.div>
  );
};


const TopicsOfInterestTab = ({ token, eventId }) => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    order: 0,
    isActive: true,
  });

  useEffect(() => {
    if (eventId) fetchTopics();
  }, [eventId]);

  const fetchTopics = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/topics-of-interest?eventId=${eventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (data.success) setTopics(data.data);
      else setTopics([]);
    } catch (error) {
      console.error("Error fetching topics:", error);
      toast.error("Failed to load topics");
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      order: 0,
      isActive: true,
    });
    setEditing(null);
  };

  const saveTopic = async () => {
    if (!formData.name.trim()) {
      toast.error("Topic name is required");
      return;
    }
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { ...formData, eventId };
      if (editing) {
        await axios.put(
          `${API_BASE}/api/topics-of-interest/${editing._id}`,
          payload,
          config
        );
        toast.success("Topic updated successfully");
      } else {
        await axios.post(
          `${API_BASE}/api/topics-of-interest`,
          payload,
          config
        );
        toast.success("Topic created successfully");
      }
      resetForm();
      setShowForm(false);
      await fetchTopics();
    } catch (error) {
      console.error("Error saving topic:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteTopic = async (id) => {
    if (!window.confirm("Delete this topic?")) return;
    try {
      await axios.delete(`${API_BASE}/api/topics-of-interest/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Topic deleted");
      await fetchTopics();
    } catch (error) {
      console.error("Error deleting topic:", error);
      toast.error("Failed to delete");
    }
  };

  const toggleActive = async (topic) => {
    try {
      await axios.patch(
        `${API_BASE}/api/topics-of-interest/${topic._id}/toggle-active`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Topic ${topic.isActive ? "deactivated" : "activated"}`);
      await fetchTopics();
    } catch (error) {
      console.error("Error toggling active:", error);
      toast.error("Failed to update status");
    }
  };

  const seedDefaultTopics = async () => {
    if (!eventId) {
      toast.error("No event ID available");
      return;
    }
    if (!window.confirm("This will replace all existing topics. Continue?"))
      return;
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/topics-of-interest/seed/event/${eventId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Default topics seeded successfully!");
      await fetchTopics();
    } catch (error) {
      console.error("Error seeding topics:", error);
      toast.error(error.response?.data?.message || "Failed to seed topics");
    } finally {
      setLoading(false);
    }
  };

  const activeTopics = topics
    .filter((t) => t.isActive === true)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const inactiveTopics = topics.filter((t) => t.isActive === false);

  if (loading && topics.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-cyan-500 border-t-transparent" />
          <p className="mt-2 text-gray-500">Loading topics...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: T.White }}>
            📋 Topics of Interest
          </h3>
          <p className="text-sm mt-1" style={{ color: T.white }}>
            Manage topics attendees can select during registration
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={fetchTopics}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition"
            style={{ background: "#6B7280", color: T.white }}
          >
            <RiRefreshLine size={18} /> Refresh
          </button>
          <button
            onClick={seedDefaultTopics}
            disabled={loading}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition"
            style={{ background: "#4CAF50", color: T.white }}
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <RiFileTextLine size={18} />
            )}
            Seed Default Topics
          </button>
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (!showForm) resetForm();
              setEditing(null);
            }}
            className="px-4 py-2 rounded-lg flex items-center gap-2 transition"
            style={{ background: T.gradPrimary, color: T.white }}
          >
            {showForm ? <RiCloseLine size={18} /> : <RiAddLine size={18} />}
            {showForm ? "Close Form" : "Add New Topic"}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-xl p-6 overflow-hidden"
            style={{
              background: T.white,
              border: `1px solid ${T.borderLight}`,
            }}
          >
            <h4 className="font-semibold mb-4" style={{ color: T.navy }}>
              {editing ? "Edit Topic" : "Add New Topic"}
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                    placeholder="e.g., Artificial Intelligence"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-sm"
                    style={{ color: T.textMid }}
                  >
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full rounded-lg p-3"
                    style={{
                      background: T.offWhite,
                      border: `1px solid ${T.border}`,
                      color: T.text,
                    }}
                  />
                </div>
                <div>
                  <label className="flex items-center gap-3 mt-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                      className="w-5 h-5 accent-green-500"
                    />
                    <span style={{ color: T.textMid }}>
                      Active (visible to attendees)
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div
              className="flex gap-3 mt-6 pt-4"
              style={{ borderTop: `1px solid ${T.border}` }}
            >
              <button
                onClick={saveTopic}
                disabled={loading}
                className="px-6 py-2 rounded-lg flex items-center gap-2"
                style={{ background: T.gradPrimary, color: T.white }}
              >
                <RiSaveLine size={18} />
                {loading ? "Saving..." : editing ? "Update Topic" : "Add Topic"}
              </button>
              {editing && (
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditing(null);
                    resetForm();
                  }}
                  className="px-6 py-2 rounded-lg"
                  style={{ background: "#E5E7EB", color: T.textMid }}
                >
                  Cancel
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Topics */}
      <div
        className="rounded-xl p-6"
        style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: T.navy }}>
          ✅ Active Topics ({activeTopics.length})
        </h3>
        <div className="space-y-3">
          {activeTopics.length > 0 ? (
            activeTopics.map((topic) => (
              <div
                key={topic._id}
                className="flex items-center gap-4 p-3 rounded-lg"
                style={{ background: T.offWhite }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ background: T.primary }}
                >
                  <RiHashtag size={18} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold" style={{ color: T.text }}>
                      {topic.name}
                    </h4>
                    <span className="text-xs" style={{ color: T.textMuted }}>
                      Order: {topic.order || 0}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(topic)}
                    className="p-2 rounded"
                    style={{
                      color: T.accentGold,
                      background: `${T.accentGold}20`,
                    }}
                    title="Deactivate"
                  >
                    <RiEyeOffLine size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(topic);
                      setFormData(topic);
                      setShowForm(true);
                    }}
                    className="p-2 rounded"
                    style={{ color: T.primary, background: `${T.primary}20` }}
                    title="Edit"
                  >
                    <RiEditLine size={16} />
                  </button>
                  <button
                    onClick={() => deleteTopic(topic._id)}
                    className="p-2 rounded"
                    style={{ color: "#EF4444", background: "#FEE2E2" }}
                    title="Delete"
                  >
                    <RiDeleteBinLine size={16} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center py-4" style={{ color: T.textMuted }}>
              No active topics. Add topics above and activate them.
            </p>
          )}
        </div>
      </div>

      {/* Inactive Topics */}
      {inactiveTopics.length > 0 && (
        <div
          className="rounded-xl p-6 opacity-60"
          style={{ background: T.white, border: `1px solid ${T.borderLight}` }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: T.navy }}>
            🚫 Inactive Topics ({inactiveTopics.length})
          </h3>
          <div className="space-y-3">
            {inactiveTopics.map((topic) => (
              <div
                key={topic._id}
                className="flex items-center gap-4 p-3 rounded-lg opacity-50"
                style={{ background: T.offWhite }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: T.border, opacity: 0.5 }}
                >
                  <RiHashtag size={18} style={{ color: T.textMuted }} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold" style={{ color: T.text }}>
                    {topic.name}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(topic)}
                    className="p-2 rounded"
                    style={{ color: "#4CAF50", background: "#E8F5E9" }}
                    title="Activate"
                  >
                    <RiEyeLine size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(topic);
                      setFormData(topic);
                      setShowForm(true);
                    }}
                    className="p-2 rounded"
                    style={{ color: T.primary, background: `${T.primary}20` }}
                    title="Edit"
                  >
                    <RiEditLine size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};
/* ══════════════════════════════════════════════════════════════════════
   MAIN SUMMIT MANAGEMENT COMPONENT
   ══════════════════════════════════════════════════════════════════════ */
const SummitManagement = () => {
  const [activeTab, setActiveTab] = useState("hero-slides");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const token = localStorage.getItem("token");
  const [searchParams] = useSearchParams();
  const eventIdFromUrl = searchParams.get("eventId");
  const TABS = [
    { id: "hero-slides", label: "🏔️ Hero Slides", icon: <RiImageLine /> },
    {
      id: "announcements",
      label: "📢 Announcements",
      icon: <RiMegaphoneLine />,
    },
    { id: "venue", label: "📍 Venue", icon: <RiMapPinLine /> },
    { id: "committee", label: "👥 Committee", icon: <RiTeamLine /> },
    { id: "pdf-schedule", label: "📄 PDF Schedule", icon: <RiFilePdfLine /> },
    {
      id: "programme",
      label: "📋 Programme Overview",
      icon: <RiFolderChartLine />,
    },
    {
  id: "topics-of-interest",
  label: "📋 Topics of Interest",
  icon: <RiHashtag />,
},
    { id: "agenda", label: "📅 Agenda / Schedule", icon: <RiListCheck /> },
    { id: "topics", label: "📚 Manage Topics", icon: <RiBookOpenLine /> },
    {
      id: "summit-topics",
      label: "📚 Summit Topics",
      icon: <RiBookOpenLine />,
    },
    { id: "accommodations", label: "🏨 Accommodations", icon: <RiHotelLine /> },
    { id: "sponsors", label: "🏆 Sponsors", icon: <RiAwardLine /> },
    {
      id: "presentation-guidelines",
      label: "🎤 Presentation Guidelines",
      icon: <RiPresentationLine />,
    },
    {
      id: "submission-guidelines",
      label: "📝 Submission Guidelines",
      icon: <RiFileTextLine />,
    },
    {
      id: "speaker-pricing",
      label: "💰 Speaker Pricing",
      icon: <RiMoneyDollarCircleLine />,
    },
    { id: "speakers", label: "🎤 Summit Speakers", icon: <RiUserStarLine /> },
    { id: "about-us", label: "📖 About Us", icon: <RiFileTextLine /> },
    {
      id: "registration-fee",
      label: "💰 Registration Fee",
      icon: <RiMoneyDollarCircleLine />,
    },
    { id: "publications", label: "📚 Publications", icon: <RiBookOpenLine /> },
    { id: "brochure", label: "📄 Brochure", icon: <RiFilePdfLine /> },
  ];

  // Read eventId from URL query param
  // Auto-select event from URL when component loads
  useEffect(() => {
    if (eventIdFromUrl && !selectedEvent) {
      fetchEventById(eventIdFromUrl);
    }
  }, [eventIdFromUrl]);

  const fetchEventById = async (eventId) => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/events/${eventId}`, {
        headers: authHeaders(token),
      });
      if (data.success && data.event) {
        setSelectedEvent(data.event);
      }
    } catch (error) {
      console.error("Error fetching event:", error);
      toast.error("Failed to load event");
    }
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    // Update URL to reflect selected event
    const url = new URL(window.location.href);
    url.searchParams.set("eventId", event._id);
    window.history.pushState({}, "", url);
  };

  const eventId = selectedEvent?._id || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Summit Management
              </h1>
              <p className="text-gray-400">
                Manage summit content for{" "}
                {selectedEvent ? selectedEvent.title : "selected event"}
              </p>
            </div>
            <EventSelector
              token={token}
              selectedEvent={selectedEvent}
              onSelect={handleEventSelect}
            />
          </div>
        </div>

        {!selectedEvent ? (
          <div
            className="rounded-xl p-8"
            style={{ background: `${T.navy}40`, backdropFilter: "blur(10px)" }}
          >
            <NoEventSelected />
          </div>
        ) : (
          <>
            <div className="flex space-x-3 mb-8 flex-wrap gap-y-2">
              {TABS.map(({ id, label, icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`px-5 py-2.5 rounded-lg text-sm capitalize transition-all flex items-center gap-2 ${activeTab === id ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "text-gray-400 hover:text-white border border-white/10"}`}
                >
                  {icon}
                  {label}
                </button>
              ))}
            </div>
            <div
              className="rounded-xl p-8"
              style={{
                background: `${T.navy}40`,
                backdropFilter: "blur(10px)",
              }}
            >
              {activeTab === "hero-slides" && (
                <SummitHeroSlidesTab token={token} eventId={eventId} />
              )}
              {activeTab === "announcements" && (
                <SubmitAnnouncementsTab token={token} eventId={eventId} />
              )}
              {activeTab === "committee" && (
                <CommitteeManagementTab token={token} eventId={eventId} />
              )}
              {activeTab === "programme" && (
                <ProgrammeOverviewTab token={token} eventId={eventId} />
              )}
              {activeTab === "agenda" && (
                <ScheduleTab token={token} eventId={eventId} />
              )}
              {activeTab === "about-us" && (
                <AboutUsTab token={token} eventId={eventId} />
              )}
              {activeTab === "submission-guidelines" && (
                <SubmissionGuidelinesTab token={token} eventId={eventId} />
              )}
              {activeTab === "topics-of-interest" && (
  <TopicsOfInterestTab token={token} eventId={eventId} />
)}
              {activeTab === "venue" && (
                <VenueManagementTab token={token} eventId={eventId} />
              )}
              {activeTab === "topics" && (
                <TopicsTab token={token} eventId={eventId} />
              )}
              {activeTab === "summit-topics" && (
                <SummitTopicsTab token={token} eventId={eventId} />
              )}
              {activeTab === "accommodations" && (
                <AccommodationsTab token={token} eventId={eventId} />
              )}
              {activeTab === "sponsors" && (
                <SponsorsTab token={token} eventId={eventId} />
              )}
              {activeTab === "brochure" && (
                <BrochureTab token={token} eventId={eventId} />
              )}
              {activeTab === "pdf-schedule" && (
                <PdfScheduleTab token={token} eventId={eventId} />
              )}
              {activeTab === "registration-fee" && (
                <RegistrationFeeTab token={token} eventId={eventId} />
              )}
              {activeTab === "presentation-guidelines" && (
                <PresentationGuidelinesTab token={token} eventId={eventId} />
              )}
              {activeTab === "speaker-pricing" && (
                <SpeakerPricingTab token={token} eventId={eventId} />
              )}
              {activeTab === "publications" && (
                <SummitPublicationsTab token={token} eventId={eventId} />
              )}
              {activeTab === "speakers" && (
                <SummitSpeakersTab token={token} eventId={eventId} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SummitManagement;
